import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { keepPreviousData, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useNavigate, useSearchParams } from 'react-router-dom';
import {
  AlertTriangle,
  Check,
  CheckCircle2,
  ChevronDown,
  Download,
  Eye,
  Loader2,
  Lock,
  Pencil,
  Receipt,
  RefreshCw,
  Search,
  ShieldCheck,
  Sparkles,
  Stamp,
  Trash2,
  Upload,
  X,
} from 'lucide-react';
import { toast } from 'sonner';
import { useIsReadOnly, useSession } from '@/shared/auth/SessionProvider';
import { Button } from '@/shared/components/ui/button';
import {
  Dialog,
  DialogBody,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/shared/components/ui/dialog';
import { Input } from '@/shared/components/ui/input';
import { Label } from '@/shared/components/ui/label';
import {
  Select,
  SelectContent,
  SelectGroup,
  SelectItem,
  SelectLabel,
  SelectTrigger,
  SelectValue,
} from '@/shared/components/ui/select';
import { Skeleton } from '@/shared/components/ui/skeleton';
import { Switch } from '@/shared/components/ui/switch';
import { cn } from '@/shared/lib/cn';
import { currencyFromSp, formatMoney, parseMoney } from '@/shared/lib/money';
import {
  confirmarCobros,
  confirmarCobroNova,
  confirmarCobrarFornitalia,
  confirmarPagarNova,
  facturarComprobante,
  solicitarCaeArca,
  type SolicitarCaeResponse,
  type CaeResumen,
  listCobrosAConfirmar,
  listCobrosNova,
  listCuentasPorCobrar,
  listCuentasPorCobrarFornitalia,
  listCuentasPorPagarNova,
  listFacturacionPendiente,
  type CobroNovaItem,
  type ComprobanteAdminItem,
  type FacturacionType,
  type FacturarStage,
  type InterCoMovementItem,
  type NovaFiscalData,
  type NovaPagadorCaja,
} from './api';
import { downloadFacturaArcaPdf, buildFacturaArcaPdfFile } from './pdf/facturaArca';
import { CaeFacturaPreview } from './components/CaeFacturaPreview';
import { ResumenPromesas } from './components/ResumenPromesas';
import {
  columnaPromesa,
  ddmmyyyyAIso,
  etiquetaColumna,
  ordenarColumnas,
  resumenMedioMes,
} from './lib/promesas';
import { hoyAr } from '@/features/facturacion/plazoNc';
import { FEX_DESVIO_MAX_PCT_DEFAULT, listActivePuntosVenta } from '@/shared/api/controllers/puntosVenta';
import { INCOTERMS_QUERY_KEY, listIncoterms } from '@/shared/api/controllers/catalogos';
import { USOS_CFDI } from '@/features/contactos/constants';
import { getCajaSaldo } from '@/features/tesoreria/api';
import { useActiveCountry } from '@/shared/countries/ActiveCountryProvider';
import { listCompras, type CompraListItem } from '@/features/compras/api';
import { saldoRestante, totalesRestantePorMoneda } from '@/features/compras/lib/saldo';
import { FileLink } from '@/shared/components/file-upload/FileLink';
import { extractInvoice, uploadDocument } from '@/shared/api/documents';
import { MoneyInput } from '@/features/ventas/components/MoneyInput';
import { CajaNombre } from '@/features/tesoreria/components/CajaNombre';
import { etiquetaCaja } from '@/shared/lib/cajasEfectivo';
import { esCajaUsd, useEsCajaUsd } from '@/shared/lib/cajaMoneda';
import { getUsadoContext, getFacturaInternaBreakdown } from '@/shared/api/controllers/ventas';
import { useRealtimeInvalidate } from '@/shared/realtime/useRealtimeInvalidate';
import { EcheqsBandeja } from './components/EcheqsBandeja';

type Mode =
  | 'cobros'
  | 'facturacion'
  | 'cobrar'
  | 'pagar'
  | 'cobros_nova'
  | 'cobrar_nova'
  | 'pagar_nova'
  | 'facturacion_nova'
  | 'echeqs';

function isMode(v: string | null | undefined): v is Mode {
  return (
    v === 'cobros' ||
    v === 'facturacion' ||
    v === 'cobrar' ||
    v === 'pagar' ||
    v === 'cobros_nova' ||
    v === 'cobrar_nova' ||
    v === 'pagar_nova' ||
    v === 'facturacion_nova' ||
    v === 'echeqs'
  );
}

/**
 * Rótulo de la píldora de moneda local en el footer. En los tabs de Pagar el
 * número es el saldo pendiente, no el total facturado: si el rótulo no lo
 * dice, el operador lee otra cosa.
 */
const PESOS_LABEL: Partial<Record<Mode, string>> = {
  cobros_nova: 'Total MXN',
  pagar: 'Restante Pesos',
  pagar_nova: 'Restante MXN',
};

const MODE_LABELS: Record<Mode, string> = {
  cobros: 'Cobros a Confirmar',
  facturacion: 'Facturación',
  // Fornitalia CxC: direct-customer promesas plus the inter-co Cobrar
  // queue (visible only when there are pending inter-co rows).
  cobrar: 'Cuentas por Cobrar',
  pagar: 'Cuentas por Pagar',
  // MX-only tabs. Visible only when the active country is MX. Each Nova
  // tab now mirrors a Fornitalia counterpart 1:1 — the operator never
  // needs to switch sections to find a Nova-side queue:
  //   - cobros_nova   → mirrors `cobros`   (anticipos pending Nova confirm)
  //   - cobrar_nova   → mirrors `cobrar`   (Nova-channel promesas)
  //   - facturacion_nova → mirrors `facturacion`
  //   - pagar_nova    → mirrors `pagar`    (inter-co Pagar Nova queue)
  cobros_nova: 'Cobros a Confirmar - Nova',
  cobrar_nova: 'Cuentas por Cobrar - Nova',
  pagar_nova: 'Cuentas por Pagar - Nova',
  facturacion_nova: 'Facturación - Nova',
  echeqs: 'E-cheqs',
};

type DialogState =
  | { kind: 'closed' }
  | { kind: 'view'; row: ComprobanteAdminItem }
  | { kind: 'view-cobro-nova'; row: CobroNovaItem }
  | { kind: 'view-interco'; row: InterCoMovementItem }
  | { kind: 'confirm-cobros'; rows: ComprobanteAdminItem[] }
  | { kind: 'confirm-cobro-nova'; row: CobroNovaItem }
  | { kind: 'confirm-cobros-nova-bulk'; rows: CobroNovaItem[] }
  | { kind: 'confirm-cobrar-fornitalia'; row: InterCoMovementItem }
  | { kind: 'confirm-pagar-nova'; row: InterCoMovementItem }
  | { kind: 'facturar'; row: ComprobanteAdminItem }
  /** AR: confirmación previa cuando se factura un Pago Final con un anticipo
   *  de la misma venta todavía pendiente de facturar. */
  | { kind: 'facturar-warn-anticipo'; row: ComprobanteAdminItem };

/**
 * Administración — admin/administración sales-side workflow dashboard.
 * Two active modes: Cobros a Confirmar and Facturación. Cuentas por
 * Cobrar / por Pagar are placeholders until the Compras port lands.
 *
 * Shell mirrors Aprobaciones: flex-col outer, sticky header table in an
 * inner scroller, multi-select checkbox column + bottom "Confirmar"
 * button in Cobros mode.
 */
export function AdministracionPage() {
  const qc = useQueryClient();
  const navigate = useNavigate();
  const { profile } = useSession();
  const { config: activeCountry } = useActiveCountry();
  // Re-render cuando llegan las cajas: computeTotals / inferRowCurrency usan esCajaUsd.
  const esUsd = useEsCajaUsd();
  const isMx = activeCountry.code === 'MX';
  // Nova role is an MX-only approver that can only touch the
  // cobros_nova / pagar_nova queues — defaults differ + tab Select is
  // pruned to those two below. Backend enforces the same allowlist.
  const isNovaRole = profile?.perfil === 'nova';
  // Tab lives in `?tab=` so the back button from a venta detail can
  // restore the originating tab (QA #14: clicking Atrás from a row
  // accessed via Facturación was landing on Cobros).
  const [searchParams, setSearchParams] = useSearchParams();
  const rawTabParam = searchParams.get('tab');
  // Backward-compat: keep legacy deep-links landing on the Fornitalia
  // CxC surface where inter-co Cobrar rows are now handled.
  const tabParam = rawTabParam === 'cobrar_fornitalia' ? 'cobrar' : rawTabParam;
  const defaultMode: Mode = isNovaRole ? 'cobros_nova' : 'cobros';
  const NOVA_ROLE_MODES = new Set<Mode>([
    'cobros_nova',
    'facturacion_nova',
    'cobrar_nova',
    'pagar_nova',
  ]);
  const tabIsAllowed = (m: Mode) => !isNovaRole || NOVA_ROLE_MODES.has(m);
  const initialMode: Mode =
    isMode(tabParam) && tabIsAllowed(tabParam) ? tabParam : defaultMode;
  const [mode, setModeState] = useState<Mode>(initialMode);
  const setMode = (next: Mode) => {
    setModeState(next);
    setSearchParams(
      (prev) => {
        const out = new URLSearchParams(prev);
        if (next === 'cobros') out.delete('tab');
        else out.set('tab', next);
        return out;
      },
      { replace: true },
    );
  };
  // Sync state ← URL when the user navigates back into this page (the
  // search params change without remounting the component). For Nova
  // role we ignore tabs they can't open so a shared deep-link doesn't
  // strand them on a 403'd queue.
  useEffect(() => {
    if (isMode(tabParam) && tabIsAllowed(tabParam) && tabParam !== mode) {
      setModeState(tabParam);
    }
    if (!tabParam && mode !== defaultMode) setModeState(defaultMode);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tabParam]);
  const isReadOnly = useIsReadOnly();
  // Las acciones de las pestañas Nova (Confirmar cobro, Facturar,
  // Confirmar Pagar/Cobrar Inter-co, el Confirmar masivo de Cobros Nova)
  // estuvieron cerradas para `administracion` hasta el 2026-08-19: se
  // consideraban decisiones del rol Nova. El cliente pidió que
  // Administración ejecute el flujo Nova completo, así que el candado se
  // eliminó y sólo queda el read-only de sesión — que es lo que el
  // servidor ya permitía (`requireRole('admin','administracion','nova')`
  // en los dos confirmar de Nova).
  const effectiveReadOnly = isReadOnly;
  const [search, setSearch] = useState('');
  const [selected, setSelected] = useState<Set<string>>(new Set());
  // Nova-cobros queue uses its own selection set — IDs collide with the
  // Fornitalia cobros set conceptually (both are sharepoint ids of
  // ComprobantePagos rows) so we keep separate buckets to avoid
  // accidental cross-table leakage when the user switches tabs.
  const [selectedNova, setSelectedNova] = useState<Set<string>>(new Set());
  const [dialog, setDialog] = useState<DialogState>({ kind: 'closed' });
  // MX-only segmented filter for Facturación: Todas / Ventas / Internas.
  // Default 'todas' so MX users see both queues at once; AR users
  // never see the toggle (internas is empty on AR-scope queries).
  const [facturacionFilter, setFacturacionFilter] = useState<FacturacionType>('todas');

  // Auto-refresh every 15s while mounted + on tab refocus. Multi-admin
  // queues (cobros, facturación, CxC, CxP) move under each other — this
  // keeps each user's list close to the server's truth without requiring
  // manual refresh, and complements the server-side optimistic-concurrency
  // guards (PreconditionFailedError → 412 → `concurrent_modification`)
  // that prevent stale-data double-confirms. React Query pauses both the
  // interval and focus-refetch when the tab is backgrounded.
  const REFRESH_MS = 15_000;

  // `refetchOnMount: 'always'` is the safety net for the realtime
  // channel: if the user creates a pago elsewhere and immediately
  // navigates here, we ALWAYS refetch on mount even if the cache is
  // technically still inside its staleTime window. The realtime
  // invalidations + interval handle the in-page case; this covers
  // the cross-page race where the realtime event might arrive
  // before this query is subscribed.
  // Country code in every queryKey so AR/MX caches don't share slots.
  // `queryClient.clear()` on switch is the safety net; this is the
  // primary guarantee against stale cross-country reads.
  const countryCode = activeCountry.code;
  const cobrosQ = useQuery({
    queryKey: ['administracion', countryCode, 'cobros'],
    queryFn: listCobrosAConfirmar,
    staleTime: 30_000,
    refetchInterval: REFRESH_MS,
    refetchOnWindowFocus: true,
    refetchOnMount: 'always',
    enabled: mode === 'cobros',
  });
  // On MX we honour the segmented filter; on AR we always pass
  // 'ventas' so the server short-circuits the internas branch. The
  // 'nova' type is reserved for the dedicated 'facturacion_nova' mode
  // below — it's never sent from this query.
  const facturacionTypeForServer: FacturacionType = isMx ? facturacionFilter : 'ventas';
  const facturacionQ = useQuery({
    queryKey: ['administracion', countryCode, 'facturacion', facturacionTypeForServer],
    queryFn: () => listFacturacionPendiente(facturacionTypeForServer),
    staleTime: 30_000,
    refetchInterval: REFRESH_MS,
    refetchOnWindowFocus: true,
    refetchOnMount: 'always',
    enabled: mode === 'facturacion',
  });
  // MX-only — Facturación Nova queue. Operator-entered Nova-channel
  // cobros (Nova Banorte / Nova Mercado Pago / Herconstru) with factura
  // that have been confirmed by Nova (Anticipo confirmed → original
  // flipped to 'Confirmado'). Operator registers the customer-facing
  // N° de factura here via the same FacturarDialog as the regular flow.
  const facturacionNovaQ = useQuery({
    queryKey: ['administracion', countryCode, 'facturacion', 'nova'],
    queryFn: () => listFacturacionPendiente('nova'),
    staleTime: 30_000,
    refetchInterval: REFRESH_MS,
    refetchOnWindowFocus: true,
    refetchOnMount: 'always',
    enabled: mode === 'facturacion_nova' && isMx,
  });
  // Cuentas por Cobrar — Fornitalia tab. Promesas filtered to non-Nova
  // medios (Nova-channel promesas live under `cobrar_nova`).
  const cxcQ = useQuery({
    queryKey: ['administracion', countryCode, 'cuentas-por-cobrar', 'fornitalia'],
    queryFn: () => listCuentasPorCobrar('fornitalia'),
    staleTime: 30_000,
    refetchInterval: REFRESH_MS,
    refetchOnWindowFocus: true,
    refetchOnMount: 'always',
    enabled: mode === 'cobrar',
  });
  // Cuentas por Cobrar - Nova — Nova-channel customer promesas
  // (medio_pago is Nova Banorte / Nova Mercado Pago / Herconstru).
  // Server splits the queue via `channel=nova` query param. MX-only.
  const cxcNovaQ = useQuery({
    queryKey: ['administracion', countryCode, 'cuentas-por-cobrar', 'nova'],
    queryFn: () => listCuentasPorCobrar('nova'),
    staleTime: 30_000,
    refetchInterval: REFRESH_MS,
    refetchOnWindowFocus: true,
    refetchOnMount: 'always',
    enabled: mode === 'cobrar_nova' && isMx,
  });
  // Cuentas por Pagar mode reuses the Compras list data, filtered to
  // Pendiente / Pago Parcial statuses. Shares the `['compras']` cache
  // slot with the Compras page so navigating between them is free —
  // both sides include the country code so AR/MX stay isolated.
  const comprasQ = useQuery({
    queryKey: ['compras', countryCode],
    queryFn: listCompras,
    staleTime: 30_000,
    refetchInterval: REFRESH_MS,
    refetchOnWindowFocus: true,
    enabled: mode === 'pagar',
  });
  // MX-only — Cobros Nova queue. Each row is an Anticipo
  // (Cuentas por Cobrar - Nova, Pendiente de Confirmacion). Confirming
  // a row writes the Tesorería Ingreso server-side. PR 3.3.e.1.
  const cobrosNovaQ = useQuery({
    queryKey: ['administracion', countryCode, 'cobros-nova'],
    queryFn: listCobrosNova,
    staleTime: 30_000,
    refetchInterval: REFRESH_MS,
    refetchOnWindowFocus: true,
    refetchOnMount: 'always',
    enabled: mode === 'cobros_nova' && isMx,
  });
  // MX-only — Cuentas por Pagar Nova queue. Reads `cash_movements`
  // pairs auto-emitted by `/facturar` for Factura Interna rows.
  // Confirming releases the lock on the matched Cobrar row. PR 3.3.e.3.
  const pagarNovaQ = useQuery({
    queryKey: ['administracion', countryCode, 'pagar-nova'],
    queryFn: listCuentasPorPagarNova,
    staleTime: 30_000,
    refetchInterval: REFRESH_MS,
    refetchOnWindowFocus: true,
    refetchOnMount: 'always',
    enabled: mode === 'pagar_nova' && isMx,
  });
  // MX-only — inter-co Cobrar Fornitalia rows (cash_movements queue,
  // generated by /facturar on Factura Interna). Rows start
  // `cobro_locked`; server rejects confirm until matching Pagar Nova
  // clears. These are confirmed from the Fornitalia CxC tab.
  const cobrarFornitaliaQ = useQuery({
    queryKey: ['administracion', countryCode, 'cobrar-fornitalia'],
    queryFn: listCuentasPorCobrarFornitalia,
    staleTime: 30_000,
    refetchInterval: REFRESH_MS,
    refetchOnWindowFocus: true,
    refetchOnMount: 'always',
    enabled: mode === 'cobrar' && isMx,
  });

  // Live updates for the four admin queues:
  //   • cobros / facturación → sales_payments + sales_payment_receipts
  //     (sales team uploads a comprobante → row appears for admin)
  //   • cuentas por cobrar → sales_headers (factura issuance flips status)
  //   • cuentas por pagar → purchase_headers (Pagar Factura flips status)
  useRealtimeInvalidate(
    ['sales_payments', 'sales_payment_receipts', 'sales_headers', 'purchase_headers'],
    [['administracion'], ['compras']],
  );

  const isFetching =
    (mode === 'cobros' && cobrosQ.isFetching) ||
    (mode === 'facturacion' && facturacionQ.isFetching) ||
    (mode === 'cobrar' && cxcQ.isFetching) ||
    (mode === 'cobrar_nova' && cxcNovaQ.isFetching) ||
    (mode === 'cobrar' && isMx && cobrarFornitaliaQ.isFetching) ||
    (mode === 'pagar' && comprasQ.isFetching) ||
    (mode === 'cobros_nova' && cobrosNovaQ.isFetching) ||
    (mode === 'pagar_nova' && pagarNovaQ.isFetching) ||
    (mode === 'facturacion_nova' && facturacionNovaQ.isFetching);
  const handleRefresh = () => {
    qc.invalidateQueries({ queryKey: ['administracion'] });
    qc.invalidateQueries({ queryKey: ['compras'] });
  };

  // Clear the selection whenever the mode changes — ids are only
  // meaningful within the currently-visible list.
  useEffect(() => {
    setSelected(new Set());
  }, [mode]);

  const cobrosRows = cobrosQ.data ?? [];
  const facturacionRows = facturacionQ.data ?? [];
  const facturacionNovaRows = facturacionNovaQ.data ?? [];
  const cxcRows = cxcQ.data ?? [];
  const cxcNovaRows = cxcNovaQ.data ?? [];
  const comprasRows = useMemo<CompraListItem[]>(
    () =>
      (comprasQ.data ?? []).filter((c) => {
        const s = c.fields.Status_CO;
        return s === 'Pendiente' || s === 'Pago Parcial';
      }),
    [comprasQ.data],
  );

  const filteredCobros = useMemo(
    () => filterRows(cobrosRows, search),
    [cobrosRows, search],
  );
  const filteredFacturacion = useMemo(
    () => filterRows(facturacionRows, search),
    [facturacionRows, search],
  );
  const filteredFacturacionNova = useMemo(
    () => filterRows(facturacionNovaRows, search),
    [facturacionNovaRows, search],
  );
  // Cuentas por Cobrar: filtro por período ('vencido' o un mes 'YYYY-MM', la
  // misma columna del resumen) y por caja. El resumen ignora el período (sus
  // columnas SON los períodos) pero respeta la búsqueda y la caja.
  // Resumen plegable (arranca plegado para no aplastar la lista); se recuerda
  // por navegador.
  const [resumenAbierto, setResumenAbierto] = useState(() => {
    try {
      return localStorage.getItem('fornitalia.cxc.resumenAbierto') === '1';
    } catch {
      return false;
    }
  });
  const toggleResumenAbierto = () =>
    setResumenAbierto((v) => {
      try {
        localStorage.setItem('fornitalia.cxc.resumenAbierto', v ? '0' : '1');
      } catch {
        /* sin storage: dura lo que la página */
      }
      return !v;
    });
  const [cxcPeriodo, setCxcPeriodo] = useState('todos');
  const [cxcMedio, setCxcMedio] = useState('todos');
  const hoy = hoyAr();
  const cxcColumna = useCallback(
    (r: ComprobanteAdminItem) => columnaPromesa(ddmmyyyyAIso(r.fields.FechaCobro_CP), hoy),
    [hoy],
  );
  const cxcMedios = useMemo(
    () => [...new Set(cxcRows.map((r) => (r.fields.MedioPago_CP ?? '').trim()).filter(Boolean))].sort(),
    [cxcRows],
  );
  const cxcPorBusquedaYMedio = useMemo(
    () =>
      filterRows(cxcRows, search).filter(
        (r) => cxcMedio === 'todos' || (r.fields.MedioPago_CP ?? '').trim() === cxcMedio,
      ),
    [cxcRows, search, cxcMedio],
  );
  const cxcResumen = useMemo(
    () =>
      resumenMedioMes(
        cxcPorBusquedaYMedio.map((r) => ({
          medio: (r.fields.MedioPago_CP ?? '').trim() || '—',
          moneda: inferRowCurrency(r),
          monto: parseMoney(r.fields.Monto_CP) ?? 0,
          fecha: ddmmyyyyAIso(r.fields.FechaCobro_CP),
        })),
        hoy,
      ),
    // esUsd: la moneda sale del ABM de cajas (useEsCajaUsd), que llega async.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [cxcPorBusquedaYMedio, hoy, esUsd],
  );
  const cxcPeriodos = useMemo(() => ordenarColumnas(cxcRows.map(cxcColumna)), [cxcRows, cxcColumna]);
  const filteredCxC = useMemo(
    () =>
      cxcPeriodo === 'todos'
        ? cxcPorBusquedaYMedio
        : cxcPorBusquedaYMedio.filter((r) => cxcColumna(r) === cxcPeriodo),
    [cxcPorBusquedaYMedio, cxcPeriodo, cxcColumna],
  );
  const filteredCxCNova = useMemo(
    () => filterRows(cxcNovaRows, search),
    [cxcNovaRows, search],
  );
  const filteredCompras = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return comprasRows;
    return comprasRows.filter((r) => {
      const f = r.fields;
      return (
        (f.Proveedor_CO ?? '').toLowerCase().includes(q) ||
        (f.NumeroComprobante_CO ?? '').toLowerCase().includes(q) ||
        (f.Comprobante_CO ?? '').toLowerCase().includes(q)
      );
    });
  }, [comprasRows, search]);

  const visibleRows =
    mode === 'cobros'
      ? filteredCobros
      : mode === 'facturacion'
        ? filteredFacturacion
        : mode === 'facturacion_nova'
          ? filteredFacturacionNova
          : mode === 'cobrar'
            ? filteredCxC
            : mode === 'cobrar_nova'
              ? filteredCxCNova
              : [];

  // Drop stale selections when the underlying list changes (e.g. after
  // a successful confirm invalidates the query).
  useEffect(() => {
    if (mode !== 'cobros') return;
    const visibleIds = new Set(cobrosRows.map((r) => r.id));
    setSelected((prev) => {
      const next = new Set<string>();
      for (const id of prev) if (visibleIds.has(id)) next.add(id);
      return next.size === prev.size ? prev : next;
    });
  }, [cobrosRows, mode]);

  const totals = useMemo(() => computeTotals(visibleRows), [visibleRows, esUsd]);
  // Cuentas por Pagar no pasa por `computeTotals`: las filas son compras
  // (`Total_CO`/`Moneda_CO`), no comprobantes de cobro. Y lo que se suma es
  // el RESTANTE, no el total facturado — el tab existe para saber cuánto
  // falta pagar, y con pagos parciales el total miente.
  const totalsPagar = useMemo(
    () => totalesRestantePorMoneda(filteredCompras),
    [filteredCompras],
  );
  const selectedRows = useMemo(
    () => cobrosRows.filter((r) => selected.has(r.id)),
    [cobrosRows, selected],
  );

  // Nova cobros — separate from the Fornitalia cobros pipeline because
  // the row shape (`CobroNovaItem`) doesn't fit `ComprobanteAdminItem`.
  // Same search + cleanup + selected-row patterns as above.
  const filteredCobrosNova = useMemo(() => {
    const q = search.trim().toLowerCase();
    const data = cobrosNovaQ.data ?? [];
    if (!q) return data;
    return data.filter(
      (r) =>
        (r.cliente ?? '').toLowerCase().includes(q) ||
        (r.nro_cotizacion ?? '').toLowerCase().includes(q) ||
        (r.medio_pago_origen ?? '').toLowerCase().includes(q),
    );
  }, [cobrosNovaQ.data, search]);
  useEffect(() => {
    if (mode !== 'cobros_nova') return;
    const ids = new Set((cobrosNovaQ.data ?? []).map((r) => r.id));
    setSelectedNova((prev) => {
      const next = new Set<string>();
      for (const id of prev) if (ids.has(id)) next.add(id);
      return next.size === prev.size ? prev : next;
    });
  }, [cobrosNovaQ.data, mode]);
  const selectedNovaRows = useMemo(
    () => filteredCobrosNova.filter((r) => selectedNova.has(r.id)),
    [filteredCobrosNova, selectedNova],
  );
  // All Nova rows are MXN. Aggregate to a single bucket so FooterTotals
  // can show "Total MXN" instead of the Fornitalia-flavoured Pesos/USD
  // pair which doesn't make sense here.
  const novaTotalMxn = useMemo(
    () => filteredCobrosNova.reduce((s, r) => s + (r.monto ?? 0), 0),
    [filteredCobrosNova],
  );

  // Cuentas por Pagar - Nova: filas inter-compañía, todas en MXN.
  const pagarNovaRows = useMemo(
    () => filterInterCo(pagarNovaQ.data ?? [], search),
    [pagarNovaQ.data, search],
  );
  const pagarNovaTotal = useMemo(
    () => pagarNovaRows.reduce((s, r) => s + r.monto, 0),
    [pagarNovaRows],
  );

  const title = `Administración - ${MODE_LABELS[mode]}`;

  return (
    <div className="flex h-full flex-col gap-6">
      <div className="flex shrink-0 flex-col items-start justify-between gap-4 md:flex-row md:items-center">
        {/* Title row — when the active mode is MX Facturación, the
            segmented filter (Todas / Ventas / Internas) sits inline
            to the right of the title so the header doesn't claim an
            extra row just to show three tabs. On mobile it wraps
            below the title via the flex-wrap container. */}
        <div className="flex flex-wrap items-center gap-3">
          <h1 className="text-2xl font-semibold tracking-tight text-foreground md:text-3xl">
            {title}
          </h1>
          {mode === 'facturacion' && isMx && (
            <FacturacionFilter
              value={facturacionFilter}
              onChange={setFacturacionFilter}
            />
          )}
        </div>
        <div className="flex w-full items-center gap-3 md:w-auto">
          <div className="w-60 shrink-0">
            <Select value={mode} onValueChange={(v) => setMode(v as Mode)}>
              <SelectTrigger>
                <SelectValue placeholder="Seleccionar Estado" />
              </SelectTrigger>
              <SelectContent>
                {/* In MX we group the queues by which legal entity owns
                    the workflow: Fornitalia/Italmexy operates the
                    customer-facing tabs (top group); Nova operates the
                    intermediary tabs (bottom group). Mirrors the
                    Pesos/Dólares grouping pattern used by Tesorería's
                    CajaSelect. AR has no Nova flow, so we render flat
                    without a group header. The Nova role only sees the
                    two queues it actions (cobros_nova, pagar_nova) —
                    the backend rejects everything else. */}
                {isNovaRole ? (
                  <>
                    <SelectItem value="cobros_nova">
                      Cobros a Confirmar - Nova
                    </SelectItem>
                    <SelectItem value="facturacion_nova">
                      Facturación - Nova
                    </SelectItem>
                    <SelectItem value="cobrar_nova">
                      Cuentas por Cobrar - Nova
                    </SelectItem>
                    <SelectItem value="pagar_nova">
                      Cuentas por Pagar - Nova
                    </SelectItem>
                  </>
                ) : isMx ? (
                  <>
                    <SelectGroup>
                      <SelectLabel>Fornitalia</SelectLabel>
                      <SelectItem value="cobros">Cobros a Confirmar</SelectItem>
                      <SelectItem value="facturacion">Facturación</SelectItem>
                      <SelectItem value="cobrar">Cuentas por Cobrar</SelectItem>
                      <SelectItem value="pagar">Cuentas por Pagar</SelectItem>
                    </SelectGroup>
                    <SelectGroup>
                      {/* Order mirrors the Fornitalia group above so the
                          two columns of mental model line up 1:1 — Cobros,
                          Facturación, Cuentas por Cobrar, Cuentas por
                          Pagar. */}
                      <SelectLabel>Nova</SelectLabel>
                      <SelectItem value="cobros_nova">
                        Cobros a Confirmar - Nova
                      </SelectItem>
                      <SelectItem value="facturacion_nova">
                        Facturación - Nova
                      </SelectItem>
                      <SelectItem value="cobrar_nova">
                        Cuentas por Cobrar - Nova
                      </SelectItem>
                      <SelectItem value="pagar_nova">
                        Cuentas por Pagar - Nova
                      </SelectItem>
                    </SelectGroup>
                  </>
                ) : (
                  <>
                    <SelectItem value="cobros">Cobros a Confirmar</SelectItem>
                    <SelectItem value="facturacion">Facturación</SelectItem>
                    <SelectItem value="cobrar">Cuentas por Cobrar</SelectItem>
                    <SelectItem value="pagar">Cuentas por Pagar</SelectItem>
                    <SelectItem value="echeqs">E-cheqs</SelectItem>
                  </>
                )}
              </SelectContent>
            </Select>
          </div>
          <div className="relative flex-1 md:w-72">
            <Search
              className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground"
              aria-hidden
            />
            <Input
              variant="filled"
              placeholder="Búsqueda"
              className="pl-9"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              aria-label="Buscar"
            />
          </div>
          <Button
            variant="outline"
            size="icon"
            className="h-12 w-12 shrink-0"
            onClick={handleRefresh}
            aria-label="Actualizar"
            title="Actualizar"
          >
            <RefreshCw
              className={cn('h-4 w-4', isFetching && 'animate-spin')}
              aria-hidden
            />
          </Button>
        </div>
      </div>

      {mode === 'cobros' ? (
        <CobrosTable
          rows={filteredCobros}
          loading={cobrosQ.isPending}
          error={cobrosQ.isError}
          searchActive={Boolean(search.trim())}
          selected={selected}
          onToggle={(id) =>
            setSelected((prev) => {
              const next = new Set(prev);
              if (next.has(id)) next.delete(id);
              else next.add(id);
              return next;
            })
          }
          onToggleAll={(allSelected) => {
            if (allSelected) setSelected(new Set());
            else setSelected(new Set(filteredCobros.map((r) => r.id)));
          }}
          onView={(row) => setDialog({ kind: 'view', row })}
          onOpenVenta={(row) =>
            row.fields.IDOperacion_CP &&
            navigate(`/ventas/${row.fields.IDOperacion_CP}/pagos`, {
              state: { from: 'administracion' as const, tab: 'cobros' },
            })
          }
        />
      ) : mode === 'facturacion' ? (
        <FacturacionTable
          rows={filteredFacturacion}
          loading={facturacionQ.isPending}
          error={facturacionQ.isError}
          searchActive={Boolean(search.trim())}
          onView={(row) => setDialog({ kind: 'view', row })}
          onFacturar={(row) => {
            // AR: facturar un Pago Final mientras un ANTICIPO de la MISMA venta
            // sigue pendiente de facturar → diálogo de confirmación previo (acá
            // y no dentro del Facturar dialog, para no sumarle ruido). Conviene
            // facturar primero el anticipo así el Pago Final lo descuenta y cita
            // su número. Se chequea contra la cola SIN filtrar por búsqueda
            // (facturacionRows) para que un filtro activo no esconda al hermano.
            const op = row.fields.IDOperacion_CP;
            const anticipoPendiente =
              !isMx &&
              (row.fields.EsPagoFinal_CP ?? '') === 'Si' &&
              Boolean(op) &&
              facturacionRows.some(
                (r) =>
                  r.id !== row.id &&
                  r.fields.IDOperacion_CP === op &&
                  (r.fields.EsPagoFinal_CP ?? '') !== 'Si',
              );
            setDialog(
              anticipoPendiente
                ? { kind: 'facturar-warn-anticipo', row }
                : { kind: 'facturar', row },
            );
          }}
          onOpenVenta={(row) =>
            row.fields.IDOperacion_CP &&
            navigate(`/ventas/${row.fields.IDOperacion_CP}/pagos`, {
              state: { from: 'administracion' as const, tab: 'facturacion' },
            })
          }
        />
      ) : mode === 'facturacion_nova' ? (
        // Reuses the regular FacturacionTable + FacturarDialog — the
        // data shape and action (register N° de factura + upload PDF)
        // is identical to the AR-style flow. The only difference is
        // the source rows come from `type=nova` instead of `type=ventas`,
        // and the URL tab on Atrás-from-venta lands back here. The
        // Facturar action is hidden for `administracion` (only `nova`
        // and `admin` can act on Nova rows).
        <FacturacionTable
          rows={filteredFacturacionNova}
          loading={facturacionNovaQ.isPending}
          error={facturacionNovaQ.isError}
          searchActive={Boolean(search.trim())}
          isReadOnly={effectiveReadOnly}
          // MX Nova: hide the Anticipo/Pago Final pill — the stage is
          // decided at upload time via the FacturarDialog toggle, not
          // at registration, so the pill would always read "Anticipo"
          // and mislead operators about what they're about to upload.
          hideStageLabel
          onView={(row) => setDialog({ kind: 'view', row })}
          onFacturar={(row) => setDialog({ kind: 'facturar', row })}
          onOpenVenta={(row) =>
            row.fields.IDOperacion_CP &&
            navigate(`/ventas/${row.fields.IDOperacion_CP}/pagos`, {
              state: { from: 'administracion' as const, tab: 'facturacion_nova' },
            })
          }
        />
      ) : mode === 'pagar' ? (
        <CuentasPorPagarTable
          rows={filteredCompras}
          loading={comprasQ.isPending}
          error={comprasQ.isError}
          searchActive={Boolean(search.trim())}
          onPagar={(row) => navigate(`/administracion/pagar/${row.id}`)}
          onVer={(row) =>
            navigate(`/compras/${row.id}`, { state: { from: '/administracion' } })
          }
        />
      ) : mode === 'cobrar' ? (
        <div className="flex min-h-0 flex-1 flex-col gap-4">
          {cxcResumen.monedas.length > 0 && (
            <section className="flex shrink-0 flex-col gap-2">
              <button
                type="button"
                onClick={toggleResumenAbierto}
                aria-expanded={resumenAbierto}
                className="flex flex-wrap items-center gap-x-3 gap-y-1 text-left"
              >
                <span className="flex items-center gap-1.5 text-xs font-medium uppercase tracking-wide text-muted-foreground">
                  <ChevronDown
                    className={cn('h-4 w-4 transition-transform', !resumenAbierto && '-rotate-90')}
                    aria-hidden
                  />
                  A cobrar por caja y mes
                </span>
                {/* Plegado: los números clave en una línea. */}
                {!resumenAbierto &&
                  cxcResumen.monedas.map((m) => {
                    const mon = m.moneda === 'USD' ? 'USD' : m.moneda === 'MXN' ? 'MXN' : 'ARS';
                    return (
                      <span key={m.moneda} className="text-xs text-muted-foreground">
                        {m.totalPorColumna.vencido ? (
                          <span className="text-rose-700">
                            Vencido {formatMoney(m.totalPorColumna.vencido, mon)} ·{' '}
                          </span>
                        ) : null}
                        Total <span className="font-semibold text-foreground">{formatMoney(m.total, mon)}</span>
                      </span>
                    );
                  })}
              </button>
              {/* Alto animado: grid 0fr ↔ 1fr (sin medir en JS), con fundido. */}
              <div
                className={cn(
                  'grid transition-[grid-template-rows,opacity] duration-300 ease-out',
                  resumenAbierto ? 'grid-rows-[1fr] opacity-100' : 'grid-rows-[0fr] opacity-0',
                )}
                aria-hidden={!resumenAbierto}
              >
                <div className="min-h-0 overflow-hidden">
                  <ResumenPromesas resumen={cxcResumen} mesActual={hoy.slice(0, 7)} />
                </div>
              </div>
            </section>
          )}
          <section className="flex min-h-0 flex-col gap-2">
            <div className="flex flex-wrap items-center justify-between gap-2">
              <h2 className="text-xs font-medium uppercase tracking-wide text-muted-foreground">
                Promesas de Pago
              </h2>
              <div className="flex flex-wrap gap-2">
                <Select value={cxcPeriodo} onValueChange={setCxcPeriodo}>
                  <SelectTrigger className="h-9 w-48" aria-label="Período">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="todos">Todos los períodos</SelectItem>
                    {cxcPeriodos.map((c) => (
                      <SelectItem key={c} value={c}>
                        {c === 'vencido' ? 'Vencidas' : etiquetaColumna(c)}
                        {c === hoy.slice(0, 7) ? ' (este mes)' : ''}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <Select value={cxcMedio} onValueChange={setCxcMedio}>
                  <SelectTrigger className="h-9 w-56" aria-label="Caja">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="todos">Todas las cajas</SelectItem>
                    {cxcMedios.map((m) => (
                      <SelectItem key={m} value={m}>
                        {m}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
            <CuentasPorCobrarTable
              rows={filteredCxC}
              loading={cxcQ.isPending}
              error={cxcQ.isError}
              searchActive={Boolean(search.trim())}
              onView={(row) => setDialog({ kind: 'view', row })}
              onOpenVenta={(row) =>
                row.fields.IDOperacion_CP &&
                navigate(`/ventas/${row.fields.IDOperacion_CP}/pagos`, {
                  state: { from: 'administracion' as const, tab: 'cobrar' },
                })
              }
            />
          </section>
          {isMx && filterInterCo(cobrarFornitaliaQ.data ?? [], search).length > 0 && (
            <section className="flex min-h-0 flex-1 flex-col gap-2">
              <h2 className="text-xs font-medium uppercase tracking-wide text-muted-foreground">
                Inter-compania
              </h2>
              <InterCoTable
                variant="cobrar"
                rows={filterInterCo(cobrarFornitaliaQ.data ?? [], search)}
                loading={cobrarFornitaliaQ.isPending}
                error={cobrarFornitaliaQ.isError}
                searchActive={Boolean(search.trim())}
                isReadOnly={isReadOnly}
                onConfirm={(row) => setDialog({ kind: 'confirm-cobrar-fornitalia', row })}
                onView={(row) => setDialog({ kind: 'view-interco', row })}
              />
            </section>
          )}
        </div>
      ) : mode === 'cobrar_nova' ? (
        // MX-only Nova "Cuentas por Cobrar - Nova" (promesas only).
        <CuentasPorCobrarTable
          rows={filteredCxCNova}
          loading={cxcNovaQ.isPending}
          error={cxcNovaQ.isError}
          searchActive={Boolean(search.trim())}
          onView={(row) => setDialog({ kind: 'view', row })}
          onOpenVenta={(row) =>
            row.fields.IDOperacion_CP &&
            navigate(`/ventas/${row.fields.IDOperacion_CP}/pagos`, {
              state: { from: 'administracion' as const, tab: 'cobrar_nova' },
            })
          }
        />
      ) : mode === 'cobros_nova' ? (
        <NovaCobrosTable
          rows={filteredCobrosNova}
          loading={cobrosNovaQ.isPending}
          error={cobrosNovaQ.isError}
          searchActive={Boolean(search.trim())}
          isReadOnly={effectiveReadOnly}
          selected={selectedNova}
          onToggle={(id) =>
            setSelectedNova((prev) => {
              const next = new Set(prev);
              if (next.has(id)) next.delete(id);
              else next.add(id);
              return next;
            })
          }
          onToggleAll={(allSelected) => {
            if (allSelected) setSelectedNova(new Set());
            else setSelectedNova(new Set(filteredCobrosNova.map((r) => r.id)));
          }}
          onView={(row) => setDialog({ kind: 'view-cobro-nova', row })}
        />
      ) : mode === 'pagar_nova' ? (
        <InterCoTable
          variant="pagar"
          rows={pagarNovaRows}
          loading={pagarNovaQ.isPending}
          error={pagarNovaQ.isError}
          searchActive={Boolean(search.trim())}
          isReadOnly={effectiveReadOnly}
          // Opens the caja-picker dialog. The dialog owns the mutation
          // + saldo display + insufficient-balance warning, so the
          // table's Confirmar button can't fire the API by itself.
          onConfirm={(row) => setDialog({ kind: 'confirm-pagar-nova', row })}
          onView={(row) => setDialog({ kind: 'view-interco', row })}
        />
      ) : mode === 'echeqs' ? (
        <EcheqsBandeja search={search} readOnly={effectiveReadOnly} />
      ) : (
        <div className="flex-1 rounded-2xl border border-dashed bg-card/40 p-10 text-center text-sm text-muted-foreground">
          Próximamente.
        </div>
      )}

      {/* La bandeja de E-cheqs trae su propio pie (total + cantidad). */}
      {mode !== 'echeqs' && (
        <FooterTotals
          totalPesos={
            mode === 'cobros_nova'
              ? novaTotalMxn
              : mode === 'pagar'
                ? totalsPagar.pesos
                : mode === 'pagar_nova'
                  ? pagarNovaTotal
                  : totals.pesos
          }
          totalDolares={mode === 'pagar' ? totalsPagar.dolares : totals.dolares}
          count={
            mode === 'cobros_nova'
              ? filteredCobrosNova.length
              : mode === 'pagar'
                ? filteredCompras.length
                : mode === 'pagar_nova'
                  ? pagarNovaRows.length
                  : visibleRows.length
          }
          selectedCount={
            mode === 'cobros'
              ? selected.size
              : mode === 'cobros_nova'
                ? selectedNova.size
                : 0
          }
          onConfirmar={() => {
            if (mode === 'cobros') {
              setDialog({ kind: 'confirm-cobros', rows: selectedRows });
            } else if (mode === 'cobros_nova') {
              setDialog({ kind: 'confirm-cobros-nova-bulk', rows: selectedNovaRows });
            }
          }}
          showConfirmar={
            (mode === 'cobros' || mode === 'cobros_nova') && !effectiveReadOnly
          }
          pesosLabel={PESOS_LABEL[mode] ?? 'Total Pesos'}
          dolaresLabel={mode === 'pagar' ? 'Restante Dólares' : 'Total Dólares'}
          pesosCurrency={mode === 'cobros_nova' || mode === 'pagar_nova' ? 'MXN' : 'ARS'}
          hideDolares={mode === 'cobros_nova' || mode === 'pagar_nova'}
        />
      )}

      {dialog.kind === 'view' && (
        <ViewComprobanteDialog
          row={dialog.row}
          onClose={() => setDialog({ kind: 'closed' })}
        />
      )}
      {dialog.kind === 'view-cobro-nova' && (
        <ViewCobroNovaDialog
          row={dialog.row}
          onClose={() => setDialog({ kind: 'closed' })}
        />
      )}
      {dialog.kind === 'view-interco' && (
        <ViewInterCoDialog
          row={dialog.row}
          onClose={() => setDialog({ kind: 'closed' })}
        />
      )}
      {dialog.kind === 'confirm-cobros' && (
        <ConfirmarCobrosDialog
          rows={dialog.rows}
          onClose={() => setDialog({ kind: 'closed' })}
          onAfter={() => setSelected(new Set())}
        />
      )}
      {dialog.kind === 'facturar' && (
        <FacturarDialog
          row={dialog.row}
          onClose={() => setDialog({ kind: 'closed' })}
        />
      )}
      {dialog.kind === 'facturar-warn-anticipo' && (
        <Dialog open onOpenChange={(o) => !o && setDialog({ kind: 'closed' })}>
          <DialogContent className="max-w-md">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <AlertTriangle className="h-5 w-5 text-amber-500" aria-hidden />
                Anticipo pendiente de facturar
              </DialogTitle>
              <DialogDescription>
                Esta venta tiene un <strong>anticipo pendiente de facturar</strong>. Conviene
                facturar primero el anticipo: así el Pago Final lo descuenta y cita su número
                de factura. ¿Querés facturar el Pago Final igual?
              </DialogDescription>
            </DialogHeader>
            <DialogFooter>
              <Button
                type="button"
                variant="outline"
                onClick={() => setDialog({ kind: 'closed' })}
              >
                Cancelar
              </Button>
              <Button
                type="button"
                onClick={() => setDialog({ kind: 'facturar', row: dialog.row })}
              >
                Facturar igual
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      )}
      {dialog.kind === 'confirm-cobro-nova' && (
        <ConfirmarCobroNovaDialog
          row={dialog.row}
          onClose={() => setDialog({ kind: 'closed' })}
        />
      )}
      {dialog.kind === 'confirm-cobros-nova-bulk' && (
        <ConfirmarCobrosNovaBulkDialog
          rows={dialog.rows}
          onClose={() => setDialog({ kind: 'closed' })}
          onAfter={() => setSelectedNova(new Set())}
        />
      )}
      {dialog.kind === 'confirm-pagar-nova' && (
        <ConfirmarPagarNovaDialog
          row={dialog.row}
          onClose={() => setDialog({ kind: 'closed' })}
        />
      )}
      {dialog.kind === 'confirm-cobrar-fornitalia' && (
        <ConfirmarCobrarFornitaliaDialog
          row={dialog.row}
          onClose={() => setDialog({ kind: 'closed' })}
        />
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Filtering + totals
// ---------------------------------------------------------------------------

function filterRows(rows: ComprobanteAdminItem[], search: string): ComprobanteAdminItem[] {
  const q = search.trim().toLowerCase();
  if (!q) return rows;
  return rows.filter((r) => {
    const f = r.fields;
    return (
      (r.clienteRO ?? '').toLowerCase().includes(q) ||
      (f.MedioPago_CP ?? '').toLowerCase().includes(q) ||
      (f.IDOperacion_CP ?? '').toLowerCase().includes(q) ||
      (f.NroFactura_CP ?? '').toLowerCase().includes(q)
    );
  });
}

/**
 * Footer totals match the PowerApps split:
 *   - Total Pesos = sum of rows whose native currency is ARS
 *   - Total Dólares = sum of rows whose native currency is USD
 * We infer the row's native currency from MedioPago_CP (the string
 * convention PowerApps uses — USD medios carry 'Dolar' in the label).
 * This intentionally does NOT use the venta currency (for display we
 * want to show cash-flow totals in each currency).
 */
function computeTotals(rows: ComprobanteAdminItem[]): { pesos: number; dolares: number } {
  let pesos = 0;
  let dolares = 0;
  for (const r of rows) {
    const isUsd = esCajaUsd(r.fields.MedioPago_CP);
    const monto = parseMoney(r.fields.Monto_CP) ?? 0;
    if (isUsd) dolares += monto;
    else pesos += monto;
  }
  return { pesos, dolares };
}

// ---------------------------------------------------------------------------
// Facturación filter (MX) — segmented control
// ---------------------------------------------------------------------------

function FacturacionFilter({
  value,
  onChange,
}: {
  value: FacturacionType;
  onChange: (next: FacturacionType) => void;
}) {
  const options: Array<{ key: FacturacionType; label: string }> = [
    { key: 'todas', label: 'Todas' },
    { key: 'ventas', label: 'Ventas' },
    // Auto-generated Factura Interna por costo contable (PR 3.3.d).
    // Nova-channel customer cobros (with factura) live in their own
    // top-level "Facturación - Nova" tab — not in this segmented
    // filter — so operators can't accidentally double-register them.
    { key: 'internas', label: 'Internas' },
  ];
  return (
    <div
      role="tablist"
      aria-label="Filtro de facturación"
      className="inline-flex shrink-0 self-start rounded-lg border bg-secondary p-1"
    >
      {options.map((opt) => {
        const active = opt.key === value;
        return (
          <button
            key={opt.key}
            role="tab"
            type="button"
            aria-selected={active}
            onClick={() => onChange(opt.key)}
            className={cn(
              'rounded-md px-3 py-1.5 text-sm font-medium transition-colors',
              active
                ? 'bg-background text-foreground shadow-sm'
                : 'text-muted-foreground hover:text-foreground',
            )}
          >
            {opt.label}
          </button>
        );
      })}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Tables
// ---------------------------------------------------------------------------

/**
 * Marca compacta de Facturación Previa para las colas de Administración
 * (Cobros a Confirmar / Cuentas por Cobrar), donde la columna Tipo casi no
 * tiene lugar: "PV" y el texto completo en el tooltip.
 *
 * Importa acá porque cambia lo que el operador está por hacer: en estas colas
 * la plata TODAVÍA no entró, pero la factura puede estar ya emitida ante ARCA.
 */
function PvTag({
  fields,
  size = 'md',
}: {
  fields: ComprobanteAdminItem['fields'];
  size?: 'md' | 'sm';
}) {
  if (fields.FacturacionPrevia_CP !== 'SI') return null;
  const emitida = fields.Facturado_CP === 'SI';
  return (
    <span
      title={
        emitida
          ? `Factura Anticipada — la factura ya fue emitida${
              fields.NroFactura_CP ? ` (${fields.NroFactura_CP})` : ''
            } antes de cobrarse`
          : 'Factura Anticipada aprobada — se factura antes de recibir el pago'
      }
      className={cn(
        'inline-flex shrink-0 rounded-md bg-emerald-100 font-semibold uppercase tracking-wide text-emerald-800',
        size === 'sm' ? 'px-1.5 py-0.5 text-[10px]' : 'px-2 py-1 text-[11px]',
      )}
    >
      FA
    </span>
  );
}

function CobrosTable({
  rows,
  loading,
  error,
  searchActive,
  selected,
  onToggle,
  onToggleAll,
  onView,
  onOpenVenta,
}: {
  rows: ComprobanteAdminItem[];
  loading: boolean;
  error: boolean;
  searchActive: boolean;
  selected: Set<string>;
  onToggle: (id: string) => void;
  onToggleAll: (allSelected: boolean) => void;
  onView: (row: ComprobanteAdminItem) => void;
  onOpenVenta: (row: ComprobanteAdminItem) => void;
}) {
  const allSelected = rows.length > 0 && rows.every((r) => selected.has(r.id));
  const emptyMessage = searchActive
    ? 'Sin coincidencias para la búsqueda.'
    : 'No hay cobros pendientes de confirmación.';
  return (
    <div className="flex min-h-0 flex-1 flex-col overflow-hidden rounded-2xl border bg-card">
      {/* Desktop / tablet table */}
      <div className="hidden min-h-0 flex-1 overflow-y-auto md:block">
        <table className="w-full text-sm">
          <thead className="sticky top-0 z-10 border-b bg-card text-left text-xs text-muted-foreground">
            <tr>
              <Th className="w-10">
                <CheckboxCell
                  checked={allSelected}
                  onChange={() => onToggleAll(allSelected)}
                  disabled={rows.length === 0}
                  aria-label="Seleccionar todos"
                />
              </Th>
              <Th>Fecha</Th>
              <Th>Cliente</Th>
              <Th>Caja</Th>
              <Th className="text-right">Monto</Th>
              <Th>Tipo</Th>
              <Th className="w-24 text-right">Acciones</Th>
            </tr>
          </thead>
          <tbody className="divide-y divide-border">
            {loading ? (
              <SkeletonRows cols={7} />
            ) : error ? (
              <RowStatus cols={7}>
                <span className="text-destructive">
                  No se pudieron cargar los cobros.
                </span>
              </RowStatus>
            ) : rows.length === 0 ? (
              <RowStatus cols={7}>{emptyMessage}</RowStatus>
            ) : (
              rows.map((r) => {
                const f = r.fields;
                const monto = parseMoney(f.Monto_CP);
                const moneda = inferRowCurrency(r);
                const tipo = f.EsPagoFinal_CP === 'Si' ? 'Pago Final' : 'Anticipo';
                return (
                  <tr key={r.id} className="transition-colors hover:bg-muted/30">
                    <Td>
                      <CheckboxCell
                        checked={selected.has(r.id)}
                        onChange={() => onToggle(r.id)}
                        aria-label={`Seleccionar cobro ${r.id}`}
                      />
                    </Td>
                    <Td className="text-muted-foreground">{f.Fecha_CP || '—'}</Td>
                    <Td className="font-medium text-foreground">
                      {r.clienteRO || f.Cliente_CP || '—'}
                    </Td>
                    <Td className="text-muted-foreground"><CajaNombre caja={f.MedioPago_CP} soloMarca /></Td>
                    <Td className="text-right font-semibold text-foreground">
                      {monto != null ? (
                        <span className="whitespace-nowrap">
                          {formatMoney(monto, moneda)}{' '}
                          <span className="text-xs font-normal text-muted-foreground">
                            {moneda}
                          </span>
                        </span>
                      ) : (
                        '—'
                      )}
                    </Td>
                    <Td>
                      <div className="inline-flex items-center gap-1">
                        <span
                          className={cn(
                            'inline-flex rounded-md px-2 py-1 text-[11px] font-semibold uppercase tracking-wide',
                            f.EsPagoFinal_CP === 'Si'
                              ? 'bg-emerald-100 text-emerald-700'
                              : 'bg-sky-100 text-sky-700',
                          )}
                        >
                          {tipo}
                        </span>
                        <PvTag fields={f} />
                      </div>
                    </Td>
                    <Td className="text-right">
                      <div className="inline-flex items-center gap-1.5">
                        <IconButton
                          onClick={() => onView(r)}
                          label="Ver detalles"
                          tone="neutral"
                        >
                          <Eye className="h-4 w-4" aria-hidden />
                        </IconButton>
                        <IconButton
                          onClick={() => onOpenVenta(r)}
                          label="Ver pagos de la venta"
                          tone="neutral"
                          disabled={!r.fields.IDOperacion_CP}
                        >
                          <Receipt className="h-4 w-4" aria-hidden />
                        </IconButton>
                      </div>
                    </Td>
                  </tr>
                );
              })
            )}
          </tbody>
        </table>
      </div>

      {/* Mobile cards */}
      <div className="min-h-0 flex-1 overflow-y-auto md:hidden">
        <MobileCardShell
          loading={loading}
          error={error}
          empty={rows.length === 0}
          emptyMessage={emptyMessage}
          errorMessage="No se pudieron cargar los cobros."
        >
          {rows.map((r) => {
            const f = r.fields;
            const monto = parseMoney(f.Monto_CP);
            const moneda = inferRowCurrency(r);
            const tipo = f.EsPagoFinal_CP === 'Si' ? 'Pago Final' : 'Anticipo';
            const isSelected = selected.has(r.id);
            return (
              <li
                key={r.id}
                className={cn(
                  'flex flex-col gap-2 px-4 py-3 transition-colors',
                  isSelected && 'bg-emerald-50/60',
                )}
              >
                <div className="flex items-start gap-3">
                  <CheckboxCell
                    checked={isSelected}
                    onChange={() => onToggle(r.id)}
                    aria-label={`Seleccionar cobro ${r.id}`}
                  />
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center justify-between gap-2">
                      <span className="truncate text-sm font-medium text-foreground">
                        {r.clienteRO || f.Cliente_CP || '—'}
                      </span>
                      <span className="flex shrink-0 items-center gap-1">
                        <span
                          className={cn(
                            'rounded-md px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide',
                            f.EsPagoFinal_CP === 'Si'
                              ? 'bg-emerald-100 text-emerald-700'
                              : 'bg-sky-100 text-sky-700',
                          )}
                        >
                          {tipo}
                        </span>
                        <PvTag fields={f} size="sm" />
                      </span>
                    </div>
                    <div className="mt-0.5 text-xs text-muted-foreground">
                      {f.Fecha_CP || '—'} · <CajaNombre caja={f.MedioPago_CP} soloMarca />
                    </div>
                  </div>
                </div>
                <div className="flex items-center justify-between gap-2 pl-8">
                  <span className="text-base font-semibold text-foreground">
                    {monto != null ? formatMoney(monto, moneda) : '—'}{' '}
                    <span className="text-xs font-normal text-muted-foreground">
                      {moneda}
                    </span>
                  </span>
                  <div className="inline-flex items-center gap-1.5">
                    <IconButton onClick={() => onView(r)} label="Ver detalles" tone="neutral">
                      <Eye className="h-4 w-4" aria-hidden />
                    </IconButton>
                    <IconButton
                      onClick={() => onOpenVenta(r)}
                      label="Ver pagos de la venta"
                      tone="neutral"
                      disabled={!r.fields.IDOperacion_CP}
                    >
                      <Receipt className="h-4 w-4" aria-hidden />
                    </IconButton>
                  </div>
                </div>
              </li>
            );
          })}
        </MobileCardShell>
      </div>
    </div>
  );
}

function FacturacionTable({
  rows,
  loading,
  error,
  searchActive,
  isReadOnly: isReadOnlyProp,
  hideStageLabel,
  onView,
  onFacturar,
  onOpenVenta,
}: {
  rows: ComprobanteAdminItem[];
  loading: boolean;
  error: boolean;
  searchActive: boolean;
  /** Optional override. When omitted, falls back to the session-level
   *  visitante read-only. Provided by callers that need extra gating
   *  (e.g. Nova tab seen by `administracion` — view-only). */
  isReadOnly?: boolean;
  /** When true, suppress the Anticipo / Pago Final pill in the Tipo
   *  column (Factura Interna still renders — it's a distinct stage,
   *  not part of the anticipo/final dichotomy). Used by the MX Nova
   *  tab where the registration-time Pago Final toggle was removed —
   *  every non-Interna row defaults to "Anticipo" until Admin uploads
   *  the factura via the FacturarDialog's stage toggle, so the pill is
   *  stale until that point. AR keeps the pill since registration-time
   *  Pago Final still applies there. */
  hideStageLabel?: boolean;
  onView: (row: ComprobanteAdminItem) => void;
  onFacturar: (row: ComprobanteAdminItem) => void;
  onOpenVenta: (row: ComprobanteAdminItem) => void;
}) {
  const sessionReadOnly = useIsReadOnly();
  const isReadOnly = isReadOnlyProp ?? sessionReadOnly;
  const emptyMessage = searchActive
    ? 'Sin coincidencias para la búsqueda.'
    : 'No hay comprobantes pendientes de facturar.';
  return (
    <div className="flex min-h-0 flex-1 flex-col overflow-hidden rounded-2xl border bg-card">
      {/* Desktop / tablet table */}
      <div className="hidden min-h-0 flex-1 overflow-y-auto md:block">
        <table className="w-full text-sm">
          <thead className="sticky top-0 z-10 border-b bg-card text-left text-xs text-muted-foreground">
            <tr>
              <Th>Fecha</Th>
              <Th>Cliente</Th>
              <Th>Caja</Th>
              <Th className="text-right">Monto</Th>
              <Th>Tipo</Th>
              <Th className="w-28 text-right">Acciones</Th>
            </tr>
          </thead>
          <tbody className="divide-y divide-border">
            {loading ? (
              <SkeletonRows cols={6} />
            ) : error ? (
              <RowStatus cols={6}>
                <span className="text-destructive">
                  No se pudieron cargar los pendientes de facturación.
                </span>
              </RowStatus>
            ) : rows.length === 0 ? (
              <RowStatus cols={6}>{emptyMessage}</RowStatus>
            ) : (
              rows.map((r) => {
                const f = r.fields;
                const monto = parseMoney(f.Monto_CP);
                const moneda = inferRowCurrency(r);
                const isInterna = f.EsFacturaInterna_CP === 'SI';
                // Usado FIs carry a placeholder monto (costoContable ×
                // 1.05 × 1.16) until administración types the real
                // amount via the FacturarDialog override. Show "—"
                // while the row is still pending facturación so the
                // operator isn't misled by the placeholder.
                const usadoPendingMonto =
                  isInterna && r.facturaInternaUsado && f.Facturado_CP !== 'SI';
                const tipo = isInterna
                  ? 'Factura Interna'
                  : f.EsPagoFinal_CP === 'Si'
                    ? 'Pago Final'
                    : 'Anticipo';
                return (
                  <tr
                    key={r.id}
                    className={cn(
                      'transition-colors',
                      // MX Nova "horno usado" rows tint red end-to-end so the
                      // operator notices before opening the dialog that the
                      // FI needs manual handling. AR rows always render with
                      // the default hover state.
                      r.facturaInternaUsado
                        ? 'bg-rose-50/60 hover:bg-rose-100/60'
                        : 'hover:bg-muted/30',
                    )}
                  >
                    <Td className="text-muted-foreground">{f.Fecha_CP || '—'}</Td>
                    <Td className="font-medium text-foreground">
                      {r.clienteRO || f.Cliente_CP || '—'}
                    </Td>
                    <Td className="text-muted-foreground"><CajaNombre caja={f.MedioPago_CP} soloMarca /></Td>
                    <Td className="text-right font-semibold text-foreground">
                      {!usadoPendingMonto && monto != null ? (
                        <span className="whitespace-nowrap">
                          {formatMoney(monto, moneda)}{' '}
                          <span className="text-xs font-normal text-muted-foreground">
                            {moneda}
                          </span>
                        </span>
                      ) : (
                        <span
                          className="text-muted-foreground"
                          title={
                            usadoPendingMonto
                              ? 'El monto lo carga administración al facturar (horno usado).'
                              : undefined
                          }
                        >
                          —
                        </span>
                      )}
                    </Td>
                    <Td>
                      {(() => {
                        // MX Nova path (`hideStageLabel`): the pill follows
                        // actual upload state, not the (stale) registration
                        // flag. Until the operator uploads an Anticipada PDF
                        // we don't know which stage this row will become,
                        // so render `—`. As soon as `FacturaAnticipadaFileName_CP`
                        // lands the row is committed to the Anticipo stage —
                        // show that. The Final commit removes the row from
                        // this queue (Facturado_CP flips to 'SI'), so there's
                        // no "Final" case to render here.
                        if (hideStageLabel && !isInterna) {
                          const anticipadaCommitted = Boolean(
                            f.FacturaAnticipadaFileName_CP?.trim(),
                          );
                          if (!anticipadaCommitted) {
                            return <span className="text-muted-foreground">—</span>;
                          }
                          return (
                            <span className="inline-flex rounded-md bg-sky-100 px-2 py-1 text-[11px] font-semibold uppercase tracking-wide text-sky-700">
                              Anticipo
                            </span>
                          );
                        }
                        // Factura Interna rows on a "horno usado" venta show
                        // the Tipo pill in red instead of violet so the row
                        // visually flags the manual-amount workflow before
                        // the operator opens the dialog. Non-Interna or
                        // non-usado rows keep their existing pill colours.
                        const usadoFI = isInterna && r.facturaInternaUsado;
                        return (
                          <div className="inline-flex items-center gap-1">
                            <span
                              className={cn(
                                'inline-flex rounded-md px-2 py-1 text-[11px] font-semibold uppercase tracking-wide',
                                usadoFI
                                  ? 'bg-rose-100 text-rose-700'
                                  : isInterna
                                    ? 'bg-violet-100 text-violet-700'
                                    : f.EsPagoFinal_CP === 'Si'
                                      ? 'bg-emerald-100 text-emerald-700'
                                      : 'bg-sky-100 text-sky-700',
                              )}
                            >
                              {tipo}
                            </span>
                            {usadoFI && (
                              <span className="inline-flex rounded-md bg-rose-100 px-2 py-1 text-[11px] font-semibold uppercase tracking-wide text-rose-700">
                                Usado
                              </span>
                            )}
                            {/* La cola ahora mezcla cobros confirmados (plata
                                adentro, fila congelada) con cobros de
                                Facturación Previa (plata NO adentro, fila
                                todavía anulable). Sin distinguirlos el
                                operador factura los segundos creyendo que
                                cobró. */}
                            {/* "FA", no "Anticipada": esta misma celda ya
                                muestra el Tipo, que puede ser "Anticipo"
                                (etapa de la factura). Dos palabras casi
                                iguales, pegadas y con significados distintos,
                                se leen mal — y encima la columna es angosta.
                                Misma abreviatura que `PvTag`, texto completo
                                al pasar el mouse. */}
                            {f.FacturacionPrevia_CP === 'SI' && (
                              <span
                                className="inline-flex shrink-0 rounded-md bg-emerald-100 px-2 py-1 text-[11px] font-semibold uppercase tracking-wide text-emerald-800"
                                title={`Factura Anticipada — sin cobrar (${f.Status_P ?? '—'})`}
                              >
                                FA
                              </span>
                            )}
                          </div>
                        );
                      })()}
                    </Td>
                    <Td className="text-right">
                      <div className="inline-flex items-center gap-1">
                        <IconButton
                          onClick={() => onView(r)}
                          label="Ver detalles"
                          tone="neutral"
                        >
                          <Eye className="h-4 w-4" aria-hidden />
                        </IconButton>
                        <IconButton
                          onClick={() => onOpenVenta(r)}
                          label="Abrir venta"
                          tone="neutral"
                          disabled={!r.fields.IDOperacion_CP}
                        >
                          <Receipt className="h-4 w-4" aria-hidden />
                        </IconButton>
                        {!isReadOnly && (
                          <IconButton
                            onClick={() => onFacturar(r)}
                            label="Facturar"
                            tone="success"
                          >
                            <ShieldCheck className="h-4 w-4" aria-hidden />
                          </IconButton>
                        )}
                      </div>
                    </Td>
                  </tr>
                );
              })
            )}
          </tbody>
        </table>
      </div>

      {/* Mobile cards */}
      <div className="min-h-0 flex-1 overflow-y-auto md:hidden">
        <MobileCardShell
          loading={loading}
          error={error}
          empty={rows.length === 0}
          emptyMessage={emptyMessage}
          errorMessage="No se pudieron cargar los pendientes de facturación."
        >
          {rows.map((r) => {
            const f = r.fields;
            const monto = parseMoney(f.Monto_CP);
            const moneda = inferRowCurrency(r);
            const isInterna = f.EsFacturaInterna_CP === 'SI';
            // See desktop variant — placeholder monto on usado FIs
            // is hidden until administración cargas the real value.
            const usadoPendingMonto =
              isInterna && r.facturaInternaUsado && f.Facturado_CP !== 'SI';
            const tipo = isInterna
              ? 'Factura Interna'
              : f.EsPagoFinal_CP === 'Si'
                ? 'Pago Final'
                : 'Anticipo';
            const novaPath = hideStageLabel && !isInterna;
            const novaAnticipadaCommitted = novaPath
              && Boolean(f.FacturaAnticipadaFileName_CP?.trim());
            return (
              <li key={r.id} className="flex flex-col gap-2 px-4 py-3">
                <div className="flex items-start justify-between gap-2">
                  <span className="truncate text-sm font-medium text-foreground">
                    {r.clienteRO || f.Cliente_CP || '—'}
                  </span>
                  {novaPath ? (
                    novaAnticipadaCommitted && (
                      <span className="shrink-0 rounded-md bg-sky-100 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-sky-700">
                        Anticipo
                      </span>
                    )
                  ) : (
                    <span
                      className={cn(
                        'shrink-0 rounded-md px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide',
                        isInterna
                          ? 'bg-violet-100 text-violet-700'
                          : f.EsPagoFinal_CP === 'Si'
                            ? 'bg-emerald-100 text-emerald-700'
                            : 'bg-sky-100 text-sky-700',
                      )}
                    >
                      {tipo}
                    </span>
                  )}
                </div>
                <div className="text-xs text-muted-foreground">
                  {f.Fecha_CP || '—'} · <CajaNombre caja={f.MedioPago_CP} soloMarca />
                </div>
                <div className="flex items-center justify-between gap-2">
                  <span className="text-base font-semibold text-foreground">
                    {!usadoPendingMonto && monto != null
                      ? formatMoney(monto, moneda)
                      : '—'}{' '}
                    <span className="text-xs font-normal text-muted-foreground">
                      {moneda}
                    </span>
                  </span>
                  <div className="inline-flex items-center gap-1">
                    <IconButton onClick={() => onView(r)} label="Ver detalles" tone="neutral">
                      <Eye className="h-4 w-4" aria-hidden />
                    </IconButton>
                    <IconButton
                      onClick={() => onOpenVenta(r)}
                      label="Abrir venta"
                      tone="neutral"
                      disabled={!r.fields.IDOperacion_CP}
                    >
                      <Receipt className="h-4 w-4" aria-hidden />
                    </IconButton>
                    {!isReadOnly && (
                      <IconButton onClick={() => onFacturar(r)} label="Facturar" tone="success">
                        <ShieldCheck className="h-4 w-4" aria-hidden />
                      </IconButton>
                    )}
                  </div>
                </div>
              </li>
            );
          })}
        </MobileCardShell>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Cuentas por Pagar — reuses Compras list data filtered to pending statuses
// ---------------------------------------------------------------------------

/**
 * Cuentas por Cobrar — read-only queue of `ComprobantePagos` rows that
 * clients committed to pay (`Status_P='Pendiente de Cobro'`). Matches
 * PowerApps Screen_Administracion line 1060; no per-row mutations
 * because confirmation happens via the Cobros a Confirmar flow after
 * the client actually pays.
 */
function CuentasPorCobrarTable({
  rows,
  loading,
  error,
  searchActive,
  onView,
  onOpenVenta,
}: {
  rows: ComprobanteAdminItem[];
  loading: boolean;
  error: boolean;
  searchActive: boolean;
  onView: (row: ComprobanteAdminItem) => void;
  onOpenVenta: (row: ComprobanteAdminItem) => void;
}) {
  const emptyMessage = searchActive
    ? 'Sin coincidencias para la búsqueda.'
    : 'No hay promesas de pago pendientes.';
  return (
    <div className="flex min-h-0 flex-1 flex-col overflow-hidden rounded-2xl border bg-card">
      {/* Desktop / tablet table */}
      <div className="hidden min-h-0 flex-1 overflow-y-auto md:block">
        <table className="w-full text-sm">
          <thead className="sticky top-0 z-10 border-b bg-card text-left text-xs text-muted-foreground">
            <tr>
              <Th>Fecha</Th>
              <Th>Cliente</Th>
              <Th>Caja</Th>
              <Th className="text-right">Monto</Th>
              <Th>Tipo</Th>
              <Th className="w-28 text-right">Acciones</Th>
            </tr>
          </thead>
          <tbody className="divide-y divide-border">
            {loading ? (
              <SkeletonRows cols={6} />
            ) : error ? (
              <RowStatus cols={6}>
                <span className="text-destructive">
                  No se pudieron cargar las cuentas por cobrar.
                </span>
              </RowStatus>
            ) : rows.length === 0 ? (
              <RowStatus cols={6}>{emptyMessage}</RowStatus>
            ) : (
              rows.map((r) => {
                const f = r.fields;
                const monto = parseMoney(f.Monto_CP);
                const moneda = inferRowCurrency(r);
                const tipo = f.EsPagoFinal_CP === 'Si' ? 'Pago Final' : 'Anticipo';
                return (
                  <tr key={r.id} className="transition-colors hover:bg-muted/30">
                    <Td className="text-muted-foreground">
                      {f.FechaCobro_CP || f.Fecha_CP || '—'}
                    </Td>
                    <Td>
                      <div className="font-medium text-foreground">
                        {r.clienteRO || f.Cliente_CP || '—'}
                      </div>
                      {f.IDOperacion_CP && (
                        <div className="text-[11px] text-muted-foreground">
                          Venta Nº {f.IDOperacion_CP}
                        </div>
                      )}
                    </Td>
                    <Td className="text-muted-foreground"><CajaNombre caja={f.MedioPago_CP} soloMarca /></Td>
                    <Td className="text-right font-semibold text-foreground">
                      {monto != null ? (
                        <span className="whitespace-nowrap">
                          {formatMoney(monto, moneda)}{' '}
                          <span className="text-xs font-normal text-muted-foreground">
                            {moneda}
                          </span>
                        </span>
                      ) : (
                        '—'
                      )}
                    </Td>
                    <Td>
                      <div className="inline-flex items-center gap-1">
                        <span
                          className={cn(
                            'inline-flex rounded-md px-2 py-1 text-[11px] font-semibold uppercase tracking-wide',
                            f.EsPagoFinal_CP === 'Si'
                              ? 'bg-emerald-100 text-emerald-700'
                              : 'bg-sky-100 text-sky-700',
                          )}
                        >
                          {tipo}
                        </span>
                        <PvTag fields={f} />
                      </div>
                    </Td>
                    <Td className="text-right">
                      <div className="inline-flex items-center gap-1">
                        <IconButton
                          onClick={() => onView(r)}
                          label="Ver detalles"
                          tone="neutral"
                        >
                          <Eye className="h-4 w-4" aria-hidden />
                        </IconButton>
                        <IconButton
                          onClick={() => onOpenVenta(r)}
                          label="Ver pagos de la venta"
                          tone="neutral"
                        >
                          <Receipt className="h-4 w-4" aria-hidden />
                        </IconButton>
                      </div>
                    </Td>
                  </tr>
                );
              })
            )}
          </tbody>
        </table>
      </div>

      {/* Mobile cards */}
      <div className="min-h-0 flex-1 overflow-y-auto md:hidden">
        <MobileCardShell
          loading={loading}
          error={error}
          empty={rows.length === 0}
          emptyMessage={emptyMessage}
          errorMessage="No se pudieron cargar las cuentas por cobrar."
        >
          {rows.map((r) => {
            const f = r.fields;
            const monto = parseMoney(f.Monto_CP);
            const moneda = inferRowCurrency(r);
            const tipo = f.EsPagoFinal_CP === 'Si' ? 'Pago Final' : 'Anticipo';
            return (
              <li key={r.id} className="flex flex-col gap-2 px-4 py-3">
                <div className="flex items-start justify-between gap-2">
                  <span className="truncate text-sm font-medium text-foreground">
                    {r.clienteRO || f.Cliente_CP || '—'}
                  </span>
                  <span className="flex shrink-0 items-center gap-1">
                    <span
                      className={cn(
                        'rounded-md px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide',
                        f.EsPagoFinal_CP === 'Si'
                          ? 'bg-emerald-100 text-emerald-700'
                          : 'bg-sky-100 text-sky-700',
                      )}
                    >
                      {tipo}
                    </span>
                    <PvTag fields={f} size="sm" />
                  </span>
                </div>
                <div className="text-xs text-muted-foreground">
                  {f.FechaCobro_CP || f.Fecha_CP || '—'} · <CajaNombre caja={f.MedioPago_CP} soloMarca />
                  {f.IDOperacion_CP && <> · Venta {f.IDOperacion_CP}</>}
                </div>
                <div className="flex items-center justify-between gap-2">
                  <span className="text-base font-semibold text-foreground">
                    {monto != null ? formatMoney(monto, moneda) : '—'}{' '}
                    <span className="text-xs font-normal text-muted-foreground">
                      {moneda}
                    </span>
                  </span>
                  <div className="inline-flex items-center gap-1">
                    <IconButton onClick={() => onView(r)} label="Ver detalles" tone="neutral">
                      <Eye className="h-4 w-4" aria-hidden />
                    </IconButton>
                    <IconButton
                      onClick={() => onOpenVenta(r)}
                      label="Ver pagos de la venta"
                      tone="neutral"
                    >
                      <Receipt className="h-4 w-4" aria-hidden />
                    </IconButton>
                  </div>
                </div>
              </li>
            );
          })}
        </MobileCardShell>
      </div>
    </div>
  );
}

function CuentasPorPagarTable({
  rows,
  loading,
  error,
  searchActive,
  onVer,
  onPagar,
}: {
  rows: CompraListItem[];
  loading: boolean;
  error: boolean;
  searchActive: boolean;
  onVer: (row: CompraListItem) => void;
  onPagar: (row: CompraListItem) => void;
}) {
  const isReadOnly = useIsReadOnly();
  const emptyMessage = searchActive
    ? 'Sin coincidencias para la búsqueda.'
    : 'No hay compras pendientes de pago.';
  return (
    <div className="flex min-h-0 flex-1 flex-col overflow-hidden rounded-2xl border bg-card">
      {/* Desktop / tablet table */}
      <div className="hidden min-h-0 flex-1 overflow-y-auto md:block">
        <table className="w-full text-sm">
          <thead className="sticky top-0 z-10 bg-muted text-left text-xs uppercase tracking-wide text-muted-foreground">
            <tr>
              <Th>Estado</Th>
              <Th>Fecha</Th>
              <Th>Proveedor</Th>
              <Th className="text-right">Total</Th>
              <Th className="text-right">Restante</Th>
              <Th className="w-28 text-right">Acciones</Th>
            </tr>
          </thead>
          <tbody className="divide-y divide-border">
            {loading ? (
              <SkeletonRows cols={6} />
            ) : error ? (
              <RowStatus cols={6}>
                <span className="text-destructive">No se pudieron cargar las compras.</span>
              </RowStatus>
            ) : rows.length === 0 ? (
              <RowStatus cols={6}>{emptyMessage}</RowStatus>
            ) : (
              rows.map((r) => {
                const f = r.fields;
                const moneda = currencyFromSp(f.Moneda_CO);
                const total = parseMoney(f.Total_CO) ?? 0;
                const restante = saldoRestante(r);
                const statusCls =
                  f.Status_CO === 'Pago Parcial'
                    ? 'bg-indigo-100 text-indigo-700'
                    : 'bg-amber-100 text-amber-700';
                return (
                  <tr key={r.id} className="transition-colors hover:bg-muted/30">
                    <Td>
                      <span
                        className={cn(
                          'inline-flex whitespace-nowrap rounded-md px-2 py-1 text-[11px] font-semibold uppercase tracking-wide',
                          statusCls,
                        )}
                      >
                        {f.Status_CO}
                      </span>
                    </Td>
                    <Td className="text-muted-foreground">{f.FechaComprobante_CO || '—'}</Td>
                    <Td>
                      <div className="flex flex-col leading-tight">
                        <span className="font-medium text-foreground">
                          {f.Proveedor_CO || '—'}
                        </span>
                        <span className="text-xs text-muted-foreground">
                          {f.LlevaFactura_CO === 'SI'
                            ? `${f.Comprobante_CO ?? ''} ${
                                f.NumeroComprobante_CO ?? ''
                              }`.trim() || '—'
                            : '—'}
                        </span>
                      </div>
                    </Td>
                    <Td className="text-right font-semibold text-foreground">
                      <span className="whitespace-nowrap">
                        {formatMoney(total, moneda).replace(/^US\$/, '$')}{' '}
                        <span className="text-xs font-normal text-muted-foreground">
                          {moneda}
                        </span>
                      </span>
                    </Td>
                    <Td
                      className={cn(
                        'text-right font-semibold',
                        restante > 0.01 ? 'text-amber-700' : 'text-emerald-700',
                      )}
                    >
                      <span className="whitespace-nowrap">
                        {formatMoney(restante, moneda).replace(/^US\$/, '$')}{' '}
                        <span className="text-xs font-normal text-muted-foreground">
                          {moneda}
                        </span>
                      </span>
                    </Td>
                    <Td className="text-right">
                      <div className="inline-flex items-center gap-1">
                        <IconButton onClick={() => onVer(r)} label="Ver detalles" tone="neutral">
                          <Eye className="h-4 w-4" aria-hidden />
                        </IconButton>
                        {!isReadOnly && (
                          <IconButton onClick={() => onPagar(r)} label="Pagar" tone="success">
                            <Stamp className="h-4 w-4" aria-hidden />
                          </IconButton>
                        )}
                      </div>
                    </Td>
                  </tr>
                );
              })
            )}
          </tbody>
        </table>
      </div>

      {/* Mobile cards */}
      <div className="min-h-0 flex-1 overflow-y-auto md:hidden">
        <MobileCardShell
          loading={loading}
          error={error}
          empty={rows.length === 0}
          emptyMessage={emptyMessage}
          errorMessage="No se pudieron cargar las compras."
        >
          {rows.map((r) => {
            const f = r.fields;
            const moneda = currencyFromSp(f.Moneda_CO);
            const total = parseMoney(f.Total_CO) ?? 0;
            const restante = saldoRestante(r);
            const statusCls =
              f.Status_CO === 'Pago Parcial'
                ? 'bg-indigo-100 text-indigo-700'
                : 'bg-amber-100 text-amber-700';
            return (
              <li key={r.id} className="flex flex-col gap-2 px-4 py-3">
                <div className="flex items-start justify-between gap-2">
                  <span className="truncate text-sm font-medium text-foreground">
                    {f.Proveedor_CO || '—'}
                  </span>
                  <span
                    className={cn(
                      'shrink-0 rounded-md px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide',
                      statusCls,
                    )}
                  >
                    {f.Status_CO}
                  </span>
                </div>
                <div className="text-xs text-muted-foreground">
                  {f.FechaComprobante_CO || '—'} · {f.Comprobante_CO}{' '}
                  {f.NumeroComprobante_CO ?? ''}
                </div>
                <div className="grid grid-cols-2 gap-2 text-sm">
                  <div>
                    <div className="text-[10px] uppercase tracking-wide text-muted-foreground">
                      Total
                    </div>
                    <div className="font-semibold text-foreground">
                      {formatMoney(total, moneda)}
                    </div>
                  </div>
                  <div className="text-right">
                    <div className="text-[10px] uppercase tracking-wide text-muted-foreground">
                      Restante
                    </div>
                    <div
                      className={cn(
                        'font-semibold',
                        restante > 0.01 ? 'text-amber-700' : 'text-emerald-700',
                      )}
                    >
                      {formatMoney(restante, moneda)}
                    </div>
                  </div>
                </div>
                <div className="flex items-center justify-end gap-1.5">
                  <IconButton onClick={() => onVer(r)} label="Ver detalles" tone="neutral">
                    <Eye className="h-4 w-4" aria-hidden />
                  </IconButton>
                  {!isReadOnly && (
                    <IconButton onClick={() => onPagar(r)} label="Pagar" tone="success">
                      <Stamp className="h-4 w-4" aria-hidden />
                    </IconButton>
                  )}
                </div>
              </li>
            );
          })}
        </MobileCardShell>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// MX Nova — Cobros queue
// ---------------------------------------------------------------------------

/**
 * Lightweight queue for the MX Nova flow. Each row is an Anticipo
 * (Cuentas por Cobrar - Nova, Pendiente de Confirmacion) waiting on
 * Nova rol's cobro confirmation. Simpler than the AR CobrosTable —
 * no multi-select, no inventory side effects, just a confirm action
 * per row.
 *
 * Intentionally NO "Ver venta" action: Nova is an external rol and
 * shouldn't see Fornitalia-side venta detail pages. They confirm
 * cobros against the metadata exposed in this row (cliente,
 * cotización, caja origen, monto, fecha) and nothing more. PR 3.3.e.1.
 */
function NovaCobrosTable({
  rows,
  loading,
  error,
  searchActive,
  isReadOnly = false,
  selected,
  onToggle,
  onToggleAll,
  onView,
}: {
  rows: CobroNovaItem[];
  loading: boolean;
  error: boolean;
  searchActive: boolean;
  /** When true, the checkbox column is hidden so non-Nova viewers
   *  (administracion role) can't queue rows for a bulk confirm they
   *  aren't allowed to fire. The footer's Confirmar button is gated by
   *  the same flag at the page level. */
  isReadOnly?: boolean;
  selected: Set<string>;
  onToggle: (id: string) => void;
  onToggleAll: (allSelected: boolean) => void;
  onView: (row: CobroNovaItem) => void;
}) {
  const allSelected = rows.length > 0 && rows.every((r) => selected.has(r.id));
  const emptyMessage = searchActive
    ? 'Sin coincidencias para la búsqueda.'
    : 'Sin anticipos pendientes.';
  return (
    <div className="flex min-h-0 flex-1 flex-col overflow-hidden rounded-2xl border bg-card">
      {/* Desktop / tablet table. Mirrors the Fornitalia CobrosTable
          layout: checkbox column leftmost (hidden in read-only mode),
          sticky header, hover rows, no per-row Confirmar — bulk
          confirm lives in the footer. */}
      <div className="hidden min-h-0 flex-1 overflow-y-auto md:block">
        <table className="w-full text-sm">
          <thead className="sticky top-0 z-10 border-b bg-card text-left text-xs text-muted-foreground">
            <tr>
              {!isReadOnly && (
                <Th className="w-10">
                  <CheckboxCell
                    checked={allSelected}
                    onChange={() => onToggleAll(allSelected)}
                    disabled={rows.length === 0}
                    aria-label="Seleccionar todos"
                  />
                </Th>
              )}
              <Th>Fecha</Th>
              <Th>Cliente</Th>
              <Th>Cotización</Th>
              <Th>Caja origen</Th>
              <Th className="text-right">Monto</Th>
              <Th className="w-16 text-right">Acciones</Th>
            </tr>
          </thead>
          <tbody className="divide-y divide-border">
            {loading ? (
              <SkeletonRows cols={isReadOnly ? 6 : 7} />
            ) : error ? (
              <RowStatus cols={isReadOnly ? 6 : 7}>
                <span className="text-destructive">
                  No se pudo cargar la cola de Cobros Nova.
                </span>
              </RowStatus>
            ) : rows.length === 0 ? (
              <RowStatus cols={isReadOnly ? 6 : 7}>{emptyMessage}</RowStatus>
            ) : (
              rows.map((r) => {
                const cotizacion =
                  r.nro_cotizacion ||
                  (r.sales_header_sharepoint_id != null
                    ? `#${r.sales_header_sharepoint_id}`
                    : '—');
                return (
                  <tr key={r.id} className="transition-colors hover:bg-muted/30">
                    {!isReadOnly && (
                      <Td>
                        <CheckboxCell
                          checked={selected.has(r.id)}
                          onChange={() => onToggle(r.id)}
                          aria-label={`Seleccionar cobro ${r.id}`}
                        />
                      </Td>
                    )}
                    <Td className="text-muted-foreground">
                      {r.fecha_creacion || '—'}
                    </Td>
                    <Td className="font-medium text-foreground">
                      {r.cliente || '—'}
                    </Td>
                    <Td className="text-muted-foreground">{cotizacion}</Td>
                    <Td className="text-foreground">
                      {r.medio_pago_origen || '—'}
                    </Td>
                    <Td className="text-right font-semibold text-foreground">
                      <span className="whitespace-nowrap">
                        {formatMoney(r.monto, 'MXN')}{' '}
                        <span className="text-xs font-normal text-muted-foreground">
                          MXN
                        </span>
                      </span>
                    </Td>
                    <Td className="text-right">
                      <IconButton
                        onClick={() => onView(r)}
                        label="Ver comprobante"
                        tone="neutral"
                      >
                        <Eye className="h-4 w-4" aria-hidden />
                      </IconButton>
                    </Td>
                  </tr>
                );
              })
            )}
          </tbody>
        </table>
      </div>

      {/* Mobile cards */}
      <div className="min-h-0 flex-1 overflow-y-auto md:hidden">
        <MobileCardShell
          loading={loading}
          error={error}
          empty={rows.length === 0}
          emptyMessage={emptyMessage}
          errorMessage="No se pudo cargar la cola de Cobros Nova."
        >
          {rows.map((r) => {
            const isSelected = selected.has(r.id);
            const cotizacion =
              r.nro_cotizacion ||
              (r.sales_header_sharepoint_id != null
                ? `#${r.sales_header_sharepoint_id}`
                : '—');
            return (
              <li
                key={r.id}
                className={cn(
                  'flex flex-col gap-2 px-4 py-3 transition-colors',
                  isSelected && !isReadOnly && 'bg-emerald-50/60',
                )}
              >
                <div className="flex items-start gap-3">
                  {!isReadOnly && (
                    <CheckboxCell
                      checked={isSelected}
                      onChange={() => onToggle(r.id)}
                      aria-label={`Seleccionar cobro ${r.id}`}
                    />
                  )}
                  <div className="min-w-0 flex-1">
                    <span className="block truncate text-sm font-medium text-foreground">
                      {r.cliente || '—'}
                    </span>
                    <div className="mt-0.5 text-xs text-muted-foreground">
                      {r.fecha_creacion || '—'} · {r.medio_pago_origen || '—'}
                    </div>
                    <div className="mt-0.5 text-xs text-muted-foreground">
                      Cotización {cotizacion}
                    </div>
                  </div>
                </div>
                <div
                  className={cn(
                    'flex items-center justify-between gap-2',
                    !isReadOnly && 'pl-8',
                  )}
                >
                  <span className="text-base font-semibold text-foreground">
                    {formatMoney(r.monto, 'MXN')}{' '}
                    <span className="text-xs font-normal text-muted-foreground">
                      MXN
                    </span>
                  </span>
                  <IconButton
                    onClick={() => onView(r)}
                    label="Ver comprobante"
                    tone="neutral"
                  >
                    <Eye className="h-4 w-4" aria-hidden />
                  </IconButton>
                </div>
              </li>
            );
          })}
        </MobileCardShell>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Inter-co Pagar Nova / Cobrar Fornitalia (PR 3.3.e.3)
// ---------------------------------------------------------------------------

function filterInterCo(rows: InterCoMovementItem[], search: string): InterCoMovementItem[] {
  const q = search.trim().toLowerCase();
  if (!q) return rows;
  return rows.filter((r) => {
    return (
      (r.cliente ?? '').toLowerCase().includes(q) ||
      (r.nro_cotizacion ?? '').toLowerCase().includes(q) ||
      (r.descripcion ?? '').toLowerCase().includes(q)
    );
  });
}

/**
 * Shared table for Cuentas por Pagar - Nova and Cuentas por Cobrar -
 * Fornitalia. The two queues are structurally identical; only the
 * Cobrar variant honours `cobro_locked` (Lógica de Freno) — when true
 * the Confirmar button is disabled with a tooltip pointing to the
 * Pagar leg. Pagar variant ignores the lock entirely.
 */
function InterCoTable({
  variant,
  rows,
  loading,
  error,
  searchActive,
  isReadOnly,
  onConfirm,
  onView,
}: {
  variant: 'pagar' | 'cobrar';
  rows: InterCoMovementItem[];
  loading: boolean;
  error: boolean;
  searchActive: boolean;
  isReadOnly: boolean;
  onConfirm: (row: InterCoMovementItem) => void;
  onView: (row: InterCoMovementItem) => void;
}) {
  if (loading) {
    return (
      <div className="flex-1 rounded-2xl border bg-card p-6">
        <Skeleton className="h-6 w-1/3" />
        <Skeleton className="mt-4 h-12 w-full" />
        <Skeleton className="mt-3 h-12 w-full" />
        <Skeleton className="mt-3 h-12 w-full" />
      </div>
    );
  }
  if (error) {
    return (
      <div className="flex-1 rounded-2xl border border-destructive/30 bg-destructive/5 p-10 text-center text-sm text-destructive">
        No se pudo cargar la cola.
      </div>
    );
  }
  if (rows.length === 0) {
    const empty =
      variant === 'pagar'
        ? 'Sin cuentas por pagar pendientes.'
        : 'Sin cuentas por cobrar pendientes.';
    return (
      <div className="flex-1 rounded-2xl border border-dashed bg-card/40 p-10 text-center text-sm text-muted-foreground">
        {searchActive ? 'Sin resultados.' : empty}
      </div>
    );
  }
  return (
    <div className="flex-1 overflow-auto rounded-2xl border bg-card">
      <table className="min-w-full divide-y divide-border text-sm">
        <thead className="bg-secondary/40 text-xs uppercase tracking-wide text-muted-foreground">
          <tr>
            <th className="px-4 py-2 text-left">Cliente</th>
            <th className="px-4 py-2 text-left">Cotización</th>
            <th className="px-4 py-2 text-left">Descripción</th>
            <th className="px-4 py-2 text-right">Monto</th>
            <th className="px-4 py-2 text-left">Fecha</th>
            <th className="w-44 px-4 py-2 text-right">Acciones</th>
          </tr>
        </thead>
        <tbody className="divide-y divide-border">
          {rows.map((r) => {
            const locked = variant === 'cobrar' && r.cobro_locked;
            return (
              <tr key={r.id} className="hover:bg-secondary/20">
                <td className="px-4 py-3 font-medium text-foreground">
                  {r.cliente || '—'}
                </td>
                <td className="px-4 py-3 text-muted-foreground">
                  {r.nro_cotizacion || (r.sales_header_sharepoint_id != null
                    ? `#${r.sales_header_sharepoint_id}`
                    : '—')}
                </td>
                <td
                  className="max-w-[280px] truncate px-4 py-3 text-muted-foreground"
                  title={r.descripcion ?? undefined}
                >
                  {r.descripcion || '—'}
                </td>
                <td className="px-4 py-3 text-right font-semibold text-foreground">
                  {formatMoney(r.monto, (r.currency as 'ARS' | 'MXN' | 'USD') || 'MXN')}
                </td>
                <td className="px-4 py-3 text-muted-foreground">{r.fecha || '—'}</td>
                <td className="px-4 py-3">
                  <div className="flex items-center justify-end gap-2">
                    <IconButton
                      onClick={() => onView(r)}
                      label="Ver comprobante"
                      tone="neutral"
                    >
                      <Eye className="h-4 w-4" aria-hidden />
                    </IconButton>
                    {locked ? (
                      <span
                        className="inline-flex items-center gap-1 whitespace-nowrap rounded-md bg-amber-100 px-2 py-1 text-xs font-medium text-amber-800"
                        title="Esperando que Nova confirme su Cuenta por Pagar"
                      >
                        <Lock className="h-3.5 w-3.5" aria-hidden />
                        Esperando Nova
                      </span>
                    ) : (
                      !isReadOnly && (
                        <IconButton
                          onClick={() => onConfirm(r)}
                          label="Confirmar"
                          tone="success"
                        >
                          <Check className="h-4 w-4" aria-hidden />
                        </IconButton>
                      )
                    )}
                  </div>
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Footer
// ---------------------------------------------------------------------------

function FooterTotals({
  totalPesos,
  totalDolares,
  count,
  selectedCount,
  onConfirmar,
  showConfirmar,
  pesosLabel = 'Total Pesos',
  dolaresLabel = 'Total Dólares',
  pesosCurrency = 'ARS',
  hideDolares = false,
}: {
  totalPesos: number;
  totalDolares: number;
  count: number;
  selectedCount: number;
  onConfirmar: () => void;
  showConfirmar: boolean;
  /** Label override for the local-currency pill (default "Total Pesos").
   *  Nova mode uses "Total MXN" because all Nova rows are MXN;
   *  Cuentas por Pagar usa "Restante" porque suma el saldo, no el total. */
  pesosLabel?: string;
  dolaresLabel?: string;
  /** Currency formatter for the local-currency pill. Default 'ARS'. */
  pesosCurrency?: 'ARS' | 'MXN';
  /** Hide the USD pill when the mode is single-currency (Nova all-MXN). */
  hideDolares?: boolean;
}) {
  return (
    <div className="flex shrink-0 flex-wrap items-center gap-3 rounded-2xl border bg-card px-4 py-3">
      {/* Totals on the left, Confirmar on the right — matches the
          standard form pattern (info first, primary action last) and
          puts the action button in the natural thumb zone. */}
      <div className="flex flex-wrap items-center gap-2">
        <TotalPill label={pesosLabel}>{formatMoney(totalPesos, pesosCurrency)}</TotalPill>
        {!hideDolares && (
          <TotalPill label={dolaresLabel}>{formatMoney(totalDolares, 'USD')}</TotalPill>
        )}
        <TotalPill label="Total">{count}</TotalPill>
      </div>
      {showConfirmar && (
        <Button
          onClick={onConfirmar}
          disabled={selectedCount === 0}
          className="ml-auto h-10 gap-2 bg-foreground text-background hover:bg-foreground/90"
        >
          <Check className="h-4 w-4" aria-hidden />
          Confirmar
          {selectedCount > 0 ? ` (${selectedCount})` : ''}
        </Button>
      )}
    </div>
  );
}

function TotalPill({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <span className="inline-flex h-10 items-center gap-2 rounded-lg border bg-secondary px-3">
      {/* Label uses the gallery header treatment (text-xs uppercase
          tracking-wide) so the pill reads as a row of mini-headers
          with their values inline. */}
      <span className="text-xs uppercase tracking-wide text-muted-foreground">{label}</span>
      <span className="text-sm font-semibold tabular-nums text-foreground">{children}</span>
    </span>
  );
}

// ---------------------------------------------------------------------------
// Dialogs
// ---------------------------------------------------------------------------

function ViewComprobanteDialog({
  row,
  onClose,
}: {
  row: ComprobanteAdminItem;
  onClose: () => void;
}) {
  const f = row.fields;
  const moneda = inferRowCurrency(row);
  const monto = parseMoney(f.Monto_CP);
  const pesos = parseMoney(f.Pesos_CP);
  const tc = parseMoney(f.TipoCambio_CP);
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle>Comprobante #{row.id}</DialogTitle>
          <DialogDescription>
            Operación Nº {f.IDOperacion_CP ?? '—'} · {row.clienteRO || f.Cliente_CP || '—'}
          </DialogDescription>
        </DialogHeader>
        <DialogBody className="space-y-3 text-sm">
          <Field label="Medio de pago" value={etiquetaCaja(f.MedioPago_CP ?? '')} />
          <div className="grid grid-cols-2 gap-3">
            <Field
              label="Monto"
              value={monto != null ? formatMoney(monto, moneda) : '—'}
            />
            <Field
              label="Tipo de cambio"
              value={tc != null ? tc.toLocaleString('es-AR') : '—'}
            />
          </div>
          {pesos != null && pesos > 0 && (
            <Field
              label="Equivalente"
              value={formatMoney(pesos, moneda === 'USD' ? 'ARS' : 'USD')}
            />
          )}
          <div className="grid grid-cols-3 gap-3">
            <Field label="Fecha" value={f.Fecha_CP} />
            <Field label="Fecha cobro" value={f.FechaCobro_CP} />
            <Field label="Fecha pago" value={f.FechaPago_CP} />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <Field label="Tipo" value={f.EsPagoFinal_CP === 'Si' ? 'Pago Final' : 'Anticipo'} />
            <Field label="Factura" value={f.ContieneFactura_CP === 'SI' ? 'Sí' : 'No'} />
          </div>
          {f.NroFactura_CP && <Field label="Nº factura" value={f.NroFactura_CP} />}

          <div className="space-y-1.5">
            <p className="text-[11px] font-medium uppercase tracking-wide text-muted-foreground">
              Comprobante de pago
            </p>
            {f.ComprobanteDriveItemID_CP && f.ComprobanteFileName_CP ? (
              <FileLink
                entityType="ComprobanteVenta"
                entityId={row.id}
                fileName={f.ComprobanteFileName_CP}
                allowDelete={false}
              />
            ) : (
              <p className="rounded-lg border border-dashed bg-card/40 px-3 py-2 text-xs text-muted-foreground">
                Sin comprobante adjunto.
              </p>
            )}
          </div>

          {f.FacturaDriveItemID_CP && f.FacturaFileName_CP && (
            <div className="space-y-1.5">
              <p className="text-[11px] font-medium uppercase tracking-wide text-muted-foreground">
                Factura emitida
              </p>
              <FileLink
                entityType="FacturaVenta"
                entityId={row.id}
                fileName={f.FacturaFileName_CP}
                allowDelete={false}
              />
            </div>
          )}
        </DialogBody>
        <DialogFooter>
          <Button type="button" onClick={onClose}>
            Cerrar
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function Field({ label, value }: { label: string; value?: string | null }) {
  return (
    <div className="space-y-0.5">
      <p className="text-[11px] font-medium uppercase tracking-wide text-muted-foreground">
        {label}
      </p>
      <p className="text-sm text-foreground">{value && value.trim() ? value : '—'}</p>
    </div>
  );
}

/**
 * Read-only preview for a Nova "Cobros a Confirmar" row. Lets the Nova
 * approver see the comprobante de pago PDF that the Fornitalia operator
 * uploaded when registering the anticipo, before deciding whether to
 * confirm via `confirmarCobroNova`.
 */
function ViewCobroNovaDialog({
  row,
  onClose,
}: {
  row: CobroNovaItem;
  onClose: () => void;
}) {
  const cotizacion =
    row.nro_cotizacion ||
    (row.sales_header_sharepoint_id != null
      ? `#${row.sales_header_sharepoint_id}`
      : '—');
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle>Comprobante Nova #{row.id}</DialogTitle>
          <DialogDescription>
            Cotización {cotizacion} · {row.cliente || '—'}
          </DialogDescription>
        </DialogHeader>
        <DialogBody className="space-y-3 text-sm">
          <div className="grid grid-cols-2 gap-3">
            <Field
              label="Monto"
              value={row.monto != null ? formatMoney(row.monto, 'MXN') : '—'}
            />
            <Field label="Caja origen" value={row.medio_pago_origen} />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <Field label="Fecha" value={row.fecha_creacion} />
            <Field label="Estado" value={row.status} />
          </div>
          <div className="space-y-1.5">
            <p className="text-[11px] font-medium uppercase tracking-wide text-muted-foreground">
              Comprobante de pago
            </p>
            {row.comprobante_sp_id &&
            row.comprobante_drive_item_id &&
            row.comprobante_file_name ? (
              <FileLink
                entityType="ComprobanteVenta"
                // SP id of the operator's originating receipt — that's
                // the row that actually owns the drive item id. Falling
                // back to `row.id` (Anticipo's own SP id) would 404 at
                // the download endpoint because the Anticipo row has
                // `ComprobanteDriveItemID_CP` null.
                entityId={row.comprobante_sp_id}
                fileName={row.comprobante_file_name}
                allowDelete={false}
              />
            ) : (
              <p className="rounded-lg border border-dashed bg-card/40 px-3 py-2 text-xs text-muted-foreground">
                Sin comprobante adjunto.
              </p>
            )}
          </div>
        </DialogBody>
        <DialogFooter>
          <Button type="button" onClick={onClose}>
            Cerrar
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

/**
 * Read-only preview for an inter-co Pagar/Cobrar row. The document
 * shown is the Factura Interna PDF on the receipt referenced by
 * `id_comprobante_pago_sp` (that receipt has `es_factura_interna=true`
 * and its `factura_*` columns hold the invoice). Nova validates this
 * PDF before confirming Pagar; Fornitalia re-checks before Cobrar.
 */
function ViewInterCoDialog({
  row,
  onClose,
}: {
  row: InterCoMovementItem;
  onClose: () => void;
}) {
  const cotizacion =
    row.nro_cotizacion ||
    (row.sales_header_sharepoint_id != null
      ? `#${row.sales_header_sharepoint_id}`
      : '—');
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle>Inter-compañía #{row.id}</DialogTitle>
          <DialogDescription>
            Cotización {cotizacion} · {row.cliente || '—'}
          </DialogDescription>
        </DialogHeader>
        <DialogBody className="space-y-3 text-sm">
          <div className="grid grid-cols-2 gap-3">
            <Field
              label="Monto"
              value={formatMoney(
                row.monto,
                (row.currency as 'ARS' | 'MXN' | 'USD') || 'MXN',
              )}
            />
            <Field label="Fecha" value={row.fecha} />
          </div>
          {row.descripcion && (
            <Field label="Descripción" value={row.descripcion} />
          )}
          <div className="space-y-1.5">
            <p className="text-[11px] font-medium uppercase tracking-wide text-muted-foreground">
              Factura interna
            </p>
            {row.id_comprobante_pago_sp != null &&
            row.factura_drive_item_id &&
            row.factura_file_name ? (
              <FileLink
                entityType="FacturaVenta"
                entityId={String(row.id_comprobante_pago_sp)}
                fileName={row.factura_file_name}
                allowDelete={false}
              />
            ) : (
              <p className="rounded-lg border border-dashed bg-card/40 px-3 py-2 text-xs text-muted-foreground">
                Sin factura adjunta.
              </p>
            )}
          </div>
          {/* Comprobante de pago Nova attached when confirming the
              Pagar leg. Same FileLink for both sides: Pagar reads it
              off the row directly, Cobrar gets it from the paired
              Pagar via the JOIN in `listInterCoQueue`. */}
          <div className="space-y-1.5">
            <p className="text-[11px] font-medium uppercase tracking-wide text-muted-foreground">
              Comprobante de pago (Nova)
            </p>
            {row.comprobante_owner_sp_id != null &&
            row.comprobante_drive_item_id &&
            row.comprobante_file_name ? (
              <FileLink
                entityType="ComprobanteInterCo"
                entityId={String(row.comprobante_owner_sp_id)}
                fileName={row.comprobante_file_name}
                allowDelete={false}
              />
            ) : (
              <p className="rounded-lg border border-dashed bg-card/40 px-3 py-2 text-xs text-muted-foreground">
                Aún no se adjuntó. Nova lo carga al confirmar el pago.
              </p>
            )}
          </div>
        </DialogBody>
        <DialogFooter>
          <Button type="button" onClick={onClose}>
            Cerrar
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function ConfirmarCobrosDialog({
  rows,
  onClose,
  onAfter,
}: {
  rows: ComprobanteAdminItem[];
  onClose: () => void;
  onAfter: () => void;
}) {
  const qc = useQueryClient();
  const mutation = useMutation({
    mutationFn: () => confirmarCobros(rows.map((r) => r.id)),
    onSuccess: (response) => {
      const confirmadosTotal = response.results.reduce(
        (sum, r) => sum + r.confirmados.length,
        0,
      );
      const skippedTotal =
        response.notFound.length +
        response.alreadyProcessed.length +
        response.results.reduce((s, r) => s + r.skipped.length, 0);
      if (confirmadosTotal > 0) {
        toast.success(`${confirmadosTotal} cobro(s) confirmado(s)`, {
          description:
            skippedTotal > 0
              ? `${skippedTotal} no se procesaron — revisá la tabla.`
              : undefined,
        });
      } else {
        toast.info('No se procesó ningún cobro', {
          description: 'Es posible que ya hayan sido confirmados por otra sesión.',
        });
      }
      qc.invalidateQueries({ queryKey: ['administracion'] });
      qc.invalidateQueries({ queryKey: ['ventas'] });
      qc.invalidateQueries({ queryKey: ['detalle-venta'] });
      qc.invalidateQueries({ queryKey: ['home', 'operaciones'] });
      onAfter();
      onClose();
    },
    onError: (err) =>
      toast.error('No se pudieron confirmar los cobros', {
        description: err instanceof Error ? err.message : 'Error desconocido',
      }),
  });

  const byOperacion = useMemo(() => {
    const map = new Map<string, ComprobanteAdminItem[]>();
    for (const r of rows) {
      const key = r.fields.IDOperacion_CP ?? '—';
      const list = map.get(key) ?? [];
      list.push(r);
      map.set(key, list);
    }
    return Array.from(map.entries());
  }, [rows]);

  // Mirrors PowerApps' `PagoContainsFactura` hint on `PopUp_ConfirmPagos`:
  // confirmed cobros with `ContieneFactura_CP='SI'` flow into the
  // Facturación tab afterwards for Nº de factura entry, so warn the
  // admin up front instead of leaving the next step implicit.
  const conFacturaCount = useMemo(
    () => rows.filter((r) => r.fields.ContieneFactura_CP === 'SI').length,
    [rows],
  );

  return (
    <Dialog open onOpenChange={(o) => !o && !mutation.isPending && onClose()}>
      <DialogContent
        className="max-w-lg"
        processing={mutation.isPending && 'Confirmando cobros…'}
      >
        <DialogHeader>
          <div className="flex items-start gap-3">
            <span className="mt-0.5 inline-flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-emerald-100 text-emerald-700">
              <ShieldCheck className="h-5 w-5" aria-hidden />
            </span>
            <div>
              <DialogTitle>Confirmar {rows.length} cobro(s)</DialogTitle>
              <DialogDescription>
                Cada venta avanzará a <span className="font-medium">Pago Parcial</span> o{' '}
                <span className="font-medium">Pendiente Despacho</span> según el total
                confirmado hasta el momento.
              </DialogDescription>
            </div>
          </div>
        </DialogHeader>
        <DialogBody className="space-y-3">
          <ul className="space-y-1.5 text-sm">
            {byOperacion.map(([opId, opRows]) => (
              <li
                key={opId}
                className="flex items-center justify-between rounded-lg border bg-secondary px-3 py-2"
              >
                <span>
                  Operación{' '}
                  <span className="font-medium text-foreground">#{opId}</span>
                </span>
                <span className="text-xs text-muted-foreground">
                  {opRows.length} cobro(s)
                </span>
              </li>
            ))}
          </ul>
          {conFacturaCount > 0 && (
            <div className="rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-900">
              {conFacturaCount === 1 ? (
                <>
                  <span className="font-medium">1 cobro</span> incluye factura.
                  Una vez confirmado, vas a poder cargar el Nº de factura en la
                  pestaña <span className="font-medium">Facturación</span>.
                </>
              ) : (
                <>
                  <span className="font-medium">{conFacturaCount} cobros</span>{' '}
                  incluyen factura. Una vez confirmados, vas a poder cargar el
                  Nº de cada factura en la pestaña{' '}
                  <span className="font-medium">Facturación</span>.
                </>
              )}
            </div>
          )}
        </DialogBody>
        <DialogFooter>
          <Button
            type="button"
            variant="outline"
            onClick={onClose}
            disabled={mutation.isPending}
          >
            Cancelar
          </Button>
          <Button
            type="button"
            onClick={() => mutation.mutate()}
            disabled={mutation.isPending}
          >
            Confirmar
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

/**
 * Confirmation dialog for a single Cuentas por Cobrar - Nova anticipo.
 *
 * Prevents misclicks + double-clicks on the table's "Confirmar" button
 * (the action is irreversible: it flips the anticipo to Confirmado AND
 * writes a Tesorería Ingreso + optional 5% comisión Egreso for
 * Herconstru-style cajas). The mutation lives inside this dialog so
 * `mutation.isPending` disables both action buttons in flight.
 */
function ConfirmarCobroNovaDialog({
  row,
  onClose,
}: {
  row: CobroNovaItem;
  onClose: () => void;
}) {
  const qc = useQueryClient();
  const { config: activeCountry } = useActiveCountry();
  const countryCode = activeCountry.code;
  const isHerconstru = row.medio_pago_origen === 'Herconstru';
  const mutation = useMutation({
    mutationFn: () => confirmarCobroNova(row.id),
    onSuccess: (result) => {
      const comisionNote = result.egresoId != null ? ' Comisión 5% registrada.' : '';
      toast.success(
        `Cobro confirmado en ${row.medio_pago_origen ?? '—'}.${comisionNote}`,
      );
      qc.invalidateQueries({ queryKey: ['administracion', countryCode, 'cobros-nova'] });
      onClose();
    },
    onError: (err) => {
      toast.error(err instanceof Error ? err.message : 'Error al confirmar');
    },
  });

  const cotizacionLabel =
    row.nro_cotizacion ??
    (row.sales_header_sharepoint_id != null
      ? `#${row.sales_header_sharepoint_id}`
      : '—');

  return (
    <Dialog open onOpenChange={(o) => !o && !mutation.isPending && onClose()}>
      <DialogContent
        className="max-w-md"
        processing={mutation.isPending && 'Confirmando cobro…'}
      >
        <DialogHeader>
          <div className="flex items-start gap-3">
            <span className="mt-0.5 inline-flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-emerald-100 text-emerald-700">
              <ShieldCheck className="h-5 w-5" aria-hidden />
            </span>
            <div>
              <DialogTitle>Confirmar cobro Nova</DialogTitle>
              <DialogDescription>
                Se registrará un Ingreso en Tesorería sobre la caja{' '}
                <span className="font-medium text-foreground">
                  {row.medio_pago_origen ?? '—'}
                </span>
                {isHerconstru
                  ? ' y un Egreso automático de comisión (5%).'
                  : '.'}
              </DialogDescription>
            </div>
          </div>
        </DialogHeader>
        <DialogBody className="space-y-3 text-sm">
          <div className="space-y-2">
            <ConfirmarCobroNovaRow label="Cliente" value={row.cliente ?? '—'} />
            <ConfirmarCobroNovaRow label="Cotización" value={cotizacionLabel} />
            <ConfirmarCobroNovaRow
              label="Caja origen"
              value={row.medio_pago_origen ?? '—'}
            />
            <ConfirmarCobroNovaRow label="Monto" value={formatMoney(row.monto, 'MXN')} />
          </div>
          <FiscalDataBlock data={row.fiscal_data} />
        </DialogBody>
        <DialogFooter>
          <Button
            type="button"
            variant="outline"
            onClick={onClose}
            disabled={mutation.isPending}
          >
            Cancelar
          </Button>
          <Button
            type="button"
            onClick={() => mutation.mutate()}
            disabled={mutation.isPending}
          >
            Confirmar
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function ConfirmarCobroNovaRow({ label, value }: { label: string; value: string }) {
  return (
    <div className="flex items-center justify-between gap-2 border-b border-border/60 pb-1.5 last:border-b-0 last:pb-0">
      <span className="text-muted-foreground">{label}</span>
      <span className="font-medium text-foreground">{value}</span>
    </div>
  );
}

/**
 * Receptor fiscal data block shown on every Nova confirm dialog so the
 * rol has the data needed to stamp the CFDI 4.0 factura without
 * leaving the queue. Returns null when there's no linked contacto
 * (legacy ventas with a text-only cliente) — the dialog still confirms
 * but the operator must look the data up elsewhere.
 */
function FiscalDataBlock({
  data,
  cols = 1,
}: {
  data: NovaFiscalData | null;
  /** 2 → two-fields-per-row grid (wide MX facturar dialog). 1 → stacked rows
   *  (the narrow Nova cobro-confirm dialog keeps its original look). */
  cols?: 1 | 2;
}) {
  if (!data) {
    return (
      <div className="rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-900">
        Sin datos fiscales del contacto. Verificá los datos del cliente antes
        de emitir la factura.
      </div>
    );
  }
  const usoCfdiLabel = (() => {
    if (!data.uso_cfdi) return '—';
    const found = USOS_CFDI.find((u) => u.code === data.uso_cfdi);
    return found ? `${found.code} — ${found.label}` : data.uso_cfdi;
  })();
  const fields: Array<{ label: string; value: string; full?: boolean }> = [
    { label: 'RFC', value: data.rfc ?? '—' },
    { label: 'Razón Social', value: data.razon_social ?? '—' },
    { label: 'Régimen Fiscal', value: data.regimen_fiscal ?? '—' },
    { label: 'Código Postal', value: data.codigo_postal ?? '—' },
    { label: 'Uso del CFDI', value: usoCfdiLabel },
    ...(data.email ? [{ label: 'Email', value: data.email }] : []),
    ...(data.direccion_completa
      ? [{ label: 'Dirección', value: data.direccion_completa, full: true }]
      : []),
  ];
  return (
    <div className="rounded-lg border bg-secondary/30 px-3 py-2.5">
      <div className="mb-2 text-xs font-semibold uppercase tracking-wide text-muted-foreground">
        Datos de facturación
      </div>
      {cols === 2 ? (
        // Two fields per row (label on top) so the panel stays short — the
        // receptor block was the main reason the MX facturar dialog scrolled.
        // Dirección spans the full width since it's the longest value.
        <div className="grid grid-cols-1 gap-x-6 gap-y-2 sm:grid-cols-2">
          {fields.map((f) => (
            <FiscalField key={f.label} label={f.label} value={f.value} full={f.full} />
          ))}
        </div>
      ) : (
        <div className="space-y-1.5">
          {fields.map((f) => (
            <ConfirmarCobroNovaRow key={f.label} label={f.label} value={f.value} />
          ))}
        </div>
      )}
    </div>
  );
}

/** Compact label-on-top field for the 2-col receptor fiscal grid. */
function FiscalField({
  label,
  value,
  full,
}: {
  label: string;
  value: string;
  full?: boolean;
}) {
  return (
    <div className={full ? 'sm:col-span-2' : undefined}>
      <div className="text-[11px] uppercase tracking-wide text-muted-foreground">
        {label}
      </div>
      <div className="break-words text-sm font-medium text-foreground">{value}</div>
    </div>
  );
}

/** Campos fiscales obligatorios para el CFDI que faltan (vacío = completo). */
function fiscalMissingLabels(data: NovaFiscalData | null): string[] {
  const req: Array<[keyof NovaFiscalData, string]> = [
    ['rfc', 'RFC'],
    ['razon_social', 'Razón Social'],
    ['regimen_fiscal', 'Régimen Fiscal'],
    ['codigo_postal', 'Código Postal'],
    ['uso_cfdi', 'Uso del CFDI'],
  ];
  if (!data) return req.map(([, label]) => label);
  return req.filter(([k]) => !String(data[k] ?? '').trim()).map(([, label]) => label);
}

/**
 * Panel READ-ONLY de datos fiscales del receptor en el diálogo de Facturación
 * (MX). Nova necesita estos datos para el CFDI y hoy los tiene que pedir al
 * vendedor; acá los ve directo. Si faltan obligatorios, avisa que los complete
 * el vendedor — Nova NO edita el contacto (decisión de producto).
 */
/** The amber "faltan datos fiscales" alert. Split out of ReceptorFiscalPanel so
 *  it can be rendered elsewhere (e.g. the Factura Interna desglose side column,
 *  where the fiscal data stays inline but the warning fills the aside). */
function FiscalMissingWarning({ data }: { data: NovaFiscalData | null }) {
  const faltan = fiscalMissingLabels(data);
  if (faltan.length === 0) return null;
  return (
    <div className="rounded-lg border border-amber-300 bg-amber-50 px-3 py-2 text-xs leading-relaxed text-amber-900">
      <p className="font-medium">
        {data
          ? 'Faltan datos fiscales del cliente para el CFDI.'
          : 'El cliente no tiene datos fiscales cargados.'}
      </p>
      <p className="mt-0.5">
        {data ? `Faltan: ${faltan.join(', ')}. ` : ''}
        Avisá al vendedor para que los complete en Contactos — no se editan
        desde acá.
      </p>
    </div>
  );
}

function ReceptorFiscalPanel({ data }: { data: NovaFiscalData | null }) {
  return (
    <div className="space-y-2">
      {data && <FiscalDataBlock data={data} cols={2} />}
      <FiscalMissingWarning data={data} />
    </div>
  );
}

/**
 * Bulk confirmation dialog for multiple Cuentas por Cobrar - Nova rows.
 * Mirrors the Fornitalia `ConfirmarCobrosDialog` pattern but uses the
 * single-row `/cobros-nova/confirmar` endpoint in a loop — the Nova
 * endpoint doesn't accept a batch payload (each confirm needs its own
 * Tesorería side-effects to land on the right caja).
 *
 * Per-row failures are surfaced as toast counts rather than aborting
 * the whole batch — if 4 of 5 succeed, the user sees "4 confirmados, 1
 * falló" and can retry the failure separately.
 */
function ConfirmarCobrosNovaBulkDialog({
  rows,
  onClose,
  onAfter,
}: {
  rows: CobroNovaItem[];
  onClose: () => void;
  onAfter: () => void;
}) {
  const qc = useQueryClient();
  const { config: activeCountry } = useActiveCountry();
  const countryCode = activeCountry.code;
  const totalMxn = rows.reduce((s, r) => s + (r.monto ?? 0), 0);
  const herconstruCount = rows.filter((r) => r.medio_pago_origen === 'Herconstru').length;
  const mutation = useMutation({
    mutationFn: async () => {
      // Sequential — keeps the Tesorería side-effects linear, avoids
      // racing two Ingresos on the same caja, and the per-row error
      // handling stays simple. The N here is typically small (handful
      // of cobros per session).
      let ok = 0;
      const failed: Array<{ id: string; reason: string }> = [];
      for (const row of rows) {
        try {
          await confirmarCobroNova(row.id);
          ok += 1;
        } catch (err) {
          failed.push({
            id: row.id,
            reason: err instanceof Error ? err.message : 'Error desconocido',
          });
        }
      }
      return { ok, failed };
    },
    onSuccess: ({ ok, failed }) => {
      if (ok > 0) {
        toast.success(`${ok} cobro(s) confirmado(s)`, {
          description:
            failed.length > 0
              ? `${failed.length} no se procesaron — revisá la cola.`
              : undefined,
        });
      } else {
        toast.info('No se procesó ningún cobro', {
          description: failed[0]?.reason ?? undefined,
        });
      }
      qc.invalidateQueries({ queryKey: ['administracion', countryCode, 'cobros-nova'] });
      qc.invalidateQueries({ queryKey: ['administracion'] });
      onAfter();
      onClose();
    },
    onError: (err) =>
      toast.error('No se pudieron confirmar los cobros', {
        description: err instanceof Error ? err.message : 'Error desconocido',
      }),
  });

  return (
    <Dialog open onOpenChange={(o) => !o && !mutation.isPending && onClose()}>
      <DialogContent
        className="max-w-lg"
        processing={mutation.isPending && 'Confirmando cobros…'}
      >
        <DialogHeader>
          <div className="flex items-start gap-3">
            <span className="mt-0.5 inline-flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-emerald-100 text-emerald-700">
              <ShieldCheck className="h-5 w-5" aria-hidden />
            </span>
            <div>
              <DialogTitle>Confirmar {rows.length} cobro(s) Nova</DialogTitle>
              <DialogDescription>
                Cada confirmación registra un Ingreso en Tesorería sobre la caja
                origen del cobro
                {herconstruCount > 0
                  ? `; ${herconstruCount} de ellos también dispara un Egreso de comisión 5%.`
                  : '.'}
              </DialogDescription>
            </div>
          </div>
        </DialogHeader>
        <DialogBody className="space-y-3 text-sm">
          <ul className="max-h-72 space-y-1.5 overflow-y-auto pr-1">
            {rows.map((r) => {
              const cotizacion =
                r.nro_cotizacion ||
                (r.sales_header_sharepoint_id != null
                  ? `#${r.sales_header_sharepoint_id}`
                  : '—');
              return (
                <li
                  key={r.id}
                  className="flex items-center justify-between gap-3 rounded-md border bg-secondary/30 px-3 py-2"
                >
                  <div className="min-w-0 flex-1">
                    <div className="truncate font-medium text-foreground">
                      {r.cliente ?? '—'}
                    </div>
                    <div className="text-xs text-muted-foreground">
                      Cotización {cotizacion} · {r.medio_pago_origen ?? '—'}
                    </div>
                  </div>
                  <span className="shrink-0 text-sm font-semibold tabular-nums text-foreground">
                    {formatMoney(r.monto, 'MXN')}
                  </span>
                </li>
              );
            })}
          </ul>
          <div className="flex items-center justify-between rounded-md border bg-muted/40 px-3 py-2 text-sm">
            <span className="text-muted-foreground">Total MXN</span>
            <span className="font-semibold tabular-nums text-foreground">
              {formatMoney(totalMxn, 'MXN')}
            </span>
          </div>
        </DialogBody>
        <DialogFooter>
          <Button
            type="button"
            variant="outline"
            onClick={onClose}
            disabled={mutation.isPending}
          >
            Cancelar
          </Button>
          <Button
            type="button"
            onClick={() => mutation.mutate()}
            disabled={mutation.isPending || rows.length === 0}
          >
            Confirmar
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

/**
 * Confirmation dialog for a Cuenta por Pagar - Nova row. Nova picks
 * which of its operational cajas (Nova Banorte / Nova Mercado Pago /
 * Herconstru) to debit the inter-co payment from. Saldos for all 3 are
 * fetched in parallel so the operator sees balances upfront; an
 * insufficient saldo warns but never blocks (matching Tesorería's
 * "negative is allowed" stance).
 *
 * On confirm: server flips the Pagar row to Confirmado, writes the
 * Egreso to the chosen caja, and unlocks the matching Cobrar
 * Fornitalia row. The Cobrar's later confirm always credits BBVA
 * Italmexy regardless of which caja paid here.
 */
const NOVA_PAGADOR_CAJAS: NovaPagadorCaja[] = [
  'Nova Banorte',
  'Nova Mercado Pago',
  'Herconstru',
];

function ConfirmarPagarNovaDialog({
  row,
  onClose,
}: {
  row: InterCoMovementItem;
  onClose: () => void;
}) {
  const qc = useQueryClient();
  const { config: activeCountry } = useActiveCountry();
  const countryCode = activeCountry.code;
  // Pay from the caja the cobro actually landed in. `medio_pago_origen`
  // is the collection caja; when it's one of the valid Nova cajas we
  // lock the selector to it so the operator can't drain a caja that
  // never received the money (the Nova Banorte / Nova Mercado Pago
  // mismatch, 2026-07-03). Falls back to a manual pick only when it's
  // absent/unresolvable (legacy rows, split-payment Facturas Internas).
  const lockedCaja: NovaPagadorCaja | null =
    row.medio_pago_origen != null &&
    (NOVA_PAGADOR_CAJAS as readonly string[]).includes(row.medio_pago_origen)
      ? (row.medio_pago_origen as NovaPagadorCaja)
      : null;
  const [cajaOrigen, setCajaOrigen] = useState<NovaPagadorCaja>(
    lockedCaja ?? 'Nova Banorte',
  );
  // Comprobante de pago Nova attaches to prove the inter-co payment.
  // Mandatory: the user said this should always be uploaded on confirm,
  // and Fornitalia reads it later from "Cobros a Confirmar". We stage
  // the file locally first, then run upload → confirm in series so
  // partial failures (drive write fails) don't leave a confirmed row
  // without proof.
  const [stagedFile, setStagedFile] = useState<File | null>(null);
  const [fileError, setFileError] = useState<string | null>(null);
  const [uploading, setUploading] = useState(false);
  const [previewFile, setPreviewFile] = useState<File | null>(null);
  const fileInputRef = useRef<HTMLInputElement | null>(null);
  const initialComprobanteName = row.comprobante_file_name ?? null;
  const initialComprobanteOwnerSpId = row.comprobante_owner_sp_id;
  const [localComprobanteName, setLocalComprobanteName] = useState<string | null>(
    initialComprobanteName,
  );
  const hasUploadedComprobante = localComprobanteName !== null;
  // The row's sharepoint_id IS the comprobante owner on the Pagar leg
  // (Nova writes the file straight to this row). Fall back to whatever
  // the server reported so legacy rows without sharepoint_id surface a
  // useful error instead of a silent no-op.
  const comprobanteOwnerSpId =
    row.sharepoint_id ?? initialComprobanteOwnerSpId ?? null;

  // Parallel saldo queries — predictable shape (always 3 cajas), so
  // three explicit useQuery calls beat useQueries for type safety.
  // They share Tesorería's cache key so navigating to the Tesoreria
  // page after this confirm reuses the values.
  const banorteQ = useQuery({
    queryKey: ['tesoreria', countryCode, 'saldo', 'Nova Banorte'],
    queryFn: () => getCajaSaldo('Nova Banorte'),
    staleTime: 30_000,
  });
  const mpQ = useQuery({
    queryKey: ['tesoreria', countryCode, 'saldo', 'Nova Mercado Pago'],
    queryFn: () => getCajaSaldo('Nova Mercado Pago'),
    staleTime: 30_000,
  });
  const herconstrQ = useQuery({
    queryKey: ['tesoreria', countryCode, 'saldo', 'Herconstru'],
    queryFn: () => getCajaSaldo('Herconstru'),
    staleTime: 30_000,
  });
  const saldoByCaja: Record<NovaPagadorCaja, { saldo: number | null; loading: boolean }> = {
    'Nova Banorte': { saldo: banorteQ.data?.saldo ?? null, loading: banorteQ.isPending },
    'Nova Mercado Pago': { saldo: mpQ.data?.saldo ?? null, loading: mpQ.isPending },
    Herconstru: { saldo: herconstrQ.data?.saldo ?? null, loading: herconstrQ.isPending },
  };
  const selectedSaldo = saldoByCaja[cajaOrigen].saldo;
  const insufficient = selectedSaldo != null && selectedSaldo < row.monto;

  const mutation = useMutation({
    mutationFn: async () => {
      // Upload the comprobante first when one is staged. We do it
      // before the confirm so a confirmed row never lacks proof — if
      // the upload fails the row stays in the queue and the operator
      // can retry. Already-uploaded comprobantes (legacy rows) skip
      // straight to the confirm.
      if (stagedFile && !hasUploadedComprobante) {
        if (comprobanteOwnerSpId == null) {
          throw new Error(
            'No se puede adjuntar el comprobante: la fila no tiene sharepoint_id. Refrescá la lista.',
          );
        }
        setUploading(true);
        try {
          const uploaded = await uploadDocument({
            entityType: 'ComprobanteInterCo',
            entityId: String(comprobanteOwnerSpId),
            file: stagedFile,
          });
          setLocalComprobanteName(uploaded.fileName);
          setStagedFile(null);
        } finally {
          setUploading(false);
        }
      }
      return confirmarPagarNova(row.id, cajaOrigen);
    },
    onSuccess: (result) => {
      const unlockedNote = result.unlockedCobrarId
        ? ' La Cuenta por Cobrar Fornitalia quedó habilitada.'
        : '';
      toast.success(`Pago confirmado en ${result.cajaOrigen}.${unlockedNote}`);
      qc.invalidateQueries({ queryKey: ['administracion', countryCode, 'pagar-nova'] });
      qc.invalidateQueries({ queryKey: ['administracion', countryCode, 'cobrar-fornitalia'] });
      // Invalidate Tesorería so the newly-emitted Egreso shows up on
      // the chosen caja immediately if the operator switches over.
      qc.invalidateQueries({ queryKey: ['tesoreria', countryCode] });
      onClose();
    },
    onError: (err) => {
      toast.error(err instanceof Error ? err.message : 'Error al confirmar');
    },
  });

  const ALLOWED_MIMES = ['application/pdf', 'image/png', 'image/jpeg'];
  const MAX_BYTES = 4 * 1024 * 1024;
  const triggerFilePick = () => {
    if (mutation.isPending || uploading) return;
    setFileError(null);
    fileInputRef.current?.click();
  };
  const onFileInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const picked = e.target.files?.[0] ?? null;
    e.target.value = '';
    if (!picked) return;
    if (!ALLOWED_MIMES.includes(picked.type)) {
      setFileError('Tipo no soportado: solo PDF, PNG o JPG.');
      return;
    }
    if (picked.size > MAX_BYTES) {
      setFileError('El archivo supera los 4 MB.');
      return;
    }
    setStagedFile(picked);
  };

  // Confirm requires either a freshly-staged file or a previously-
  // uploaded comprobante (legacy retry case). Without one, the toggle
  // stays disabled — proof-of-payment is mandatory per the new flow.
  const busy = mutation.isPending || uploading;
  const hasFile = hasUploadedComprobante || stagedFile !== null;

  return (
    <Dialog open onOpenChange={(o) => !o && !busy && onClose()}>
      <DialogContent
        className="max-w-md"
        processing={busy && (uploading ? 'Subiendo comprobante…' : 'Confirmando pago…')}
      >
        <DialogHeader>
          <div className="flex items-start gap-3">
            <span className="mt-0.5 inline-flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-emerald-100 text-emerald-700">
              <ShieldCheck className="h-5 w-5" aria-hidden />
            </span>
            <div>
              <DialogTitle>Confirmar pago a Fornitalia</DialogTitle>
              <DialogDescription>
                Elegí desde qué caja Nova se deducirá este pago inter-compañía.
                Italmexy recibirá el monto en su caja BBVA al confirmar la
                Cuenta por Cobrar.
              </DialogDescription>
            </div>
          </div>
        </DialogHeader>
        <DialogBody className="space-y-3 text-sm">
          <div className="space-y-2 rounded-lg border bg-secondary/30 px-3 py-2.5">
            <ConfirmarCobroNovaRow label="Cliente" value={row.cliente ?? '—'} />
            <ConfirmarCobroNovaRow
              label="Cotización"
              value={
                row.nro_cotizacion ??
                (row.sales_header_sharepoint_id != null
                  ? `#${row.sales_header_sharepoint_id}`
                  : '—')
              }
            />
            <ConfirmarCobroNovaRow
              label="Monto a pagar"
              value={formatMoney(row.monto, 'MXN')}
            />
          </div>
          <FiscalDataBlock data={row.fiscal_data} />
          <div className="space-y-1.5">
            <Label htmlFor="pagar-nova-caja">Caja origen Nova</Label>
            <Select
              value={cajaOrigen}
              onValueChange={(v) => setCajaOrigen(v as NovaPagadorCaja)}
              disabled={lockedCaja !== null}
            >
              <SelectTrigger id="pagar-nova-caja">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {NOVA_PAGADOR_CAJAS.map((caja) => {
                  const { saldo, loading } = saldoByCaja[caja];
                  const label = loading
                    ? `${caja} · cargando saldo…`
                    : saldo != null
                      ? `${caja} · saldo ${formatMoney(saldo, 'MXN')}`
                      : caja;
                  return (
                    <SelectItem key={caja} value={caja}>
                      {label}
                    </SelectItem>
                  );
                })}
              </SelectContent>
            </Select>
            {lockedCaja !== null && (
              <p className="text-xs text-muted-foreground">
                Se paga desde <strong>{lockedCaja}</strong> — la caja donde
                se cobró esta venta.
              </p>
            )}
          </div>
          {insufficient && (
            <div className="flex items-start gap-2 rounded-lg border border-amber-300 bg-amber-50 px-3 py-2 text-xs text-amber-900">
              <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" aria-hidden />
              <span>
                <strong>Saldo insuficiente.</strong> {cajaOrigen} tiene{' '}
                <span className="font-semibold">
                  {selectedSaldo != null ? formatMoney(selectedSaldo, 'MXN') : '—'}
                </span>
                . Confirmar dejará la caja en negativo — podés continuar si es lo
                esperado.
              </span>
            </div>
          )}
          <div className="space-y-1.5">
            <Label>Comprobante de pago</Label>
            <p className="text-xs text-muted-foreground">
              Subí el comprobante (PDF/PNG/JPG) que Fornitalia verá al
              confirmar la Cuenta por Cobrar.
            </p>
            {hasUploadedComprobante && localComprobanteName && comprobanteOwnerSpId != null ? (
              <FileLink
                entityType="ComprobanteInterCo"
                entityId={String(comprobanteOwnerSpId)}
                fileName={localComprobanteName}
                onDeleted={() => setLocalComprobanteName(null)}
              />
            ) : (
              <div className="flex items-center gap-2">
                <Button
                  type="button"
                  variant="outline"
                  onClick={triggerFilePick}
                  disabled={busy}
                  title={stagedFile ? stagedFile.name : undefined}
                  className="min-w-0 flex-1 justify-start gap-2"
                >
                  <Upload className="h-4 w-4 shrink-0" aria-hidden />
                  <span className="truncate">
                    {stagedFile ? stagedFile.name : 'Adjuntar comprobante'}
                  </span>
                </Button>
                {stagedFile && (
                  <>
                    <Button
                      type="button"
                      variant="ghost"
                      size="icon"
                      onClick={() => setPreviewFile(stagedFile)}
                      disabled={busy}
                      aria-label="Ver comprobante"
                      title="Ver comprobante"
                    >
                      <Eye className="h-4 w-4" aria-hidden />
                    </Button>
                    <Button
                      type="button"
                      variant="ghost"
                      size="icon"
                      onClick={triggerFilePick}
                      disabled={busy}
                      aria-label="Reemplazar comprobante"
                      title="Reemplazar comprobante"
                    >
                      <Pencil className="h-4 w-4" aria-hidden />
                    </Button>
                    <Button
                      type="button"
                      variant="ghost"
                      size="icon"
                      onClick={() => setStagedFile(null)}
                      disabled={busy}
                      className="text-muted-foreground hover:text-destructive"
                      aria-label="Quitar comprobante"
                      title="Quitar comprobante"
                    >
                      <Trash2 className="h-4 w-4" aria-hidden />
                    </Button>
                  </>
                )}
                <input
                  ref={fileInputRef}
                  type="file"
                  accept="application/pdf,image/png,image/jpeg"
                  hidden
                  onChange={onFileInputChange}
                />
              </div>
            )}
            {fileError && (
              <p className="text-xs text-destructive">{fileError}</p>
            )}
          </div>
        </DialogBody>
        <DialogFooter>
          <Button
            type="button"
            variant="outline"
            onClick={onClose}
            disabled={busy}
          >
            Cancelar
          </Button>
          <Button
            type="button"
            onClick={() => mutation.mutate()}
            disabled={busy || !hasFile}
            title={
              !hasFile
                ? 'Adjuntá el comprobante de pago antes de confirmar'
                : undefined
            }
          >
            Confirmar pago
          </Button>
        </DialogFooter>
        {previewFile && (
          <FacturaPreviewDialog
            file={previewFile}
            onClose={() => setPreviewFile(null)}
          />
        )}
      </DialogContent>
    </Dialog>
  );
}

function ConfirmarCobrarFornitaliaDialog({
  row,
  onClose,
}: {
  row: InterCoMovementItem;
  onClose: () => void;
}) {
  const qc = useQueryClient();
  const { config: activeCountry } = useActiveCountry();
  const countryCode = activeCountry.code;
  const [open, setOpen] = useState(true);
  const mutation = useMutation({
    mutationFn: () => confirmarCobrarFornitalia(row.id),
    onSuccess: (result) => {
      const cd = result.cajaDiferencia;
      const note =
        cd.skipped === false
          ? ` Margen ${formatMoney(cd.diferencia, 'MXN')} y comision ${formatMoney(cd.comision, 'MXN')} en Caja Diferencia.`
          : cd.skipped === 'non_positive'
            ? ' Sin margen (cobrado <= costo contable).'
            : '';
      toast.success(`Cobro Fornitalia confirmado.${note}`);
      qc.invalidateQueries({
        queryKey: ['administracion', countryCode, 'cobrar-fornitalia'],
      });
      qc.invalidateQueries({ queryKey: ['administracion', countryCode, 'cobrar'] });
      setOpen(false);
      onClose();
    },
    onError: (err) => {
      toast.error(err instanceof Error ? err.message : 'Error al confirmar');
    },
  });

  const cotizacionLabel =
    row.nro_cotizacion ??
    (row.sales_header_sharepoint_id != null ? `#${row.sales_header_sharepoint_id}` : '—');

  return (
    <Dialog open={open} onOpenChange={(o) => !o && !mutation.isPending && onClose()}>
      <DialogContent className="max-w-md" processing={mutation.isPending && 'Confirmando cobro…'}>
        <DialogHeader>
          <div className="flex items-start gap-3">
            <span className="mt-0.5 inline-flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-amber-100 text-amber-700">
              <AlertTriangle className="h-5 w-5" aria-hidden />
            </span>
            <div>
              <DialogTitle>Confirmar Cobrar Fornitalia</DialogTitle>
              <DialogDescription>
                Esto marcará la cuenta como confirmada y registrará el Ingreso
                de la cuenta inter-compañía en la caja de Italmexy.
              </DialogDescription>
            </div>
          </div>
        </DialogHeader>
        <DialogBody className="space-y-3 text-sm">
          <div className="space-y-2 rounded-lg border bg-secondary/30 px-3 py-2.5">
            <ConfirmarCobroNovaRow label="Cliente" value={row.cliente ?? '—'} />
            <ConfirmarCobroNovaRow label="Cotización" value={cotizacionLabel} />
            <ConfirmarCobroNovaRow label="Monto" value={formatMoney(row.monto, 'MXN')} />
            <ConfirmarCobroNovaRow label="Descripción" value={row.descripcion ?? '—'} />
          </div>
          <FiscalDataBlock data={row.fiscal_data} />
          <div className="rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-900">
            Confirmá solo una vez. Si hacés doble clic, el segundo intento será rechazado porque
            la fila ya quedará confirmada.
          </div>
        </DialogBody>
        <DialogFooter>
          <Button type="button" variant="outline" onClick={onClose} disabled={mutation.isPending}>
            Cancelar
          </Button>
          <Button type="button" onClick={() => mutation.mutate()} disabled={mutation.isPending}>
            Confirmar cobro
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

/**
 * AR result screen after ARCA approves a CAE. Shows the comprobante nº,
 * CAE + vencimiento, and lets the operator preview / download the branded
 * representación impresa (the same PDF auto-attached to the row on success).
 */
function CaeExitoView({
  result,
  comprobanteId,
  onAttachStatusChange,
  onClose,
}: {
  result: Extract<SolicitarCaeResponse, { outcome: 'facturado' }>;
  comprobanteId: string;
  onAttachStatusChange?: (status: 'attaching' | 'done' | 'failed') => void;
  onClose: () => void;
}) {
  const qc = useQueryClient();
  const [downloading, setDownloading] = useState(false);
  const [previewing, setPreviewing] = useState(false);
  const [previewFile, setPreviewFile] = useState<File | null>(null);
  // Attach the CAE PDF to the FacturaVenta slot HERE, on the always-visible
  // success screen — not fire-and-forget in the mutation's onSuccess. The CAE
  // is already persisted (facturado=true), so a failed attach used to strand
  // the factura PDF-less with no recovery: the cobro leaves the Facturación
  // queue (filter Facturado_CP ne 'SI') and the venta detail is read-only.
  // Retrying here + a manual button turns the old dead-end toast into a real
  // recovery path.
  const [attach, setAttach] = useState<'attaching' | 'done' | 'failed'>('attaching');
  // Sticky once it has failed at least once, so the recovery box (with the
  // retry button) stays visible while a manual retry is in flight instead of
  // flickering back to the plain "Adjuntando…" line.
  const [everFailed, setEverFailed] = useState(false);
  const runAttach = useCallback(async () => {
    setAttach('attaching');
    try {
      const file = await buildFacturaArcaPdfFile(result.factura);
      // A few attempts: the failure that stranded facturas was a transient
      // SharePoint upload blip, not a bad payload (the PDF generates fine).
      let lastErr: unknown;
      for (let i = 0; i < 3; i++) {
        try {
          await uploadDocument({ entityType: 'FacturaVenta', entityId: comprobanteId, file });
          qc.invalidateQueries({ queryKey: ['administracion'] });
          qc.invalidateQueries({ queryKey: ['detalle-venta'] });
          setAttach('done');
          return;
        } catch (err) {
          lastErr = err;
        }
      }
      throw lastErr;
    } catch (err) {
      console.warn('[solicitar-cae] auto-attach PDF failed', err);
      setEverFailed(true);
      setAttach('failed');
    }
  }, [result.factura, comprobanteId, qc]);
  useEffect(() => {
    void runAttach();
  }, [runAttach]);
  // Report attach status up so the parent can guard closing the dialog while
  // the PDF is still un-attached (facturado but PDF-less has no recovery path).
  useEffect(() => {
    onAttachStatusChange?.(attach);
  }, [attach, onAttachStatusChange]);
  const handlePdf = async () => {
    setDownloading(true);
    try {
      await downloadFacturaArcaPdf(result.factura);
    } catch (err) {
      toast.error('No se pudo generar el PDF', {
        description: err instanceof Error ? err.message : 'Error desconocido',
      });
    } finally {
      setDownloading(false);
    }
  };
  const handlePreview = async () => {
    setPreviewing(true);
    try {
      const file = await buildFacturaArcaPdfFile(result.factura);
      setPreviewFile(file);
    } catch (err) {
      toast.error('No se pudo generar el PDF', {
        description: err instanceof Error ? err.message : 'Error desconocido',
      });
    } finally {
      setPreviewing(false);
    }
  };
  return (
    <>
      <DialogHeader>
        <DialogTitle className="flex items-center gap-2">
          <CheckCircle2 className="h-5 w-5 text-emerald-600" aria-hidden />
          CAE emitido
        </DialogTitle>
        <DialogDescription>
          ARCA aprobó el comprobante. El PDF con el QR se adjuntó
          automáticamente a la factura; podés previsualizarlo o descargar una
          copia.
        </DialogDescription>
      </DialogHeader>
      <DialogBody className="space-y-3">
        <dl className="space-y-2 rounded-md border bg-muted/40 px-3 py-2.5">
          <ConfirmarCobroNovaRow label="Comprobante" value={result.nroComprobante || '—'} />
          <ConfirmarCobroNovaRow label="CAE" value={result.cae} />
          <ConfirmarCobroNovaRow label="Vencimiento CAE" value={result.vencimiento ?? '—'} />
        </dl>
        {result.observacion && (
          <p className="rounded-md border border-amber-300 bg-amber-50 px-3 py-2 text-xs text-amber-800">
            {result.observacion}
          </p>
        )}
        <button
          type="button"
          onClick={handlePreview}
          disabled={previewing}
          className="inline-flex items-center gap-1.5 text-sm font-medium text-primary hover:underline disabled:opacity-60"
        >
          <Eye className="h-4 w-4" aria-hidden />
          {previewing ? 'Generando…' : 'Ver PDF'}
        </button>
        {attach === 'attaching' && !everFailed && (
          <p className="flex items-center gap-1.5 rounded-md border border-amber-300 bg-amber-50 px-3 py-2 text-xs text-amber-800">
            <Loader2 className="h-4 w-4 shrink-0 animate-spin" aria-hidden />
            Adjuntando el PDF al sistema…
          </p>
        )}
        {attach === 'done' && (
          <p className="flex items-center gap-1.5 rounded-md border border-emerald-300 bg-emerald-50 px-3 py-2 text-xs text-emerald-800">
            <CheckCircle2 className="h-4 w-4 shrink-0" aria-hidden />
            PDF adjuntado al sistema.
          </p>
        )}
        {everFailed && attach !== 'done' && (
          <div className="space-y-1.5 rounded-md border border-amber-300 bg-amber-50 px-3 py-2 text-xs text-amber-900">
            <p>No se pudo adjuntar el PDF al sistema automáticamente.</p>
            <Button
              type="button"
              size="sm"
              variant="outline"
              disabled={attach === 'attaching'}
              onClick={() => void runAttach()}
            >
              {attach === 'attaching' ? 'Adjuntando…' : 'Reintentar adjuntar'}
            </Button>
            {attach === 'attaching' && (
              <p className="text-[11px] text-amber-700">
                El sistema está reintentando adjuntarlo automáticamente… no cierres
                esta ventana.
              </p>
            )}
          </div>
        )}
      </DialogBody>
      <DialogFooter>
        <Button
          type="button"
          variant="outline"
          className="gap-1.5"
          disabled={downloading}
          onClick={handlePdf}
        >
          <Download className="h-4 w-4" aria-hidden />
          {downloading ? 'Generando…' : 'Descargar PDF'}
        </Button>
        <Button type="button" onClick={onClose}>
          Cerrar
        </Button>
      </DialogFooter>
      {previewFile && (
        <FacturaPreviewDialog
          file={previewFile}
          onClose={() => setPreviewFile(null)}
        />
      )}
    </>
  );
}

/**
 * Formato esperado de un Nº de comprobante ARGENTINO: punto de venta (4-5
 * dígitos) + número (8 dígitos), con la letra adelante opcional — así entran
 * tanto "A-00005-00000572" como las cargas viejas sin letra ("0005-00000549").
 *
 * Sólo aplica a AR: en MX el Nº de factura es el UUID / Folio Fiscal del CFDI
 * ("4EB96FE8-0D77-…"), que legítimamente no matchea este patrón.
 *
 * Se usa para ADVERTIR, nunca para bloquear: un Nº fuera de formato pide una
 * confirmación extra. Motivo: entró un "1323213" a mano y quedó facturado, y
 * además `letraComprobante()` (Libro de IVA Ventas) no lo puede parsear, así
 * que el comprobante figura sin Tipo en el reporte.
 */
const AR_NRO_COMPROBANTE_RE = /^([A-Za-z]\s*-\s*)?\d{4,5}\s*-\s*\d{8}$/;

function FacturarDialog({
  row,
  onClose,
}: {
  row: ComprobanteAdminItem;
  onClose: () => void;
}) {
  const qc = useQueryClient();
  const { config: activeCountry } = useActiveCountry();

  // Two-stage eligibility (MX only). On MX, RegistrarPagoDialog no
  // longer asks for Pago Final at registration — every non-FacturaInterna
  // MX cobro that requires factura lands here as an anticipo
  // (`EsPagoFinal_CP='No'`) and gets the Anticipada/Final stage toggle.
  // The operator can upload either stage in any order, and "Facturar"
  // closes the dialog for the chosen stage — they re-open later to
  // upload the other stage if needed. AR rows, FacturaInterna rows,
  // and any legacy MX rows that came in with EsPagoFinal_CP='SI' stay
  // on the single-stage 'final' path.
  const isInterna = row.fields.EsFacturaInterna_CP === 'SI';
  const isAnticipo =
    (row.fields.EsPagoFinal_CP ?? '').toString().trim().toUpperCase() !== 'SI';
  const contieneFactura =
    (row.fields.ContieneFactura_CP ?? '').toString().trim().toUpperCase() === 'SI';
  const isTwoStage =
    activeCountry.code === 'MX' && !isInterna && isAnticipo && contieneFactura;
  // MX Nova "horno usado" — server-flagged when the parent venta priced
  // below cost+commission+IVA. On Factura Interna rows this means the
  // physical factura amount can legitimately differ from the costo
  // contable, so we suppress the AI mismatch warning and surface a
  // dedicated banner instead.
  const usadoFI = isInterna && row.facturaInternaUsado;

  // Server-recorded state per stage. The row carries:
  //   - Anticipada: FacturaAnticipadaFileName_CP / NroFacturaAnticipada_CP
  //   - Final:      FacturaFileName_CP / NroFactura_CP + Facturado_CP='SI'
  const initialAnticipadaFileName = row.fields.FacturaAnticipadaFileName_CP ?? null;
  const initialFinalFileName = row.fields.FacturaFileName_CP ?? null;
  // "Anticipada committed" = either server side already has it, or the
  // user just saved it in this session. The session-side flag is set
  // in `mutation.onSuccess` so the parent row snapshot (which doesn't
  // refresh mid-dialog) is irrelevant.
  const anticipadaDone = Boolean(initialAnticipadaFileName);

  // Per-stage state. For non-two-stage rows we still keep both buckets
  // so the rendering branch can stay symmetric — only the 'final' slot
  // is ever exercised in that case.
  //
  // Default to `'final'` for both branches:
  //   - non-two-stage: only stage available.
  //   - two-stage + Anticipada NOT yet committed: the toggle starts off
  //     ("Es factura anticipada" unchecked) — the common case is the
  //     operator going straight to Final. They can flip the toggle on
  //     if they actually want to register an Anticipada first.
  //   - two-stage + Anticipada already committed: only Final remains
  //     to be uploaded; the toggle is suppressed entirely.
  const [activeStage, setActiveStage] = useState<FacturarStage>('final');
  const [nroByStage, setNroByStage] = useState<Record<FacturarStage, string>>({
    anticipada: row.fields.NroFacturaAnticipada_CP ?? '',
    final: row.fields.NroFactura_CP ?? '',
  });
  // MX Nova "horno usado" monto override — only used in the FI usado
  // branch. Empty string by default per spec ("se le carga el monto
  // vacío"). Pre-validation happens at form-submit (string → number).
  const [montoUsadoRaw, setMontoUsadoRaw] = useState<string>('');
  // Fetch the parent venta's usado-context so the dialog can warn
  // when the operator's montoOverride exceeds the customer-facing
  // totalFactura — that's the only path under the post-G1 flow where
  // Nova caja ends up negative (montoOverride flows into
  // emitCajaDiferencia's `costoContable`, and diferencia = factura −
  // montoOverride). Only runs when this row is a usado FI.
  const ventaSpId = row.fields.IDOperacion_CP?.toString().trim() || null;
  const usadoCtxQ = useQuery({
    queryKey: ['usado-context-facturar', ventaSpId],
    queryFn: () => getUsadoContext(ventaSpId!),
    enabled: Boolean(ventaSpId) && row.facturaInternaUsado === true,
    staleTime: 30_000,
  });
  const facturaClienteAmount = usadoCtxQ.data?.totalFactura ?? null;
  // Cost buildup of the Factura Interna (Valor Contable) total that Italmexy
  // invoices Nova — shown read-only on the FI row so the facturador ve "qué
  // costos se consideran para el total de la factura" (costo contable →
  // × multiplicador → + IVA → total). MX Factura Interna rows only.
  // Only on the Factura Interna — the factura that FORNITALIA/Italmexy issues
  // TO Nova (Valor Contable). NOT on the Nova-side CFDI cobros; the FI is
  // facturado on the Fornitalia side (admin/administración), so `isInterna`
  // already scopes it to "cuando Fornitalia hace la factura a Nova".
  const fiBreakdownQ = useQuery({
    queryKey: ['factura-interna-breakdown', ventaSpId],
    queryFn: () => getFacturaInternaBreakdown(ventaSpId!),
    enabled: Boolean(ventaSpId) && isInterna && activeCountry.code === 'MX',
    staleTime: 30_000,
  });
  const fiBreakdown = fiBreakdownQ.data ?? null;
  // Desglose de costos (Valor Contable) shown as a full-height side column on
  // the Factura Interna (Fornitalia → Nova). Datos de Facturación stays inline.
  const showBreakdown = isInterna && Boolean(fiBreakdown);
  // Mirrors uploads that completed during this dialog session — the
  // parent-passed `row` snapshot doesn't refresh mid-session, so we
  // keep the FileLink visible after upload without re-querying.
  const [localFileNameByStage, setLocalFileNameByStage] = useState<
    Record<FacturarStage, string | null>
  >({ anticipada: null, final: null });
  const [stagedFileByStage, setStagedFileByStage] = useState<
    Record<FacturarStage, File | null>
  >({ anticipada: null, final: null });
  const [fileErrorByStage, setFileErrorByStage] = useState<
    Record<FacturarStage, string | null>
  >({ anticipada: null, final: null });
  const [extractedTotalByStage, setExtractedTotalByStage] = useState<
    Record<FacturarStage, number | null>
  >({ anticipada: null, final: null });
  const [nroFromAIByStage, setNroFromAIByStage] = useState<
    Record<FacturarStage, boolean>
  >({ anticipada: false, final: false });

  const [uploading, setUploading] = useState(false);
  const [extracting, setExtracting] = useState(false);
  const [confirmIssuesOpen, setConfirmIssuesOpen] = useState(false);
  const [previewFile, setPreviewFile] = useState<File | null>(null);
  const fileInputRef = useRef<HTMLInputElement | null>(null);

  // AR ARCA (CAE) toggle. Default OFF — the manual/offline flow (upload the
  // PDF + type the Nº) runs unless the operator opts into ARCA. When ON, the
  // dialog swaps to the ARCA path: pick the comprobante tipo and "Emitir CAE"
  // requests it from AFIP (número + CAE + QR + PDF all generated). AR-only —
  // stays false on MX so nothing there changes.
  const isAr = activeCountry.code === 'AR';
  // Facturación Previa entra a la cola SIN estar 'Confirmado', y el
  // `/facturar` manual sigue exigiendo 'Confirmado' a propósito (dejarlo
  // intacto es lo que mantiene MX inerte). Con el default en manual, el
  // operador cargaba el PDF —que ya estampa FacturaDriveItemID y prende
  // ContieneFactura— y recién ahí /facturar lo rechazaba: medio commit,
  // factura colgada y nada facturado. Para esas filas arrancamos en ARCA,
  // que es el único camino que las acepta.
  const esPrevia = isAr && row.fields.FacturacionPrevia_CP === 'SI';
  const [facturarConArca, setFacturarConArca] = useState(esPrevia);
  // Operator-chosen comprobante tipo (ARCA ids): Factura A=1, B=6, C=11.
  const [caeTipoId, setCaeTipoId] = useState<number>(6);
  // FCE MiPyME (201/206/211) → the operator can set the payment-due date
  // (FchVtoPago, the credit's maturity). Blank = server defaults to the emisión
  // date. Only surfaced/sent for FCE tipos.
  const caeEsFce = [201, 206, 211].includes(caeTipoId);
  const [caeVencimientoPago, setCaeVencimientoPago] = useState<string>('');
  // Operator override of the "Pago Final" tag from this dialog. null = follow
  // the vendor's tag (the dry-run's esPagoFinal); true/false = explicit
  // override → re-runs the preview (itemized + descuento de anticipo when final)
  // and is persisted on emit.
  const [caePagoFinal, setCaePagoFinal] = useState<boolean | null>(null);
  const hoyAr = new Date().toLocaleDateString('en-CA', {
    timeZone: 'America/Argentina/Buenos_Aires',
  });
  // Active AR Puntos de Venta for the selector (only fetched on AR).
  const arcaPvsQ = useQuery({
    queryKey: ['puntos-venta', 'active', activeCountry.code],
    queryFn: listActivePuntosVenta,
    enabled: isAr,
    staleTime: 60_000,
  });
  const arcaPvs = arcaPvsQ.data ?? [];
  const [caePvId, setCaePvId] = useState<string>('');
  // Factura E (exportación, WSFEX): otro web service y otros PV. Un cliente del exterior sólo
  // admite Factura E y un cliente argentino nunca la ve. El server le avisa al diálogo
  // (`requiere_factura_e`) cuando el receptor es del exterior.
  const caeEsE = caeTipoId === 19;
  const [receptorExterior, setReceptorExterior] = useState(false);
  const [fexIncoterm, setFexIncoterm] = useState('');
  const [fexPermiso, setFexPermiso] = useState('');
  const [fexPermisoAplicado, setFexPermisoAplicado] = useState('');
  const [fexCtzInput, setFexCtzInput] = useState('');
  const [fexCtzAplicada, setFexCtzAplicada] = useState<number | null>(null);
  const [fexCtzArca, setFexCtzArca] = useState<number | null>(null);
  const incotermsQ = useQuery({
    queryKey: INCOTERMS_QUERY_KEY,
    queryFn: listIncoterms,
    enabled: isAr && caeEsE,
    staleTime: Infinity,
  });
  // Los PV de exportación no se mezclan con los de WSFE (A/B/C): se listan todos, pero uno que no
  // corresponde al CLIENTE (exportación para un cliente argentino, o al revés) no deja continuar.
  // Los tipos de comprobante siguen al PV elegido: PV de exportación → sólo Factura E.
  const pvsCompatibles = useMemo(
    () => arcaPvs.filter((pv) => (pv.fields.EsExportacion === true) === receptorExterior),
    [arcaPvs, receptorExterior],
  );
  const caePvSel = arcaPvs.find((pv) => pv.id === caePvId) ?? null;
  const caePvEsExpo = caePvSel?.fields.EsExportacion === true;
  const caePvIncompatible = caePvSel != null && caePvEsExpo !== receptorExterior;
  // Default: el primer PV compatible. Se reubica sólo cuando cambia el cliente (p. ej. el server
  // detecta uno del exterior), no cuando el operador elige uno a mano.
  useEffect(() => {
    setCaePvId((prev) => {
      if (pvsCompatibles.some((pv) => pv.id === prev)) return prev;
      return pvsCompatibles[0]?.id ?? (arcaPvs.some((pv) => pv.id === prev) ? prev : '');
    });
  }, [pvsCompatibles, arcaPvs]);
  // El tipo sigue al PV: uno de exportación sólo emite Factura E; al volver a uno de A/B/C desde
  // Factura E se arranca por Factura B (el default del diálogo).
  useEffect(() => {
    if (!caePvSel) return;
    setCaeTipoId((prev) => (caePvEsExpo ? 19 : prev === 19 ? 6 : prev));
  }, [caePvSel, caePvEsExpo]);
  // Cotización y permiso: se aplican al preview unos instantes después de tipear (cada cambio
  // dispara un preview nuevo, y la cotización pisada manda sobre la de ARCA).
  useEffect(() => {
    const n = fexCtzInput.trim() ? parseMoney(fexCtzInput) : null;
    const id = setTimeout(() => setFexCtzAplicada(n != null && n > 0 ? n : null), 600);
    return () => clearTimeout(id);
  }, [fexCtzInput]);
  useEffect(() => {
    const id = setTimeout(() => setFexPermisoAplicado(fexPermiso), 600);
    return () => clearTimeout(id);
  }, [fexPermiso]);
  // On a successful CAE the dialog swaps to a result screen (nº, CAE,
  // vencimiento + PDF view/download) instead of closing immediately.
  const [caeResult, setCaeResult] = useState<Extract<
    SolicitarCaeResponse,
    { outcome: 'facturado' }
  > | null>(null);
  // Pre-emit confirmation: a dry-run fetches the exact values ARCA will
  // receive; the operator must confirm before a real prod CAE is issued.
  const [caeConfirm, setCaeConfirm] = useState<CaeResumen | null>(null);
  // Per-line description overrides (index → edited text) for the ARCA factura.
  // Text only; the server keeps the amounts/IVA. Defaults fall back to the
  // auto-generated line text when an index isn't edited.
  const [lineasTextoEdit, setLineasTextoEdit] = useState<Record<number, string>>({});
  // Guard closing the success screen while the PDF attach is still failing:
  // the CAE is emitted but PDF-less, and once closed there's no path back
  // (cobro is out of the Facturación queue). CaeExitoView reports its attach
  // status up; a confirm dialog warns before letting the user close anyway.
  const [caeAttachFailed, setCaeAttachFailed] = useState(false);
  const [confirmCloseOpen, setConfirmCloseOpen] = useState(false);
  const handleAttachStatus = useCallback(
    (s: 'attaching' | 'done' | 'failed') => setCaeAttachFailed(s === 'failed'),
    [],
  );
  const requestClose = () => {
    if (caeResult && caeAttachFailed) {
      setConfirmCloseOpen(true);
      return;
    }
    onClose();
  };

  // ---- active-stage projections (re-derived per render) ----
  const nroFactura = nroByStage[activeStage];
  const stagedFile = stagedFileByStage[activeStage];
  const fileError = fileErrorByStage[activeStage];
  const extractedTotal = extractedTotalByStage[activeStage];
  const nroFromAI = nroFromAIByStage[activeStage];

  const setNroFactura = (val: string | ((cur: string) => string)) =>
    setNroByStage((p) => ({
      ...p,
      [activeStage]: typeof val === 'function' ? val(p[activeStage]) : val,
    }));
  const setLocalFileName = (val: string | null) =>
    setLocalFileNameByStage((p) => ({ ...p, [activeStage]: val }));
  const setStagedFile = (val: File | null) =>
    setStagedFileByStage((p) => ({ ...p, [activeStage]: val }));
  const setFileError = (val: string | null) =>
    setFileErrorByStage((p) => ({ ...p, [activeStage]: val }));
  const setExtractedTotal = (val: number | null) =>
    setExtractedTotalByStage((p) => ({ ...p, [activeStage]: val }));
  const setNroFromAI = (val: boolean) =>
    setNroFromAIByStage((p) => ({ ...p, [activeStage]: val }));

  // Per-stage entity dispatch — the upload route patches different
  // column pairs based on this. AR + Interna + pago-final cobros all
  // use the legacy 'FacturaVenta' entity.
  const entityTypeForStage = (stage: FacturarStage) =>
    stage === 'anticipada' ? ('FacturaVentaAnticipada' as const) : ('FacturaVenta' as const);
  const currentEntityType = entityTypeForStage(activeStage);

  // Expected amount for the factura. The factura is issued in the
  // payment method's currency, so both the figure and its label key off
  // the medio: `expectedCurrency` is the medio currency (inferRowCurrency)
  // and `expectedAmount` is `Monto_CP`, the medio's native amount — NOT
  // `Pesos_CP` (the venta-currency equivalent, which still drives the
  // venta accounting, left untouched here). For same-currency rows
  // `Monto_CP` already is the venta-currency amount and `Pesos_CP` is
  // empty, so the fallback only matters for legacy cross-currency rows
  // that never stored a native amount.
  const expectedCurrency = inferRowCurrency(row);
  const expectedAmount =
    parseMoney(row.fields.Monto_CP) ?? parseMoney(row.fields.Pesos_CP) ?? 0;
  const amountsMismatch =
    !usadoFI &&
    extractedTotal != null &&
    Math.abs(extractedTotal - expectedAmount) > 0.01;
  const amountDifference = extractedTotal != null
    ? Math.abs(extractedTotal - expectedAmount)
    : 0;

  const fileNameForStage = (stage: FacturarStage): string | null => {
    const local = localFileNameByStage[stage];
    if (local !== null) return local;
    return stage === 'anticipada' ? initialAnticipadaFileName : initialFinalFileName;
  };
  const fileName = fileNameForStage(activeStage);
  const hasUploadedFile = fileName !== null;

  const mutation = useMutation({
    mutationFn: async () => {
      // Two-step commit: upload the staged file first (if any), then
      // stamp the row's nro field for the active stage. Doing it in
      // this order means the file is durably attached even if the
      // patch fails — the user can retry without re-uploading because
      // we mirror `localFileName` immediately.
      if (stagedFile && !hasUploadedFile) {
        setUploading(true);
        try {
          const result = await uploadDocument({
            entityType: currentEntityType,
            entityId: row.id,
            file: stagedFile,
          });
          setLocalFileName(result.fileName);
          setStagedFile(null);
        } finally {
          setUploading(false);
        }
      }
      return facturarComprobante(
        row.id,
        nroFactura.trim(),
        isTwoStage ? activeStage : undefined,
        usadoFI && montoUsadoParsed != null ? montoUsadoParsed : undefined,
      );
    },
    onSuccess: (r) => {
      if (r.outcome === 'facturado') {
        const stageLabel =
          isTwoStage && activeStage === 'anticipada'
            ? 'Factura Anticipada registrada'
            : 'Facturado';
        toast.success(stageLabel, { description: `Nº factura ${r.nroFactura}` });
      } else {
        toast.info('No aplicable', { description: r.message });
      }
      // Either stage confirms close the dialog — the operator re-opens
      // from the Facturación queue to upload the other stage if needed.
      // No auto-switch between tabs; the new MX flow is one-factura-
      // per-confirm.
      qc.invalidateQueries({ queryKey: ['administracion'] });
      qc.invalidateQueries({ queryKey: ['detalle-venta'] });
      onClose();
    },
    onError: (err) =>
      toast.error('No se pudo facturar', {
        description: err instanceof Error ? err.message : 'Error desconocido',
      }),
  });

  // AR ARCA path — request a CAE from AFIP for this cobro. The número,
  // CAE, vencimiento and QR come back from ARCA; on success we generate
  // the representación impresa (PDF) client-side and download it.
  // Auto-validation: a side-effect-free dry-run (NO AFIP call) that both
  // fetches the exact values ARCA will receive AND gates the Emitir CAE button.
  // Runs whenever the ARCA toggle is on with a PV + tipo chosen. If the
  // client's receptor fiscal data is missing, the button is BLOCKED — the admin
  // can't fix it (only the vendor captures that data), so we don't let them
  // attempt against production; the vendor is warned at payment-upload time.
  const caeValidationQ = useQuery({
    queryKey: [
      'cae-dryrun',
      activeCountry.code,
      row.id,
      caeTipoId,
      caePvId,
      caeVencimientoPago,
      caePagoFinal,
      caeEsE ? [fexIncoterm, fexPermisoAplicado, fexCtzAplicada] : null,
    ],
    queryFn: () =>
      solicitarCaeArca({
        comprobanteId: row.id,
        tipoComprobanteId: caeTipoId,
        puntoVentaId: caePvId ? Number(caePvId) : undefined,
        vencimientoPago: caeEsFce && caeVencimientoPago ? caeVencimientoPago : undefined,
        pagoFinal: caePagoFinal ?? undefined,
        ...(caeEsE
          ? {
              cotizacion: fexCtzAplicada ?? undefined,
              incoterm: fexIncoterm || undefined,
              permisoEmbarque: fexPermisoAplicado.trim() || undefined,
            }
          : {}),
        dryRun: true,
      }),
    enabled: isAr && facturarConArca && caeTipoId > 0 && !!caePvId && !caePvIncompatible,
    staleTime: 0,
    gcTime: 0,
    retry: false,
    // Keep the previous dry-run visible while re-fetching (e.g. after toggling
    // "Facturar como Pago Final" or changing tipo/PV) so the preview + toggle
    // don't unmount and the dialog height doesn't collapse+snap back.
    placeholderData: keepPreviousData,
  });
  const caeCheck = caeValidationQ.data;
  const caePreview = caeCheck?.outcome === 'dry_run' ? caeCheck.resumen : null;
  const caePreviewLineas = caeCheck?.outcome === 'dry_run' ? caeCheck.lineas : null;
  const caePreviewFecha = caeCheck?.outcome === 'dry_run' ? caeCheck.fecha : null;
  // Anticipo/Final: the vendor's tag (RegistrarPago "Pago Final") is the intent;
  // `esAnticipo` is what the amount-based factura logic will actually print.
  // They can disagree — surface both so the facturador catches the mismatch.
  const caeEsPagoFinal = caeCheck?.outcome === 'dry_run' && caeCheck.esPagoFinal === true;
  // Effective Pago Final shown by the toggle: the operator override if set, else
  // the vendor's tag from the dry-run. Drives the preview + what emit persists.
  const caePagoFinalEff = caePagoFinal ?? caeEsPagoFinal;
  // Última factura pendiente de la venta pero marcada como anticipo → sugerir
  // Pago Final (así saldría itemizada con el descuento de anticipo). Deriva del
  // server (`sugerirPagoFinal`), que checkea que NO quede otro cobro con factura
  // pendiente — NO reutilizar "venta saldada", que da falso positivo cuando la
  // venta se pagó en cuotas y todavía hay facturas pendientes.
  const caeSugerirPagoFinal =
    caeCheck?.outcome === 'dry_run' && caeCheck.sugerirPagoFinal === true;
  // La venta tiene hornos sin N° de Serie / Despacho (se cargan en el despacho,
  // después de facturar). Aviso NO bloqueante: la factura sale sin esos datos y
  // se puede regenerar más tarde desde "Ver Facturas".
  const caeFaltanSerieDespacho =
    caeCheck?.outcome === 'dry_run' && caeCheck.faltanSerieDespacho === true;
  // Homologación warning: AFIP_PRODUCTION=false → test servers, CAE fiscalmente
  // nulo. Surfaced in the dialog so nadie confunde una demo con producción.
  const caeHomologacion =
    caeCheck?.outcome === 'dry_run' && caeCheck.production === false;
  // Cliente del exterior: el server lo detecta (cualquier tipo) y el diálogo pasa a Factura E.
  useEffect(() => {
    if (caeCheck?.outcome === 'requiere_factura_e') {
      setReceptorExterior(true);
      setCaeTipoId(19);
    } else if (caeCheck?.outcome === 'dry_run' && caeCheck.resumen.exportacion) {
      setReceptorExterior(true);
      const arca = caeCheck.resumen.exportacion.cotizacionArca;
      if (arca != null) setFexCtzArca(arca);
    } else if (caeCheck?.outcome === 'missing_fiscal_data' && caeEsE) {
      if (caeCheck.cotizacionArca != null) setFexCtzArca(caeCheck.cotizacionArca);
    }
  }, [caeCheck, caeEsE]);
  const caeExportacion = caeCheck?.outcome === 'dry_run' ? (caeCheck.resumen.exportacion ?? null) : null;
  // El incoterm se pide en su propio campo: no se lista como "dato faltante".
  const caeMissing =
    caeCheck?.outcome === 'missing_fiscal_data'
      ? (() => {
          const m = caeCheck.missing.filter((x) => x.tag !== 'exportacion.incoterm');
          return m.length > 0 ? m : null;
        })()
      : null;
  // El preview (y lo que se va a emitir) tiene que corresponder a lo que hay en pantalla:
  // mientras la cotización / el permiso / el incoterm tipeados no estén reflejados, no se continúa.
  const normPermiso = (s: string) => s.replace(/[\s-]/g, '').toUpperCase();
  // ARCA exige el Id_permiso de exactamente 16 caracteres alfanuméricos (error 1740).
  const fexPermisoNorm = normPermiso(fexPermiso);
  const fexPermisoInvalido = fexPermisoNorm !== '' && !/^[A-Z0-9]{16}$/.test(fexPermisoNorm);
  const fexCtzTipeada = fexCtzInput.trim() ? parseMoney(fexCtzInput) : null;
  // Control contra la cotización de ARCA, misma regla que el server (desvioCotizacion en
  // server/src/lib/arca/exportacion.ts): > 2% avisa; más que el máximo del PV de
  // exportación elegido (Configuración, default 10%) no deja emitir.
  const fexCtzMaxPct =
    caePvSel?.fields.FexDesvioMaxPct ?? FEX_DESVIO_MAX_PCT_DEFAULT;
  const fexCtzDesvioPct =
    fexCtzTipeada != null && fexCtzTipeada > 0 && fexCtzArca != null && fexCtzArca > 0
      ? (Math.abs(fexCtzTipeada - fexCtzArca) / fexCtzArca) * 100
      : 0;
  const fexCtzBloqueada = fexCtzDesvioPct > fexCtzMaxPct;
  const fexCtzAviso = !fexCtzBloqueada && fexCtzDesvioPct > Math.min(2, fexCtzMaxPct);
  const fexDesincronizado =
    caeEsE &&
    caeExportacion != null &&
    ((fexCtzTipeada != null && Math.abs(fexCtzTipeada - caeExportacion.cotizacion) > 1e-6) ||
      normPermiso(fexPermiso) !== (caeExportacion.permiso ?? '') ||
      fexIncoterm !== caeExportacion.incoterm);
  // Sólo los datos del receptor los completa el vendedor; importes, punto de
  // venta o configuración de AFIP no son datos del cliente.
  const caeFaltaCliente =
    caeMissing?.some((m) => m.tag.startsWith('receptor.') || m.tag.startsWith('tipo.')) ?? false;
  const caeBlockedMsg =
    caeCheck &&
    caeCheck.outcome !== 'dry_run' &&
    caeCheck.outcome !== 'missing_fiscal_data' &&
    caeCheck.outcome !== 'requiere_factura_e'
      ? 'message' in caeCheck
        ? caeCheck.message
        : 'Este cobro no se puede facturar.'
      : null;

  const caeMutation = useMutation({
    mutationFn: () =>
      solicitarCaeArca({
        comprobanteId: row.id,
        tipoComprobanteId: caeTipoId,
        puntoVentaId: caePvId ? Number(caePvId) : undefined,
        vencimientoPago: caeEsFce && caeVencimientoPago ? caeVencimientoPago : undefined,
        pagoFinal: caePagoFinal ?? undefined,
        // Factura E: se emite con la cotización que el operador vio en el preview.
        ...(caeEsE
          ? {
              cotizacion: caeExportacion?.cotizacion,
              incoterm: fexIncoterm || undefined,
              permisoEmbarque: fexPermiso.trim() || undefined,
            }
          : {}),
        // The edited (or default) text for each printed line.
        lineasTexto: caePreviewLineas?.map((l, i) => lineasTextoEdit[i] ?? l.articulo),
      }),
    onSuccess: async (r) => {
      if (r.outcome === 'facturado') {
        // Swap to the result screen (nº, CAE, vencimiento + PDF) instead of
        // a transient toast + immediate close.
        setCaeResult(r);
        qc.invalidateQueries({ queryKey: ['administracion'] });
        qc.invalidateQueries({ queryKey: ['detalle-venta'] });
        // The PDF generation + attach (with retry) now lives in CaeExitoView,
        // the always-visible success screen — so a transient upload failure
        // can be retried right there instead of stranding a facturado cobro
        // with no manual path (it's already gone from the Facturación queue).
      } else if (r.outcome === 'missing_fiscal_data') {
        toast.error('Datos fiscales incompletos', {
          description: r.missing.map((m) => m.message).join(' · '),
        });
      } else if (r.outcome === 'fce_required') {
        // ARCA rejected the regular factura: must be a MiPyME FCE. Point the
        // operator back to the tipo selector (the dropdown has the FCE options).
        setCaeTipoId(201);
        toast.warning('Debe emitirse como Factura de Crédito (MiPyMEs)', {
          description: r.message,
          duration: 14000,
        });
      } else if (r.outcome === 'fce_required_no_config') {
        toast.error('Falta configurar el Punto de Venta', {
          description: r.message,
          duration: 14000,
        });
      } else if (r.outcome === 'afip_rejected') {
        toast.error('ARCA rechazó el comprobante', { description: r.message, duration: 12000 });
      } else if (r.outcome === 'in_progress') {
        toast.warning('Facturación en curso', { description: r.message });
      } else {
        toast.info('No aplicable', {
          description: 'message' in r ? r.message : '',
        });
      }
    },
    onError: (err) => {
      const code =
        err instanceof Error && 'code' in err
          ? (err as { code?: string }).code
          : undefined;
      if (code === 'persist_failed') {
        // The CAE WAS emitted at AFIP but the DB write failed — must not read
        // as a clean failure (re-emitting would double-issue).
        toast.error('CAE emitido pero NO se guardó', {
          description: err instanceof Error ? err.message : 'Avisá a soporte — NO vuelvas a emitir.',
          duration: 15000,
        });
        return;
      }
      toast.error('No se pudo emitir el CAE', {
        description: err instanceof Error ? err.message : 'Error desconocido',
      });
    },
  });

  const busy = uploading || mutation.isPending;
  // PDF is mandatory for all variants (Factura, Factura Nova, Factura
  // Interna Nova, two-stage Anticipada/Final) — a Nº de factura on its
  // own can't be facturado without the supporting document.
  const hasFile = hasUploadedFile || stagedFile !== null;
  // Parse the usado-monto input once for both validity + submit. Accepts
  // dot or comma decimal; empty / non-numeric / non-positive => null
  // which fails validity below.
  const montoUsadoParsed = useMemo(() => {
    if (!usadoFI) return null;
    // `parseMoney` handles both AR (1.234,56) and MX/US (1,234.56)
    // thousand-separator conventions so the MoneyInput's formatted
    // display stays parseable as the seller types.
    const n = parseMoney(montoUsadoRaw);
    return n != null && Number.isFinite(n) && n > 0 ? n : null;
  }, [usadoFI, montoUsadoRaw]);
  // Hard-block confirmation when the override would push Nova caja
  // negative (the only way `emitCajaDiferencia` produces a negative
  // diferencia under the post-G1 flow). Soft warning was insufficient
  // per the operator — this turns the Facturar button off entirely.
  const montoUsadoExceedsFactura =
    usadoFI &&
    montoUsadoParsed != null &&
    facturaClienteAmount != null &&
    facturaClienteAmount > 0 &&
    montoUsadoParsed > facturaClienteAmount;
  const valid =
    nroFactura.trim().length > 0 &&
    hasFile &&
    (!usadoFI || (montoUsadoParsed !== null && !montoUsadoExceedsFactura));

  // La extracción con IA corre en segundo plano al elegir el archivo. Si se
  // factura ANTES de que termine, `extractedTotal` todavía es null, entonces
  // `amountsMismatch` da false y la confirmación de montos se saltea entera —
  // la advertencia dependía de si la IA llegaba a tiempo. Bloqueamos el envío
  // mientras extrae. NO va dentro de `busy` a propósito: eso deshabilitaría
  // también Cancelar y el switch de ARCA.
  const submitBlocked = busy || extracting;

  // Nº fuera del formato AR — sólo advertencia, nunca bloqueo.
  const nroLooksOdd =
    isAr &&
    nroFactura.trim().length > 0 &&
    !AR_NRO_COMPROBANTE_RE.test(nroFactura.trim());

  // Cualquiera de las dos anomalías exige una confirmación explícita antes de
  // facturar. Ninguna impide facturar: la IA y el formato son heurísticas.
  const needsConfirm = amountsMismatch || nroLooksOdd;

  const triggerFilePick = () => {
    // `extracting` incluido: cambiar de archivo con una extracción en vuelo
    // deja que el resultado del archivo viejo aterrice sobre el nuevo.
    if (busy || extracting) return;
    setFileError(null);
    fileInputRef.current?.click();
  };

  const onFileInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const picked = e.target.files?.[0] ?? null;
    e.target.value = '';
    if (!picked) return;
    if (picked.type !== 'application/pdf') {
      setFileError('Solo se acepta PDF.');
      return;
    }
    if (picked.size > 4 * 1024 * 1024) {
      setFileError('El archivo supera los 4 MB.');
      return;
    }
    setStagedFile(picked);
    // Run Gemini extraction in the background. The Nº auto-fills only
    // when empty (never clobbers admin input); the total is recorded
    // separately and compared against the comprobante's expected
    // amount — mismatches surface under the inputs and force a
    // second confirmation on "Facturar". Per-outcome branching
    // distinguishes free-tier exhaustion from generic failures.
    setExtractedTotal(null);
    setExtracting(true);
    extractInvoice({ file: picked, mode: 'facturacion' })
      .then((outcome) => {
        if (outcome.kind === 'ok') {
          const nro = outcome.result.fields.nroComprobante;
          if (nro) {
            setNroFactura((cur) => {
              if (cur.trim()) return cur;
              setNroFromAI(true);
              return nro;
            });
            toast.success('Nº de factura detectado', { description: nro });
          }
          const total = outcome.result.fields.total;
          if (total != null && Number.isFinite(total)) {
            setExtractedTotal(total);
          }
          return;
        }
        if (outcome.kind === 'quota') {
          toast.warning('Cuota de IA agotada', {
            description: outcome.message,
            duration: 6000,
          });
        }
        // 'unavailable' / 'network': silent. The admin can still type.
      })
      .catch((err) => {
        // eslint-disable-next-line no-console
        console.warn('[facturar] extractInvoice threw unexpectedly', err);
      })
      .finally(() => setExtracting(false));
  };

  return (
    // Parent dialog hides while the mismatch-confirmation dialog is
    // open (so they don't stack visually) and reopens on "Revisar".
    // We don't unmount — Radix just toggles visibility — so the
    // file/nro/extracted state is preserved across the toggle.
    <Dialog
      open={!confirmIssuesOpen && caeConfirm == null && !confirmCloseOpen}
      onOpenChange={(o) => {
        if (o || busy || confirmIssuesOpen || caeConfirm != null || confirmCloseOpen) return;
        requestClose();
      }}
    >
      <DialogContent
        // MX manual facturación shows the receptor fiscal panel (RFC / Razón
        // Social / Dirección…) laid out two-per-row — give it room so the
        // 2-col grid isn't cramped. AR/ARCA keeps the narrower form.
        className={isAr ? 'max-w-2xl' : showBreakdown ? 'max-w-4xl' : 'max-w-3xl'}
        processing={
          caeMutation.isPending
            ? 'Emitiendo CAE…'
            : busy && (uploading ? 'Subiendo factura…' : 'Facturando…')
        }
      >
        {caeResult ? (
          <CaeExitoView
            result={caeResult}
            comprobanteId={row.id}
            onAttachStatusChange={handleAttachStatus}
            onClose={requestClose}
          />
        ) : (
          <>
        <DialogHeader>
          <DialogTitle>
            {/* On MX two-stage rows the title used to read "Facturar
                Anticipo", inherited from when the operator picked the
                stage at registration time. With the stage now chosen
                inside this dialog (via the Anticipada/Final toggle
                below), that title was misleading whenever the user
                actually intended to upload the Final. Keep it neutral
                and let the toggle communicate the stage. */}
            {isInterna ? 'Facturar — Factura Interna Nova' : 'Facturar comprobante'}
          </DialogTitle>
          <DialogDescription>
            Operación Nº {row.fields.IDOperacion_CP ?? '—'} ·{' '}
            {row.clienteRO || row.fields.Cliente_CP || '—'}
            {isInterna && (
              <>
                <br />
                <span className="text-xs text-muted-foreground">
                  Al guardar, se generan automáticamente los movimientos
                  Cuenta por Pagar Nova → Fornitalia y Cuenta por Cobrar
                  Fornitalia ← Nova.
                </span>
              </>
            )}
            {isTwoStage && anticipadaDone && (
              <>
                <br />
                <span className="text-xs text-muted-foreground">
                  Ya registraste la Factura Anticipada para esta operación.
                  Subí ahora la Factura Final para cerrar la facturación.
                </span>
              </>
            )}
            {/* For two-stage rows where Anticipada is NOT yet committed,
                the "Es factura anticipada" toggle sits next to the
                inputs in the body so the helper copy reads as a label
                for that toggle rather than abstract preamble. */}
          </DialogDescription>
        </DialogHeader>
        <form
          onSubmit={(e) => {
            e.preventDefault();
            // ARCA path has its own button + validity; don't let Enter fire
            // the manual facturar mutation.
            if (facturarConArca) return;
            if (!valid || submitBlocked) return;
            if (needsConfirm) {
              setConfirmIssuesOpen(true);
              return;
            }
            mutation.mutate();
          }}
          className="flex flex-1 flex-col overflow-hidden"
          noValidate
        >
          <DialogBody
            className={cn(
              showBreakdown
                ? 'space-y-4 md:grid md:grid-cols-[1fr_18rem] md:items-stretch md:gap-4 md:space-y-0'
                : 'space-y-3',
            )}
          >
            <div className="min-w-0 space-y-3">
            {/* MX: datos fiscales del receptor (read-only). Nova los necesita
                para el CFDI; si faltan, avisar al vendedor — Nova no edita el
                contacto.

                NO en la Factura Interna: ahí el receptor es Nova, no el
                cliente. Mostrar los datos fiscales del cliente en un
                documento inter-company hizo que Administración creyera que
                la factura le llegaba al cliente (reporte de 2026-08-03). El
                dato sigue estando donde corresponde: el diálogo de facturación
                normal, cuyo receptor SÍ es el cliente. */}
            {activeCountry.code === 'MX' && !isInterna && (
              <ReceptorFiscalPanel data={row.fiscal_data ?? null} />
            )}
            {isAr && (
              <label
                htmlFor="facturar-con-arca"
                className="flex items-center justify-between gap-3 rounded-lg border bg-muted/30 px-3 py-2 text-sm"
              >
                <span className="font-medium text-foreground">
                  Facturar con ARCA (CAE)
                  {esPrevia && (
                    <span className="mt-0.5 block text-xs font-normal text-muted-foreground">
                      Factura Anticipada: la carga manual sólo admite cobros
                      confirmados, así que este comprobante va por ARCA.
                    </span>
                  )}
                </span>
                <Switch
                  id="facturar-con-arca"
                  checked={facturarConArca}
                  onCheckedChange={setFacturarConArca}
                  // Bloqueado en previa: volver al manual adjunta el PDF y
                  // recién después /facturar rechaza por no estar Confirmado.
                  disabled={busy || caeMutation.isPending || esPrevia}
                />
              </label>
            )}
            {facturarConArca && (
              <>
                <div className="space-y-1.5">
                  <Label htmlFor="cae-pv">Punto de Venta</Label>
                  <Select
                    value={caePvId}
                    onValueChange={setCaePvId}
                    disabled={arcaPvs.length === 0}
                  >
                    <SelectTrigger id="cae-pv">
                      <SelectValue
                        placeholder={
                          arcaPvsQ.isPending ? 'Cargando…' : 'Sin Puntos de Venta'
                        }
                      />
                    </SelectTrigger>
                    <SelectContent>
                      {arcaPvs.map((pv) => (
                        <SelectItem key={pv.id} value={pv.id}>
                          {(pv.fields.RazonSocial || '—') +
                            ' · PV ' +
                            (pv.fields.NroPuntoVenta != null
                              ? String(pv.fields.NroPuntoVenta).padStart(5, '0')
                              : '—') +
                            (pv.fields.EsExportacion === true ? ' · Exportación' : '')}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  {caePvIncompatible && caePvSel && (
                    <div className="rounded-lg border border-amber-300 bg-amber-50 px-3 py-2 text-xs leading-relaxed text-amber-900">
                      {receptorExterior
                        ? `El cliente es del exterior: se factura con Factura E desde un Punto de Venta de exportación. El PV ${String(caePvSel.fields.NroPuntoVenta ?? '—').padStart(5, '0')} es para Factura A/B.`
                        : `El PV ${String(caePvSel.fields.NroPuntoVenta ?? '—').padStart(5, '0')} es de exportación (Factura E) y este cliente no es del exterior. Elegí un Punto de Venta de Factura A/B, o marcá al cliente como "Cliente del Exterior" en Contactos.`}
                    </div>
                  )}
                  {pvsCompatibles.length === 0 && !arcaPvsQ.isPending && (
                    <p className="text-xs text-muted-foreground">
                      {receptorExterior
                        ? 'No hay un Punto de Venta de exportación activo. Creá uno en Configuración → Puntos de Venta y marcalo como "Exportación".'
                        : 'No hay Puntos de Venta activos. Creá uno en Configuración → Puntos de Venta.'}
                    </p>
                  )}
                </div>
                <div className="space-y-1.5">
                  <Label htmlFor="cae-tipo">Tipo de comprobante (ARCA)</Label>
                  <Select
                    value={String(caeTipoId)}
                    onValueChange={(v) => setCaeTipoId(Number(v))}
                  >
                    <SelectTrigger id="cae-tipo">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {/* Fornitalia (Responsable Inscripto) emits A/B. Factura C
                          is modeled server-side (no IVA discriminado) but AFIP
                          only acepta C desde un emisor monotributo/exento. */}
                      {caePvEsExpo ? (
                        // PV de exportación: sólo Factura E (exportación, WSFEX).
                        <SelectItem value="19">Factura E (exportación)</SelectItem>
                      ) : (
                        <>
                          <SelectItem value="1">Factura A</SelectItem>
                          <SelectItem value="6">Factura B</SelectItem>
                          <SelectItem value="11">Factura C</SelectItem>
                          {/* FCE MiPyMEs — usar cuando ARCA exige comprobante de
                              crédito (por monto y condiciones). Requiere CBU +
                              transmisión en el Punto de Venta. */}
                          <SelectItem value="201">Factura de Crédito MiPyMEs A</SelectItem>
                          <SelectItem value="206">Factura de Crédito MiPyMEs B</SelectItem>
                        </>
                      )}
                    </SelectContent>
                  </Select>
                </div>
                {caeEsFce && (
                  <div className="space-y-1.5">
                    <Label htmlFor="cae-vto-pago">Vencimiento del pago (FCE)</Label>
                    <Input
                      id="cae-vto-pago"
                      type="date"
                      min={hoyAr}
                      value={caeVencimientoPago}
                      onChange={(e) => setCaeVencimientoPago(e.target.value)}
                    />
                    <p className="text-xs text-muted-foreground">
                      Fecha de pago del crédito (FchVtoPago). Vacío → fecha de emisión.
                    </p>
                  </div>
                )}
                {caeEsE && !caePvIncompatible && (
                  <div className="space-y-3 rounded-lg border bg-muted/20 px-3 py-3">
                    <p className="text-xs font-semibold text-muted-foreground">
                      Exportación — se factura en dólares, sin IVA
                    </p>
                    <div className="grid gap-3 md:grid-cols-2">
                      <div className="space-y-1.5">
                        <Label htmlFor="fex-incoterm">
                          Incoterm <span className="text-primary">*</span>
                        </Label>
                        <Select value={fexIncoterm} onValueChange={setFexIncoterm}>
                          <SelectTrigger id="fex-incoterm">
                            <SelectValue placeholder="Seleccionar Incoterm" />
                          </SelectTrigger>
                          <SelectContent>
                            {(incotermsQ.data ?? []).map((i) => (
                              <SelectItem key={i.codigo} value={i.codigo}>
                                {i.label}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>
                      <div className="space-y-1.5">
                        <Label htmlFor="fex-permiso">Nº de permiso de embarque (opcional)</Label>
                        <Input
                          id="fex-permiso"
                          value={fexPermiso}
                          onChange={(e) => setFexPermiso(e.target.value)}
                          placeholder="Vacío = sin permiso"
                          maxLength={40}
                          aria-invalid={fexPermisoInvalido}
                        />
                        <p className={cn('text-xs', fexPermisoInvalido ? 'text-destructive' : 'text-muted-foreground')}>
                          {fexPermisoInvalido
                            ? `Debe tener exactamente 16 caracteres alfanuméricos (tiene ${fexPermisoNorm.length}).`
                            : '16 caracteres alfanuméricos (sin espacios ni guiones).'}
                        </p>
                      </div>
                    </div>
                    <div className="space-y-1.5">
                      <Label htmlFor="fex-ctz">Cotización del dólar (ARS por USD)</Label>
                      <Input
                        id="fex-ctz"
                        inputMode="decimal"
                        value={
                          fexCtzInput ||
                          (caeExportacion
                            ? String(caeExportacion.cotizacion).replace('.', ',')
                            : fexCtzArca != null
                              ? String(fexCtzArca).replace('.', ',')
                              : '')
                        }
                        onChange={(e) => setFexCtzInput(e.target.value)}
                        placeholder="Ingresá la cotización"
                        aria-invalid={fexCtzBloqueada}
                      />
                      {(fexCtzBloqueada || fexCtzAviso) && (
                        <p className={cn('text-xs font-medium', fexCtzBloqueada ? 'text-destructive' : 'text-amber-700')}>
                          {fexCtzBloqueada
                            ? `Difiere ${fexCtzDesvioPct.toFixed(1)}% de la de ARCA: el máximo es ${String(fexCtzMaxPct).replace('.', ',')}%. Revisala.`
                            : `Difiere ${fexCtzDesvioPct.toFixed(1)}% de la de ARCA. Verificá que sea correcta antes de emitir.`}
                        </p>
                      )}
                      <p className="text-xs text-muted-foreground">
                        {fexCtzArca != null
                          ? `Cotización de ARCA: ${fexCtzArca.toLocaleString('es-AR', { maximumFractionDigits: 6 })}. `
                          : ''}
                        Viene cargada con la de ARCA; podés modificarla. La que figure acá es la que se declara y queda guardada.
                      </p>
                    </div>
                    {fexDesincronizado && (
                      <p className="text-xs text-amber-800">
                        Actualizando el resumen con los cambios…
                      </p>
                    )}
                  </div>
                )}
                <div className="rounded-lg border border-sky-200 bg-sky-50 px-3 py-2 text-xs leading-relaxed text-sky-900">
                  Se solicita el CAE a ARCA (AFIP) para este cobro. ARCA asigna
                  el número y el vencimiento; el PDF con el QR se genera
                  automáticamente. No hace falta subir un archivo.
                </div>
                {/* Pago Final toggle — overrides the vendor's tag so the admin
                    can facturar this cobro as final (itemiza + descuenta el
                    anticipo en ventas de varios artículos) sin volver al registro
                    del pago. The preview below reflects the choice. */}
                {caePreview && !caeEsE && (
                  <>
                    <label
                      htmlFor="facturar-pago-final"
                      className="flex items-center justify-between gap-3 rounded-lg border bg-muted/30 px-3 py-2 text-sm"
                    >
                      <span>
                        <span className="block font-medium text-foreground">
                          Facturar como Pago Final
                        </span>
                        <span className="block text-xs text-muted-foreground">
                          Itemiza los productos y descuenta el anticipo. El vendedor lo marcó como{' '}
                          {caeEsPagoFinal ? 'Pago Final' : 'Anticipo'}.
                        </span>
                      </span>
                      <Switch
                        id="facturar-pago-final"
                        checked={caePagoFinalEff}
                        onCheckedChange={(v) => setCaePagoFinal(v)}
                        disabled={busy || caeMutation.isPending || caeValidationQ.isFetching}
                      />
                    </label>
                    {caeSugerirPagoFinal && !caePagoFinalEff && (
                      <div className="rounded-lg border border-amber-300 bg-amber-50 px-3 py-2 text-xs font-medium leading-relaxed text-amber-900">
                        💡 No queda ninguna otra factura pendiente de esta venta, así que
                        probablemente sea el <strong>Pago Final</strong>. Activá el interruptor de
                        arriba para itemizar los productos y descontar el anticipo.
                      </div>
                    )}
                  </>
                )}
                {caeFaltanSerieDespacho && (
                  <div className="rounded-lg border border-amber-300 bg-amber-50 px-3 py-2 text-xs font-medium leading-relaxed text-amber-900">
                    ⚠ Esta venta todavía tiene hornos sin <strong>N° de Serie</strong> /{' '}
                    <strong>N° de Despacho</strong> (se cargan en el despacho). La factura
                    saldrá sin esos datos — podés <strong>regenerarla</strong> desde “Ver
                    Facturas” una vez despachada.
                  </div>
                )}
                {caeHomologacion && (
                  <div className="rounded-lg border border-amber-300 bg-amber-50 px-3 py-2 text-xs font-medium leading-relaxed text-amber-900">
                    ⚠ Modo HOMOLOGACIÓN (prueba). No se factura en producción — el
                    CAE emitido es de prueba y no tiene validez fiscal.
                  </div>
                )}
                {/* Fiscal-data gate. The receptor fiscal fields are captured by
                    the vendedor, not by whoever approves facturas — so if
                    they're missing we BLOCK emission here (button disabled
                    above) and tell the admin who has to fix it. */}
                {caeMissing && (
                  <div className="rounded-lg border border-red-300 bg-red-50 px-3 py-2 text-xs leading-relaxed text-red-900">
                    <div className="font-semibold">
                      {caeFaltaCliente ? 'Faltan datos fiscales del cliente' : 'No se puede emitir el CAE'}
                    </div>
                    <ul className="mt-1 list-disc space-y-0.5 pl-4">
                      {caeMissing.map((m) => (
                        <li key={m.tag}>{m.message}</li>
                      ))}
                    </ul>
                    {caeFaltaCliente && (
                      <div className="mt-1.5">
                        El <strong>vendedor</strong> debe completar estos datos del
                        cliente antes de facturar. No se puede emitir el CAE hasta
                        que estén cargados.
                      </div>
                    )}
                  </div>
                )}
                {caeBlockedMsg && (
                  <div className="rounded-lg border border-amber-300 bg-amber-50 px-3 py-2 text-xs text-amber-900">
                    {caeBlockedMsg}
                  </div>
                )}
                {caeValidationQ.isError && (
                  <div className="rounded-lg border border-amber-300 bg-amber-50 px-3 py-2 text-xs text-amber-900">
                    No se pudo validar la facturación. Reintentá.
                  </div>
                )}
              </>
            )}
            {!facturarConArca && (
              <>
            {/* PDF first: uploading kicks off the Gemini extraction so
                the Nº below can autocomplete before the user touches
                it. The keyboard `autoFocus` therefore stays on the
                Nº field — that's still the gate the admin verifies. */}
            <div className="space-y-1.5">
              <Label>Archivo PDF de la factura</Label>
              {hasUploadedFile && fileName ? (
                <FileLink
                  entityType={currentEntityType}
                  entityId={row.id}
                  fileName={fileName}
                  onDeleted={() => {
                    setLocalFileName(null);
                    qc.invalidateQueries({ queryKey: ['administracion'] });
                  }}
                />
              ) : (
                <div className="flex items-center gap-2">
                  <Button
                    type="button"
                    variant="outline"
                    onClick={triggerFilePick}
                    disabled={busy}
                    title={stagedFile ? stagedFile.name : undefined}
                    className={cn(
                      // `min-w-0` lets the inner truncate engage —
                      // flex-1 alone doesn't shrink below its
                      // content's intrinsic width, so a long
                      // filename pushes the modal sideways.
                      'min-w-0 flex-1 justify-start gap-2 transition-colors',
                      extracting &&
                        'animate-pulse border-emerald-300 bg-emerald-50 text-emerald-900 hover:bg-emerald-50',
                    )}
                  >
                    {extracting ? (
                      <Sparkles
                        className="h-4 w-4 shrink-0 animate-pulse text-emerald-600"
                        aria-hidden
                      />
                    ) : (
                      <Upload className="h-4 w-4 shrink-0" aria-hidden />
                    )}
                    {extracting ? (
                      <span className="flex flex-1 items-baseline gap-1 truncate text-sm font-medium text-emerald-900">
                        <span className="whitespace-nowrap">
                          Analizando factura con IA
                        </span>
                        <span className="inline-flex">
                          <span className="animate-bounce [animation-delay:-0.3s]">.</span>
                          <span className="animate-bounce [animation-delay:-0.15s]">.</span>
                          <span className="animate-bounce">.</span>
                        </span>
                      </span>
                    ) : (
                      <span className="truncate">
                        {stagedFile ? stagedFile.name : 'Adjuntar factura (PDF)'}
                      </span>
                    )}
                  </Button>
                  {stagedFile && !extracting && (
                    <>
                      <Button
                        type="button"
                        variant="ghost"
                        size="icon"
                        onClick={() => setPreviewFile(stagedFile)}
                        disabled={busy}
                        aria-label="Ver factura"
                        title="Ver factura"
                      >
                        <Eye className="h-4 w-4" aria-hidden />
                      </Button>
                      <Button
                        type="button"
                        variant="ghost"
                        size="icon"
                        onClick={() => {
                          setStagedFile(null);
                          setExtractedTotal(null);
                          if (nroFromAI) {
                            setNroFactura('');
                            setNroFromAI(false);
                          }
                        }}
                        disabled={busy}
                        className="text-muted-foreground hover:text-destructive"
                        aria-label="Quitar factura"
                        title="Quitar factura"
                      >
                        <Trash2 className="h-4 w-4" aria-hidden />
                      </Button>
                    </>
                  )}
                  <input
                    ref={fileInputRef}
                    type="file"
                    accept="application/pdf"
                    hidden
                    onChange={onFileInputChange}
                  />
                </div>
              )}
              {fileError && (
                <p className="text-xs text-destructive">{fileError}</p>
              )}
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="nro-factura">Nº de factura</Label>
              <Input
                id="nro-factura"
                variant="filled"
                value={nroFactura}
                onChange={(e) => {
                  setNroFactura(e.target.value);
                  setNroFromAI(false);
                }}
                placeholder="0001-00000123"
              />
              {extracting && (
                <p className="text-xs text-muted-foreground">
                  Detectando el Nº de factura desde el PDF…
                </p>
              )}
            </div>

            {isTwoStage && !anticipadaDone && (
              // MX two-stage entry point. Sits right under the Nº input
              // so the helper text below reads as a label clarifying
              // what flipping the toggle changes (anticipada =
              // non-terminal, leaves the row in the queue; off = final,
              // closes the row). Hidden once Anticipada is committed —
              // reopening the row drops the operator straight onto the
              // Final upload UI.
              <div className="space-y-1.5">
                <label
                  htmlFor="facturar-es-anticipada"
                  className="flex items-center justify-between gap-3 rounded-lg border bg-muted/30 px-3 py-2 text-sm"
                >
                  <span className="font-medium text-foreground">
                    Es factura anticipada
                  </span>
                  <Switch
                    id="facturar-es-anticipada"
                    checked={activeStage === 'anticipada'}
                    onCheckedChange={(checked) =>
                      setActiveStage(checked ? 'anticipada' : 'final')
                    }
                    disabled={uploading || mutation.isPending}
                  />
                </label>
                <div className="rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 text-xs leading-relaxed text-amber-900">
                  Marcá "Es factura anticipada" si vas a registrar una
                  anticipada — la operación queda en la cola esperando la
                  final. Si la dejás desmarcada, subís directamente la
                  factura final y se cierra la facturación.
                </div>
              </div>
            )}

            {usadoFI && (
              <>
                <div className="rounded-lg border border-rose-200 bg-rose-50 px-3 py-2 text-xs leading-relaxed text-rose-900">
                  <p className="font-semibold uppercase tracking-wide text-rose-700">
                    Advertencia
                  </p>
                  <p className="mt-1">
                    La venta quedó por debajo del costo contable + comisión +
                    IVA, así que esta factura interna se factura como
                    "usado". Ingresá manualmente el monto a registrar.
                  </p>
                </div>
                <div className="space-y-1.5">
                  <Label htmlFor="facturar-monto-usado">Monto a facturar</Label>
                  <MoneyInput
                    id="facturar-monto-usado"
                    variant="filled"
                    value={montoUsadoRaw}
                    onChange={(next) => setMontoUsadoRaw(next)}
                    moneda={expectedCurrency}
                    placeholder="Ej.: 5.000"
                  />
                  {montoUsadoRaw.trim() !== '' && montoUsadoParsed == null && (
                    <p className="text-xs text-destructive">
                      Ingresá un monto válido mayor a 0.
                    </p>
                  )}
                  {montoUsadoParsed != null &&
                    facturaClienteAmount != null &&
                    facturaClienteAmount > 0 &&
                    montoUsadoParsed > facturaClienteAmount && (
                      <p className="text-xs leading-relaxed text-destructive">
                        ⚠ El monto que estás ingresando (
                        {formatMoney(montoUsadoParsed, expectedCurrency)})
                        es mayor que la factura del cliente (
                        {formatMoney(facturaClienteAmount, expectedCurrency)}).
                        No se puede confirmar — dejaría Nova caja en
                        negativo.
                      </p>
                    )}
                </div>
              </>
            )}

            {/* Always-visible expected amount + AI-detected total
                comparison. AI may fail or extract garbage, so the
                expected figure is always shown — the admin can
                eyeball it against the PDF themselves. The amber
                warning only fires when the AI actually returned a
                total that disagrees. */}
            <div className="rounded-md border bg-muted/40 px-3 py-2">
              <div className="flex items-center justify-between gap-3 text-sm">
                <span className="text-muted-foreground">Monto a facturar</span>
                <span className="font-semibold tabular-nums text-foreground">
                  {formatMoney(expectedAmount, expectedCurrency)}
                </span>
              </div>
              {extractedTotal != null && (
                <div
                  className={cn(
                    'mt-1 flex items-center justify-between gap-3 text-xs',
                    amountsMismatch ? 'text-amber-800' : 'text-muted-foreground',
                  )}
                >
                  <span className="flex items-center gap-1.5">
                    {amountsMismatch && (
                      <AlertTriangle className="h-3.5 w-3.5" aria-hidden />
                    )}
                    Detectado en factura
                  </span>
                  <span className="font-medium tabular-nums">
                    {formatMoney(extractedTotal, expectedCurrency)}
                  </span>
                </div>
              )}
              {amountsMismatch && (
                <p className="mt-1.5 text-xs leading-relaxed text-amber-800">
                  El monto detectado por la IA no coincide con el monto a
                  facturar. Diferencia: {formatMoney(amountDifference, expectedCurrency)}.
                </p>
              )}
            </div>
              </>
            )}
            </div>
            {/* Desglose de costos (Valor Contable que Fornitalia factura a
                Nova): costo contable → × multiplicador → + IVA → total.
                Read-only, full-height side column. Factura Interna only. */}
            {showBreakdown && fiBreakdown && (
              <aside className="flex h-full flex-col rounded-lg border bg-muted/30 px-3 py-2 text-sm">
                <div className="mb-1 text-xs font-semibold uppercase tracking-wide text-muted-foreground">
                  Desglose de costos (Valor Contable)
                </div>
                <div className="flex items-baseline justify-between gap-3">
                  <span className="text-muted-foreground">Costo contable</span>
                  <span className="tabular-nums">
                    {formatMoney(fiBreakdown.costoContable, 'MXN')}
                  </span>
                </div>
                <div className="mt-1 flex items-baseline justify-between gap-3">
                  <span className="text-muted-foreground">
                    × Multiplicador ({fiBreakdown.multiplicador})
                  </span>
                  <span className="tabular-nums">
                    {formatMoney(fiBreakdown.subtotal, 'MXN')}
                  </span>
                </div>
                <div className="mt-1 flex items-baseline justify-between gap-3">
                  <span className="text-muted-foreground">
                    IVA ({Math.round(fiBreakdown.ivaRate * 100)}%)
                  </span>
                  <span className="tabular-nums">{formatMoney(fiBreakdown.iva, 'MXN')}</span>
                </div>
                <div className="mt-2 flex items-baseline justify-between gap-3 border-t pt-2">
                  <span className="font-medium">Total factura a Nova</span>
                  <span className="tabular-nums font-semibold">
                    {formatMoney(fiBreakdown.total, 'MXN')}
                  </span>
                </div>
                <p className="mt-2 border-t pt-2 text-xs text-muted-foreground">
                  Costos que forman el total de la Factura Interna que Fornitalia
                  factura a Nova.
                  {usadoFI && (
                    <>
                      {' '}
                      Venta marcada <strong>usado</strong>: el monto se carga manual
                      y puede diferir de este valor contable.
                    </>
                  )}
                </p>
              </aside>
            )}
          </DialogBody>
          <DialogFooter>
            <Button
              type="button"
              variant="outline"
              onClick={onClose}
              disabled={busy || caeMutation.isPending}
            >
              Cancelar
            </Button>
            {facturarConArca ? (
              <Button
                type="button"
                disabled={
                  caeMutation.isPending ||
                  caeValidationQ.isFetching ||
                  !caePreview ||
                  caePvIncompatible ||
                  (caeEsE && (!fexIncoterm || fexDesincronizado || fexPermisoInvalido || fexCtzBloqueada))
                }
                onClick={() => caePreview && setCaeConfirm(caePreview)}
                title={
                  caeMissing
                    ? caeFaltaCliente
                      ? 'Faltan datos fiscales del cliente — el vendedor debe completarlos.'
                      : 'No se puede emitir el CAE: revisá el detalle.'
                    : 'Revisá la factura antes de emitir el comprobante en ARCA (AFIP).'
                }
              >
                {caeValidationQ.isFetching ? 'Validando…' : 'Continuar'}
              </Button>
            ) : (
              <Button
                type="button"
                disabled={!valid || submitBlocked}
                title={
                  extracting
                    ? 'Esperá a que termine la lectura de la factura.'
                    : undefined
                }
                onClick={() => {
                  if (!valid || submitBlocked) return;
                  if (needsConfirm) {
                    setConfirmIssuesOpen(true);
                    return;
                  }
                  mutation.mutate();
                }}
              >
                {extracting ? 'Leyendo factura…' : 'Facturar'}
              </Button>
            )}
          </DialogFooter>
        </form>
        {previewFile && (
          <FacturaPreviewDialog
            file={previewFile}
            onClose={() => setPreviewFile(null)}
          />
        )}
          </>
        )}
      </DialogContent>
      {/* Second-confirmation when the AI-extracted total disagrees
          with the comprobante. The admin can still proceed — the AI
          isn't authoritative — but the mismatch must be acknowledged
          explicitly so the figure isn't facturado by accident. */}
      <Dialog
        open={confirmIssuesOpen}
        onOpenChange={(o) => !o && !busy && setConfirmIssuesOpen(false)}
      >
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <AlertTriangle className="h-5 w-5 text-amber-500" aria-hidden />
              {amountsMismatch && nroLooksOdd
                ? 'Revisá antes de facturar'
                : amountsMismatch
                  ? 'Los montos no coinciden'
                  : 'El Nº de factura parece inválido'}
            </DialogTitle>
            <DialogDescription>
              {amountsMismatch
                ? 'El monto detectado por la IA en la factura no coincide con el monto a facturar para esta operación.'
                : 'El Nº de factura no tiene el formato habitual de un comprobante.'}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-2 px-6 pb-4">
            {amountsMismatch && (
              <div className="rounded-md border bg-muted/40 px-3 py-2 text-sm">
                <div className="flex items-center justify-between gap-3">
                  <span className="text-muted-foreground">Monto a facturar</span>
                  <span className="font-semibold tabular-nums text-foreground">
                    {formatMoney(expectedAmount, expectedCurrency)}
                  </span>
                </div>
                <div className="mt-1 flex items-center justify-between gap-3">
                  <span className="text-muted-foreground">Detectado en factura</span>
                  <span className="font-semibold tabular-nums text-foreground">
                    {formatMoney(extractedTotal ?? 0, expectedCurrency)}
                  </span>
                </div>
                <div className="mt-1 flex items-center justify-between gap-3 border-t pt-1.5">
                  <span className="font-medium text-amber-800">Diferencia</span>
                  <span className="font-semibold tabular-nums text-amber-800">
                    {formatMoney(amountDifference, expectedCurrency)}
                  </span>
                </div>
              </div>
            )}
            {nroLooksOdd && (
              <div className="rounded-md border border-amber-300 bg-amber-50 px-3 py-2 text-sm">
                <div className="flex items-center justify-between gap-3">
                  <span className="text-amber-900">Nº ingresado</span>
                  <span className="font-semibold tabular-nums text-amber-900">
                    {nroFactura.trim()}
                  </span>
                </div>
                <p className="mt-1.5 text-xs leading-relaxed text-amber-900">
                  Se esperaba algo con la forma <strong>A-00005-00000572</strong>{' '}
                  (punto de venta y número). Un Nº fuera de formato después no se
                  puede identificar en el Libro de IVA Ventas.
                </p>
              </div>
            )}
            <p className="text-xs text-muted-foreground">
              {amountsMismatch
                ? 'Verificá que el PDF y el monto sean correctos antes de continuar. Si confirmás, la operación se facturará por el monto a facturar indicado arriba.'
                : 'Verificá que el Nº coincida con el comprobante antes de continuar.'}
            </p>
          </div>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setConfirmIssuesOpen(false)}
              disabled={busy}
            >
              Revisar
            </Button>
            <Button
              onClick={() => {
                // Mismos guardas que el botón Facturar: este es el que
                // realmente commitea y antes no revalidaba nada.
                if (!valid || submitBlocked || facturarConArca) return;
                setConfirmIssuesOpen(false);
                mutation.mutate();
              }}
              disabled={!valid || submitBlocked || facturarConArca}
              className="bg-amber-600 text-white hover:bg-amber-700"
            >
              Facturar igual
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Guard closing the success screen while the PDF is still un-attached.
          The CAE is already emitted; closing here strands the factura PDF-less
          with no way back (it's out of the Facturación queue). */}
      <Dialog
        open={confirmCloseOpen}
        onOpenChange={(o) => !o && setConfirmCloseOpen(false)}
      >
        <DialogContent className="max-w-sm">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <AlertTriangle className="h-5 w-5 text-amber-500" aria-hidden />
              El PDF todavía no se adjuntó
            </DialogTitle>
            <DialogDescription>
              La factura ya está emitida en ARCA, pero el PDF no se pudo adjuntar
              al sistema. Si cerrás ahora, la factura queda sin PDF y no vas a
              poder readjuntarlo desde acá. Conviene reintentar antes de cerrar.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setConfirmCloseOpen(false)}>
              Seguir intentando
            </Button>
            <Button
              variant="destructive"
              onClick={() => {
                setConfirmCloseOpen(false);
                onClose();
              }}
            >
              Cerrar igual
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Pre-emit confirmation for the ARCA path. This hits PRODUCTION AFIP and
          issues a real fiscal comprobante — the operator must review every
          value and confirm explicitly before the CAE is requested. */}
      <Dialog
        open={caeConfirm != null}
        onOpenChange={(o) => !o && !caeMutation.isPending && setCaeConfirm(null)}
      >
        <DialogContent className="max-w-4xl">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <AlertTriangle className="h-5 w-5 text-amber-500" aria-hidden />
              Confirmá la emisión en ARCA
            </DialogTitle>
            <DialogDescription>
              Se emitirá un comprobante <strong>fiscal real</strong> en ARCA
              (AFIP). Revisá la factura: una vez emitida no se puede deshacer.
            </DialogDescription>
          </DialogHeader>
          {caeConfirm && (
            <>
              <CaeFacturaPreview
                resumen={caeConfirm}
                lineas={caePreviewLineas}
                fecha={caePreviewFecha}
                edits={lineasTextoEdit}
                onEditLine={(i, text) =>
                  setLineasTextoEdit((prev) => ({ ...prev, [i]: text }))
                }
                disabled={caeMutation.isPending}
              />
              <p className="px-6 pb-2 text-xs text-muted-foreground">
                El número de comprobante y el CAE los asigna ARCA al confirmar.
              </p>
            </>
          )}
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setCaeConfirm(null)}
              disabled={caeMutation.isPending}
            >
              Volver
            </Button>
            <Button
              onClick={() => {
                setCaeConfirm(null);
                caeMutation.mutate();
              }}
              disabled={caeMutation.isPending}
            >
              Confirmar y emitir
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Dialog>
  );
}


/**
 * Inline preview for the staged factura. Same blob-URL pattern used in
 * the other Pagos dialogs — created on mount, revoked on unmount.
 */
function FacturaPreviewDialog({
  file,
  onClose,
}: {
  file: File;
  onClose: () => void;
}) {
  const [blobUrl, setBlobUrl] = useState<string | null>(null);
  useEffect(() => {
    const url = URL.createObjectURL(file);
    setBlobUrl(url);
    return () => URL.revokeObjectURL(url);
  }, [file]);
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-5xl">
        <DialogHeader>
          <DialogTitle className="truncate">{file.name}</DialogTitle>
        </DialogHeader>
        <DialogBody>
          {blobUrl == null ? (
            <div className="flex h-64 items-center justify-center text-muted-foreground">
              <Loader2 className="h-5 w-5 animate-spin" aria-hidden />
            </div>
          ) : (
            <iframe
              src={`${blobUrl}#zoom=page-width`}
              title={file.name}
              className="h-[82vh] w-full rounded-lg border"
            />
          )}
        </DialogBody>
        <DialogFooter>
          <Button type="button" variant="outline" onClick={onClose} className="gap-2">
            <X className="h-4 w-4" aria-hidden />
            Cerrar
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ---------------------------------------------------------------------------
// Cell primitives
// ---------------------------------------------------------------------------

function inferRowCurrency(row: ComprobanteAdminItem): 'ARS' | 'MXN' | 'USD' {
  // Each row's Monto_CP is stored in the medio's native currency: USD when
  // the caja is "Caja en dólares" in the ABM (shared/lib/cajaMoneda). For
  // everything else the medio is in the local home currency; use the
  // venta's `monedaRO` (server-resolved) to distinguish ARS from MXN — a
  // non-USD medio's country always matches the parent venta's country.
  if (esCajaUsd(row.fields.MedioPago_CP)) return 'USD';
  const medio = (row.fields.MedioPago_CP ?? '').toLowerCase();
  if (row.monedaRO === 'MXN') return 'MXN';
  // MX-only medios identify the row as MXN regardless of monedaRO —
  // belt-and-suspenders for legacy rows where the venta header wasn't
  // properly tagged. Factura Interna only exists in the MX Nova flow.
  if (medio.startsWith('factura interna') || medio.startsWith('nova ') || medio === 'herconstru') {
    return 'MXN';
  }
  return 'ARS';
}

function Th({ children, className }: { children: React.ReactNode; className?: string }) {
  return <th className={cn('px-4 py-3 font-normal', className)}>{children}</th>;
}

function Td({ children, className }: { children: React.ReactNode; className?: string }) {
  return <td className={cn('px-4 py-3', className)}>{children}</td>;
}

function RowStatus({ children, cols }: { children: React.ReactNode; cols: number }) {
  return (
    <tr>
      <td colSpan={cols} className="px-4 py-10 text-center text-sm text-muted-foreground">
        <div className="flex items-center justify-center gap-3">{children}</div>
      </td>
    </tr>
  );
}

function SkeletonRows({ cols }: { cols: number }) {
  return (
    <>
      {Array.from({ length: 4 }).map((_, i) => (
        <tr key={i}>
          {Array.from({ length: cols }).map((__, j) => (
            <td key={j} className="px-4 py-3.5">
              <Skeleton className="h-4 w-full max-w-[160px]" />
            </td>
          ))}
        </tr>
      ))}
    </>
  );
}

/**
 * Mobile-card shell — handles the loading / error / empty states so each
 * mode's mobile renderer only has to provide the per-row card. Rendered
 * alongside the desktop table inside the same card surface; CSS
 * (`md:hidden` / `hidden md:block`) decides which one shows.
 */
function MobileCardShell({
  loading,
  error,
  empty,
  emptyMessage,
  errorMessage = 'No se pudieron cargar los datos.',
  children,
}: {
  loading: boolean;
  error: boolean;
  empty: boolean;
  emptyMessage: string;
  errorMessage?: string;
  children: React.ReactNode;
}) {
  if (loading) {
    return (
      <div className="space-y-2 p-3">
        {Array.from({ length: 4 }).map((_, i) => (
          <Skeleton key={i} className="h-24 w-full rounded-xl" />
        ))}
      </div>
    );
  }
  if (error) {
    return (
      <div className="p-8 text-center text-sm text-destructive">{errorMessage}</div>
    );
  }
  if (empty) {
    return (
      <div className="p-8 text-center text-sm text-muted-foreground">{emptyMessage}</div>
    );
  }
  return <ul className="divide-y divide-border">{children}</ul>;
}

function IconButton({
  children,
  onClick,
  label,
  tone,
  disabled,
}: {
  children: React.ReactNode;
  onClick: () => void;
  label: string;
  tone: 'neutral' | 'success';
  disabled?: boolean;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      aria-label={label}
      title={label}
      disabled={disabled}
      className={cn(
        'inline-flex h-8 w-8 items-center justify-center rounded-md border transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring disabled:cursor-not-allowed disabled:opacity-40',
        tone === 'success'
          ? 'border-emerald-200 bg-emerald-50 text-emerald-700 hover:bg-emerald-100'
          : 'border-border text-muted-foreground hover:bg-secondary hover:text-foreground',
      )}
    >
      {children}
    </button>
  );
}

function CheckboxCell({
  checked,
  onChange,
  disabled,
  'aria-label': ariaLabel,
}: {
  checked: boolean;
  onChange: () => void;
  disabled?: boolean;
  'aria-label'?: string;
}) {
  return (
    <button
      type="button"
      role="checkbox"
      aria-checked={checked}
      aria-label={ariaLabel}
      disabled={disabled}
      onClick={() => !disabled && onChange()}
      className={cn(
        'flex h-5 w-5 items-center justify-center rounded-md border transition-colors disabled:cursor-not-allowed disabled:opacity-40',
        checked
          ? 'border-emerald-500 bg-emerald-500 text-white hover:bg-emerald-600'
          : 'border-border bg-background hover:bg-secondary',
      )}
    >
      {checked && <Check className="h-3.5 w-3.5" aria-hidden />}
    </button>
  );
}
